import React, { useEffect, useMemo, useRef, useState, useTransition } from "react";

/* ----------------------------- helpers ----------------------------- */
import API_BASE from "../../utils/api";

const isBringBackEligible = (r) => r && ["RB", "WR", "TE"].includes(r.pos);

const clamp = (v, lo = 0, hi = 1e9) => Math.max(lo, Math.min(hi, v));
// Optional team-specific portfolio minimum. Blank stack size disables it.
// Backend contract: at least qb_stack_pct% of lineups using this team's QB
// must have >= qb_stack_pct_min eligible same-team partners. Extra stacks
// remain allowed; this is a minimum percentage, not an exact distribution.
const teamStackPercentageFields = (rule) => {
  const rawSize = rule.qb_stack_pct_min;
  if (rawSize == null || String(rawSize).trim() === "") return {};
  const size = Number(rawSize);
  if (!Number.isFinite(size) || size < 1) return {};
  const rawPct = rule.qb_stack_pct;
  const pct = rawPct == null || String(rawPct).trim() === "" ? 100 : Number(rawPct);
  return {
    qb_stack_pct_min: clamp(Math.floor(size), 1, 8),
    qb_stack_pct: clamp(Number.isFinite(pct) ? pct : 100, 0, 100),
  };
};

const num = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).replace(/\$/g, "").replace(/[,  \s]/g, "").replace(/%/g, "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};
const fmt0 = (n) => (Number.isFinite(n) ? n.toLocaleString() : "—");
const fmt1 = (n) => (Number.isFinite(n) ? n.toFixed(1) : "—");
const fmtPct = (dec, decimals = 1) =>
  
  Number.isFinite(dec) ? `${(dec * 100).toFixed(decimals)}%` : "—";
  const geoMeanPct = (items, accessor) => {
  const vals = (items || [])
    .map((x) => Number(accessor(x)))
    .filter((v) => Number.isFinite(v) && v > 0);

  if (!vals.length) return 0;

  const logAvg = vals.reduce((sum, v) => sum + Math.log(v), 0) / vals.length;
  return Math.exp(logAvg);
};
const escapeCSV = (s) => /[",\r\n]/.test(String(s ?? "")) ? `"${String(s ?? "").replace(/"/g, '""')}"` : String(s ?? "");

/* ----------------------------- UI THEME ----------------------------- */

const OPT_LIGHT_THEME = {
  page: "min-h-screen bg-slate-100 text-slate-900",
  headerText: "text-slate-900",
  subtext: "text-slate-600",
  card: "border border-slate-200 bg-slate-50 shadow-sm",
  pill: "bg-white ring-slate-300",
  pillDarkBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillDarkBtnIdle: "text-slate-500 hover:text-slate-800",
  pillLightBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillLightBtnIdle: "text-slate-400 hover:text-slate-800",
  input:
    "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400",
};

const OPT_DARK_THEME = {
  page: "min-h-screen bg-slate-950 text-slate-100",
  headerText: "text-slate-100",
  subtext: "text-slate-400",
  card:
    "border border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]",
  pill: "bg-slate-900/90 ring-slate-700/80",
  pillDarkBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillDarkBtnIdle: "text-slate-400 hover:text-slate-100",
  pillLightBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillLightBtnIdle: "text-slate-500 hover:text-slate-200",
  input:
    "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500",
};

// small design tokens
const cls = {
  input:
    "h-8 px-2 text-sm rounded-md focus:ring-2 focus:ring-blue-200 focus:border-blue-400",
  btn: {
    primary: "h-8 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700",
    ghost: "h-8 px-3 rounded-md border text-sm rounded-md",
    chip: "px-3 py-1.5 text-xs font-medium rounded-full border",
    iconSm:
      "inline-flex items-center justify-center w-5 h-5 rounded-md border text-xs leading-none",
  },
  card: "rounded-2xl",
  tableHead: "px-2 py-1 font-semibold text-center select-none",
  cell: "px-2 py-1 text-center",

};const glass = {
  shell: "bg-white/5 backdrop-blur-xl border border-white/10",
  active:
    "bg-white/12 backdrop-blur-md border border-white/20 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]",
  idle:
    "bg-white/5 backdrop-blur-md border border-white/10 text-slate-300 hover:bg-white/8",
};



const timeAgo = (iso) => {
  try {
    const d = typeof iso === "string" ? new Date(iso) : new Date(iso ?? 0);
    const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  } catch {
    return "";
  }
};

// normalizers
const normName = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\u2019/g, "'")
    .replace(/\./g, "")
    .replace(/,\s*(jr|sr)\b/g, "")
    .replace(/\b(jr|sr)\b/g, "")
    .replace(/[^a-z' -]/g, "")
    .replace(/\s+/g, " ")
    .trim();
const normTeam = (s) => {
  const team = String(s || "").toUpperCase().trim();
  return team === "JAC" ? "JAX" : team;
};

// Vegas timestamps without an offset are Eastern wall-clock times.
function slateKickoffStamp(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (match) {
    return Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +(match[6] || 0));
  }
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return NaN;
  const instant = new Date(raw);
  if (!Number.isFinite(+instant)) return NaN;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(instant);
  const fields = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return Date.UTC(+fields.year, +fields.month - 1, +fields.day, +fields.hour, +fields.minute, +fields.second);
}

// Main rates are inputs; Early/Late rates are estimates from the complete slate pool.
const slateHasRates = (slate) => ["main", "early", "late"].includes(slate);

// Proportional allocation with caps. Zero weights stay zero unless positive-weight
// players cannot carry the required total; then distribute the remainder uniformly.
function allocateCappedRates(weights, total, capacities = weights.map(() => 1)) {
  const result = weights.map(() => 0);
  let remaining = Math.max(0, Math.min(total, capacities.reduce((a, b) => a + b, 0)));
  let active = weights.map((_, i) => i).filter((i) => capacities[i] > 0);
  while (remaining > 1e-10 && active.length) {
    const mass = active.reduce((sum, i) => sum + Math.max(0, weights[i]), 0);
    const shares = active.map((i) => remaining * (mass > 0 ? Math.max(0, weights[i]) / mass : 1 / active.length));
    const capped = active.filter((i, j) => shares[j] >= capacities[i] - 1e-12);
    if (!capped.length) {
      active.forEach((i, j) => { result[i] = shares[j]; });
      break;
    }
    const cappedSet = new Set(capped);
    for (const i of capped) { result[i] = capacities[i]; remaining -= capacities[i]; }
    active = active.filter((i) => !cappedSet.has(i));
  }
  return result;
}

// Scale inclusion odds instead of raw percentages: p' = c*p / (1-p+c*p).
// This preserves relative odds and smoothly saturates below 100%, rather than
// scaling a popular player past 100% and locking them at the cap.
function normalizeInclusionRates(rates, total) {
  const probabilities = rates.map((rate) => clamp(Number(rate) || 0, 0, 1));
  const target = clamp(total, 0, probabilities.length);
  if (target <= 0) return probabilities.map(() => 0);
  if (target >= probabilities.length) return probabilities.map(() => 1);
  const certain = probabilities.filter((p) => p === 1).length;
  const positive = probabilities.filter((p) => p > 0).length;
  // Exact source zeros/ones can make an interior solution impossible. Fall back
  // to capped allocation only for that degenerate case.
  if (target <= certain || target >= positive) {
    return allocateCappedRates(probabilities, target);
  }
  const logOdds = probabilities.map((p) => p > 0 && p < 1 ? Math.log(p) - Math.log1p(-p) : 0);
  const evaluate = (shift) => probabilities.map((p, i) => {
    if (p === 0 || p === 1) return p;
    const z = logOdds[i] + shift;
    if (z >= 0) return 1 / (1 + Math.exp(-z));
    const exp = Math.exp(z);
    return exp / (1 + exp);
  });
  let lo = -64;
  let hi = 64;
  for (let step = 0; step < 100; step++) {
    const mid = (lo + hi) / 2;
    const sum = evaluate(mid).reduce((a, b) => a + b, 0);
    if (sum < target) lo = mid;
    else hi = mid;
  }
  return evaluate((lo + hi) / 2);
}

function estimateSlateRates(rows, slate, slots) {
  if (!["early", "late"].includes(slate)) return rows;
  const out = rows.map((row) => ({ ...row }));
  const positions = [...new Set(slots.flatMap((slot) => slot.eligible))];
  const pools = Object.fromEntries(positions.map((pos) => [pos,
    out.filter((r) => r.pos === pos && r.slates?.[slate] && r.ratesAvailable),
  ]));
  const required = Object.fromEntries(positions.map((pos) => [pos,
    slots.filter((slot) => slot.eligible.length === 1 && slot.eligible[0] === pos).length,
  ]));
  // Do not invent rates for incomplete or structurally infeasible data.
  if (positions.some((pos) => pools[pos].length < required[pos])) {
    return out.map((r) => ({ ...r, ratesAvailable: false, pown: null, opt: null, lev: null }));
  }
  for (const metric of ["pown", "opt"]) {
    const mainTotals = Object.fromEntries(positions.map((pos) => [pos,
      rows.filter((r) => r.pos === pos && r.slates?.main && r.ratesAvailable)
        .reduce((sum, r) => sum + Math.max(0, Number(r[metric]) || 0), 0),
    ]));
    const targets = { ...required };
    for (const slot of slots.filter((slot) => slot.eligible.length > 1)) {
      const eligible = slot.eligible;
      const weights = eligible.map((pos) => Math.max(0, mainTotals[pos] - required[pos]));
      const capacities = eligible.map((pos) => Math.max(0, pools[pos].length - targets[pos]));
      if (capacities.reduce((a, b) => a + b, 0) < 1 - 1e-9) {
        return out.map((r) => ({ ...r, ratesAvailable: false, pown: null, opt: null, lev: null }));
      }
      const flex = allocateCappedRates(weights, 1, capacities);
      eligible.forEach((pos, i) => { targets[pos] += flex[i]; });
    }
    for (const pos of positions) {
      const pool = pools[pos];
      const rates = normalizeInclusionRates(pool.map((r) => Math.max(0, Number(r[metric]) || 0)), targets[pos]);
      pool.forEach((r, i) => { r[metric] = rates[i]; r.ratesEstimated = true; });
    }
  }
  return out.map((r) => r.slates?.[slate] && r.ratesAvailable
    ? { ...r, lev: r.opt - r.pown }
    : { ...r, ratesAvailable: false, pown: null, opt: null, lev: null });
}

function mergeSimulationProjections(projectionData, simulationData, slate, vegasData) {
  const rowsOf = (data) => Array.isArray(data) ? data : data?.rows ?? data?.players ?? data?.data ?? [];
  const mainRows = rowsOf(projectionData);
  const schedule = new Map(rowsOf(vegasData).map((game) => [normTeam(game.team ?? game.Team), game]));
  const simRows = rowsOf(simulationData);
  const position = (row) => String(row.pos ?? row.Pos ?? row.Slot ?? row.slot ?? "").toUpperCase().replace(/\d+$/, "");
  const player = (row) => row.player ?? row.Player ?? row.name ?? row.Name ?? "";
  const key = (row) => {
    const pos = position(row);
    const team = normTeam(row.team ?? row.Team);
    return `${team}|${pos}|${pos === "DST" ? "DST" : normName(player(row))}`;
  };
  const simulations = new Map(simRows.map((row) => [key(row), row]));
  const seen = new Set();
  const merge = (base, sim) => {
    const row = { ...base };
    if (sim) {
      for (const site of ["dk", "fd"]) {
        for (const field of ["proj", "floor", "ceiling"]) {
          const value = sim[`${site}_${field}`];
          if (value != null && Number.isFinite(Number(value))) row[`${site}_${field}`] = Number(value);
        }
      }
      row.pos = position(sim);
      row.team = normTeam(sim.team ?? sim.Team);
      row.hasSimulation = true;
    }
    const game = schedule.get(normTeam(row.team ?? row.Team));
    if (game) {
      row.opp = normTeam(game.opponent ?? game.opp ?? row.opp);
      row.time = formatVegasTime(game.gameDate ?? game.game_date) || row.time || "";
    }
    row.ratesAvailable = slateHasRates(slate) && !!base.hasMainRates;
    if (!row.ratesAvailable) {
      for (const site of ["dk", "fd"]) {
        row[`${site}_pown`] = null;
        row[`${site}_opt`] = null;
      }
      for (const k of ["DK pOWN%", "FD pOWN%", "DK pOWN", "FD pOWN", "DK Opt%", "FD Opt%", "DK Opt", "FD Opt"]) row[k] = null;
    }
    return row;
  };
  const out = mainRows.map((row) => {
    const k = key(row); seen.add(k);
    return merge({ ...row, hasMainRates: true }, simulations.get(k));
  });
  for (const sim of simRows) {
    if (seen.has(key(sim))) continue;
    out.push(merge({ player: player(sim), team: normTeam(sim.team ?? sim.Team), pos: position(sim), hasMainRates: false }, sim));
  }
  return out;
}

function buildSlateDetails(siteIds, vegasData, site) {
  const players = Array.isArray(siteIds?.[site]) ? siteIds[site] : siteIds?.sites?.[site] ?? [];
  const schedule = Array.isArray(vegasData) ? vegasData
    : vegasData?.rows ?? vegasData?.players ?? vegasData?.data ?? [];
  const byTeam = new Map();
  for (const game of schedule) {
    const team = normTeam(game.team ?? game.Team);
    const opponent = normTeam(game.opponent ?? game.Opponent ?? game.opp ?? game.Opp);
    if (!team || !opponent) continue;
    const key = [team, opponent].sort().join("@");
    const stamp = slateKickoffStamp(game.gameDate ?? game.game_date);
    byTeam.set(team, { key, stamp });
    byTeam.set(opponent, { key, stamp });
  }
  const details = {};
  for (const slate of ["main", "early", "late", "thu_mon"]) {
    const idKey = slate === "main" ? "id" : `${slate}_id`;
    const salaryKey = slate === "main" ? "salary" : `${slate}_salary`;
    const teams = new Set(players.filter((player) => {
      const salary = Number(player[salaryKey]);
      return player[idKey] && salary > 0 && salary < 50000;
    }).map((player) => normTeam(player.team ?? player.Team)));
    const games = new Map();
    let complete = teams.size > 0;
    for (const team of teams) {
      const game = byTeam.get(team);
      if (!game) { complete = false; continue; }
      games.set(game.key, game.stamp);
    }
    if (!complete) { details[slate] = null; continue; }
    const stamps = [...games.values()];
    const first = stamps.every(Number.isFinite) ? Math.min(...stamps) : NaN;
    const kickoff = Number.isFinite(first) ? new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC", weekday: "short", hour: "numeric", minute: "2-digit", hour12: true,
    }).format(new Date(first)).replace(",", "") + " ET" : "";
    details[slate] = { games: games.size, kickoff };
  }
  return details;
}

const normPos = (s) => {
  const raw = (s || "").toUpperCase().replace("/FLEX", "").trim();
  if (raw === "D" || raw === "DEF" || raw === "DEFENSE" || raw.includes("D/ST") || raw === "DST")
    return "DST";
  return raw;
};

/* ----------------------------- team meta --------------------------- */
const NFL_TEAMS = {
  ARI: { city: "Arizona", nickname: "Cardinals" },
  ATL: { city: "Atlanta", nickname: "Falcons" },
  BAL: { city: "Baltimore", nickname: "Ravens" },
  BUF: { city: "Buffalo", nickname: "Bills" },
  CAR: { city: "Carolina", nickname: "Panthers" },
  CHI: { city: "Chicago", nickname: "Bears" },
  CIN: { city: "Cincinnati", nickname: "Bengals" },
  CLE: { city: "Cleveland", nickname: "Browns" },
  DAL: { city: "Dallas", nickname: "Cowboys" },
  DEN: { city: "Denver", nickname: "Broncos" },
  DET: { city: "Detroit", nickname: "Lions" },
  GB: { city: "Green Bay", nickname: "Packers" },
  HOU: { city: "Houston", nickname: "Texans" },
  IND: { city: "Indianapolis", nickname: "Colts" },
  JAX: { city: "Jacksonville", nickname: "Jaguars" },
  KC: { city: "Kansas City", nickname: "Chiefs" },
  LAC: { city: "Los Angeles", nickname: "Chargers" },
  LAR: { city: "Los Angeles", nickname: "Rams" },
  LV: { city: "Las Vegas", nickname: "Raiders" },
  MIA: { city: "Miami", nickname: "Dolphins" },
  MIN: { city: "Minnesota", nickname: "Vikings" },
  NE: { city: "New England", nickname: "Patriots" },
  NO: { city: "New Orleans", nickname: "Saints" },
  NYG: { city: "New York", nickname: "Giants" },
  NYJ: { city: "New York", nickname: "Jets" },
  PHI: { city: "Philadelphia", nickname: "Eagles" },
  PIT: { city: "Pittsburgh", nickname: "Steelers" },
  SEA: { city: "Seattle", nickname: "Seahawks" },
  SF: { city: "San Francisco", nickname: "49ers" },
  TB: { city: "Tampa Bay", nickname: "Buccaneers" },
  TEN: { city: "Tennessee", nickname: "Titans" },
  WAS: { city: "Washington", nickname: "Commanders" },
};
function inferTeamFromNameForDST(name) {
  const nm = normName(name);
  for (const [abbr, t] of Object.entries(NFL_TEAMS)) {
    const nick = normName(t.nickname);
    const city = normName(t.city);
    const full = normName(`${t.city} ${t.nickname}`);
    if (
      nm === nick ||
      nm === city ||
      nm === full ||
      nm.includes(nick) ||
      nm.includes(city) ||
      nm.includes(full)
    ) {
      return abbr;
    }
  }
  return "";
}

/* ------------------------------ colors ----------------------------- */
const TEAM_COLORS = {
  ARI: "#97233F", ATL: "#A71930", BAL: "#241773", BUF: "#00338D", CAR: "#0085CA",
  CHI: "#0B162A", CIN: "#FB4F14", CLE: "#311D00", DAL: "#041E42", DEN: "#FB4F14",
  DET: "#0076B6", GB: "#203731", HOU: "#03202F", IND: "#002C5F", JAX: "#006778",
  KC: "#E31837", LAC: "#0080C6", LAR: "#003594", LV: "#000000", MIA: "#008E97",
  MIN: "#4F2683", NE: "#002244", NO: "#D3BC8D", NYG: "#0B2265", NYJ: "#125740",
  PHI: "#004C54", PIT: "#FFB612", SEA: "#002244", SF: "#AA0000", TB: "#D50A0A",
  TEN: "#0C2340", WAS: "#5A1414",
};
const hexToRGB = (hex) => {
  const h = (hex || "#888").replace("#", "");
  const v = parseInt(h, 16);
  return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 };
};
const readableText = (hex) => {
  const { r, g, b } = hexToRGB(hex);
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return L < 140 ? "#FFFFFF" : "#111111";
};
const TeamPill = ({ abbr, title }) => {
  const bg = TEAM_COLORS[abbr] || "#E5E7EB";
  const fg = readableText(bg);
  return (
    <span
      className="px-2 py-0.5 rounded"
      style={{ backgroundColor: bg, color: fg }}
      title={title || abbr}
    >
      {abbr || "—"}
    </span>
  );
};

/* --------------------------- misc helpers -------------------------- */
const parseKick = (t) => {
  if (!t) return -1;
  const m = String(t).match(/(\d{1,2}):(\d{2})\s*([AP])M/i);
  if (!m) return -1;
  let hh = Number(m[1]) % 12;
  const mm = Number(m[2]) || 0;
  if (/p/i.test(m[3])) hh += 12;
  return hh * 60 + mm;
};

const parseVegasDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatVegasTime = (value) => {
  const d = parseVegasDate(value);
  if (!d) return "—";
  const hour24 = d.getHours();
  const minute = d.getMinutes();
  const hour12 = hour24 % 12 || 12;
  const suffix = hour24 >= 12 ? "PM" : "AM";
  return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
};

const vegasKickMinutes = (value) => {
  const d = parseVegasDate(value);
  return d ? d.getHours() * 60 + d.getMinutes() : Number.POSITIVE_INFINITY;
};

const useStickyState = (key, init) => {
  const hydratedRef = useRef(false);
  const [v, setV] = useState(init);

  // Read once per key
  useEffect(() => {
    hydratedRef.current = false;
    try {
      const raw = localStorage.getItem(key);
      setV(raw != null ? JSON.parse(raw) : init);
    } catch {
      setV(init);
    }
    // mark hydrated on next tick (after state update has had a chance to land)
    Promise.resolve().then(() => {
      hydratedRef.current = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Write only after hydration so we don't wipe persisted values on mount
  useEffect(() => {
    if (!hydratedRef.current) return;
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {}
  }, [key, v]);

  return [v, setV];
};

// Persist a Set in localStorage, expose Set API
function useStickySet(key, initSet = new Set()) {
  const [raw, setRaw] = useStickyState(key, Array.from(initSet));
  const valueSet = useMemo(() => new Set(raw), [raw]);
  const setValue = (next) => {
    if (typeof next === "function") {
      setRaw((prev) => {
        const prevSet = new Set(prev);
        const out = next(prevSet);
        return Array.from(out instanceof Set ? out : new Set(out || []));
      });
    } else {
      setRaw(Array.from(next instanceof Set ? next : new Set(next || [])));
    }
  };
  return [valueSet, setValue];
}

function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const ct = (res.headers.get("content-type") || "").toLowerCase();
        let j;
        try {
          j = ct.includes("application/json") ? await res.json() : JSON.parse(await res.text());
        } catch (e) {
          const preview = await res.text();
          throw new Error(`Could not parse JSON. CT=${ct}. Preview: ${preview.slice(0, 200)}`);
        }
        if (alive) {
          setData(j);
          setErr(null);
        }
      } catch (e) {
        if (alive) {
          setData(null);
          setErr(e);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { data, err, loading };
}

// ---------- stable last-updated (no Date header fallback) ----------
function useStableLastUpdated({ dataUrl, metaUrl, artifactEndsWith } = {}) {
  const [d, setD] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      // 1) Prefer Last-Modified on the data file itself
      try {
        const h = await fetch(dataUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}

      // 2) Try GET (some hosts omit LM on HEAD)
      try {
        const r = await fetch(`${dataUrl}?_=${Date.now()}`, { cache: "no-store" });
        const lm = r.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}

      // 3) Fallback: meta.json fields (global or artifact-specific)
      try {
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" }).then(x => x.json());
        let iso = m?.updated_iso || m?.updated_utc || m?.updated || m?.last_updated || null;

        if (!iso && Array.isArray(m?.artifacts) && artifactEndsWith) {
          const art = m.artifacts.find(a => String(a.path || "").endsWith(artifactEndsWith));
          iso = art?.last_updated || art?.tags?.last_updated || null;
        }

        if (alive && iso) {
          const dt = new Date(iso);
          if (!isNaN(dt)) setD(dt);
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, [dataUrl, metaUrl, artifactEndsWith]);

  return d;
}

const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

  const nflLogo = (abbr) => `/logos/nfl/${abbr}.png`;
/* ----------------------------- sites ------------------------------- */
const SITES = {
  dk: {
    key: "dk",
    label: "DraftKings",
    logo: "/logos/dk.png",
    cap: 50000,
    slots: [
      { name: "QB", eligible: ["QB"] },
      { name: "RB1", eligible: ["RB"] },
      { name: "RB2", eligible: ["RB"] },
      { name: "WR1", eligible: ["WR"] },
      { name: "WR2", eligible: ["WR"] },
      { name: "WR3", eligible: ["WR"] },
      { name: "TE", eligible: ["TE"] },
      { name: "FLEX", eligible: ["RB", "WR", "TE"] },
      { name: "DST", eligible: ["DST"] },
    ],
    salary: "DK Sal",
    proj: "DK Proj",
    floor: "DK Floor",
    ceil: "DK Ceiling",
    pown: ["DK pOWN%", "DK pOWN"],
    opt: ["DK Opt%", "DK Opt"],
  },
  fd: {
    key: "fd",
    label: "FanDuel",
    logo: "/logos/fd.png",
    cap: 60000,
    slots: [
      { name: "QB", eligible: ["QB"] },
      { name: "RB1", eligible: ["RB"] },
      { name: "RB2", eligible: ["RB"] },
      { name: "WR1", eligible: ["WR"] },
      { name: "WR2", eligible: ["WR"] },
      { name: "WR3", eligible: ["WR"] },
      { name: "TE", eligible: ["TE"] },
      { name: "FLEX", eligible: ["RB", "WR", "TE"] },
      { name: "DST", eligible: ["DST"] }, // header becomes DEF for FD on export
    ],
    salary: "FD Sal",
    proj: "FD Proj",
    floor: "FD Floor",
    ceil: "FD Ceiling",
    pown: ["FD pOWN%", "FD pOWN"],
    opt: ["FD Opt%", "FD Opt"],
  },
};

const SOURCE = "/data/nfl/classic/latest/projections.json";
const SIMULATIONS_SOURCE = "/data/nfl/classic/latest/playersimulations.json";
const SITE_IDS_SOURCE = "/data/nfl/classic/latest/site_ids.json";
const VEGAS_SOURCE = "/data/nfl/classic/latest/vegas.json";

/* --------------------------- server (SSE) -------------------------- */
async function solveStreamNFL(payload, onItem, onDone) {
  const res = await fetch(`${API_BASE}/solve_nfl_stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok || !res.body) throw new Error("Stream failed to start");

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() ?? "";
    for (const chunk of parts) {
      const line = chunk.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      try {
        const evt = JSON.parse(line.slice(6));
        if (evt.done) onDone?.(evt);
        else onItem?.(evt);
      } catch {}
    }
  }
}

/* ---------------------- ordering helpers --------------------------- */
function orderPlayersForSite(names, rowsMap) {
  const pool = names.map((n) => rowsMap.get(n)).filter(Boolean);

  // ------------------------------------------------------------
  // Figure out which position owns the FLEX spot.
  //
  // NFL Classic normal requirements:
  // 2 RB
  // 3 WR
  // 1 TE
  //
  // Whatever position exceeds that minimum is the FLEX position.
  // ------------------------------------------------------------
  const skillCounts = {
    RB: pool.filter((r) => r.pos === "RB").length,
    WR: pool.filter((r) => r.pos === "WR").length,
    TE: pool.filter((r) => r.pos === "TE").length,
  };

  const requiredCounts = {
    RB: 2,
    WR: 3,
    TE: 1,
  };

  const flexPos =
    ["RB", "WR", "TE"].find(
      (pos) => skillCounts[pos] > requiredCounts[pos]
    ) || null;

  // ------------------------------------------------------------
  // Pick the actual FLEX player.
  //
  // Priority:
  // 1. Latest game start time
  // 2. Higher salary if start times are tied
  // ------------------------------------------------------------
  let flexPlayer = null;

  if (flexPos) {
    const candidates = pool.filter((r) => r.pos === flexPos);

    candidates.sort((a, b) => {
      const timeA = parseKick(a.time);
      const timeB = parseKick(b.time);

      // Later game gets FLEX priority
      if (timeA !== timeB) {
        return timeB - timeA;
      }

      // Same game time -> higher salary gets FLEX
      return (Number(b.salary) || 0) - (Number(a.salary) || 0);
    });

    flexPlayer = candidates[0] || null;
  }

  // Remove chosen FLEX player before assigning normal position slots
  const remaining = flexPlayer
    ? pool.filter((r) => r !== flexPlayer)
    : [...pool];

  // ------------------------------------------------------------
  // Sort normal RB / WR / TE slots by salary, highest to lowest.
  // FLEX logic above stays completely unchanged.
  // ------------------------------------------------------------
  const takeHighestSalary = (wanted) => {
    const candidates = remaining
      .map((r, index) => ({ r, index }))
      .filter(({ r }) => wanted.includes(r.pos));

    if (!candidates.length) return null;

    candidates.sort((a, b) => {
      return (Number(b.r.salary) || 0) - (Number(a.r.salary) || 0);
    });

    const chosen = candidates[0];
    return remaining.splice(chosen.index, 1)[0];
  };

  const take = (wanted) => {
    const i = remaining.findIndex((r) => wanted.includes(r.pos));

    if (i === -1) return null;

    return remaining.splice(i, 1)[0];
  };

  const out = [];

  // QB
  out.push(take(["QB"]));

  // RB — highest salary to lowest
  out.push(takeHighestSalary(["RB"]));
  out.push(takeHighestSalary(["RB"]));

  // WR — highest salary to lowest
  out.push(takeHighestSalary(["WR"]));
  out.push(takeHighestSalary(["WR"]));
  out.push(takeHighestSalary(["WR"]));

  // TE
  out.push(takeHighestSalary(["TE"]));

  // FLEX
  // Still uses latest start time first, salary second.
  out.push(
    flexPlayer || take(["RB", "WR", "TE"])
  );

  // DST
  out.push(take(["DST"]));

  return out.filter(Boolean);
}

/* ---------------------------- CSV export --------------------------- */
function toPlainCSV(lineups, rows, site) {
  const rowsByName = new Map(rows.map((r) => [r.name, r]));
  const header = ["#", "Salary", "Time", "Total", "Players"].join(",");
  const lines = lineups.map((L, i) => {
    const ordered = orderPlayersForSite(L.players, rowsByName);
    const qb = ordered.find((r) => r.pos === "QB");
    const time = qb?.time || "";
    const players = `"${ordered.map((r) => r.name).join(" • ")}"`;
    return [i + 1, L.salary, time, L.total.toFixed(1), players].join(",");
  });
  return [header, ...lines].join("\n");
}
function downloadPlainCSV(lineups, rows, site, fname = "nfl_lineups.csv") {
  const blob = new Blob([toPlainCSV(lineups, rows, site)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

/** Export CSV with site IDs — positions-only header (FD uses DEF). */
function downloadSiteLineupsCSV({
  lineups,
  site,
  slots,
  siteIds,
  rows,
  slate = "main",
  fname = "nfl_lineups_site_ids.csv",
}) {
  const siteKey = site === "fd" ? "fd" : "dk";
  const slotList = Array.isArray(slots) ? slots : SITES[site]?.slots || [];

  const slotHeaders = slotList.map((s) => {
    const base = String(s.name).replace(/\d+$/, "");
    return siteKey === "fd" && base === "DST" ? "DEF" : base;
  });
  const header = slotHeaders.join(",");

  const list = Array.isArray(siteIds?.[siteKey]) ? siteIds[siteKey] : siteIds?.sites?.[siteKey] ?? [];

  let fdPrefix = null;
  if (siteKey === "fd") {
    const prefCounts = new Map();
    for (const r of list) {
      const px = r.slateId ?? r.slate_id ?? r.groupId ?? r.group_id ?? r.lid ?? r.prefix;
      if (px != null && px !== "") {
        const key = String(px);
        prefCounts.set(key, (prefCounts.get(key) || 0) + 1);
      }
    }
    if (prefCounts.size === 1) fdPrefix = [...prefCounts.keys()][0];
    else if (prefCounts.size > 1) fdPrefix = [...prefCounts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  }

  const keyStrict = (n, t, p) => `${normName(n)}|${normTeam(t)}|${normPos(p)}`;
  const keyLoose = (n, p) => `${normName(n)}|${normPos(p)}`;
  const keyTeam = (t, p) => `${normTeam(t)}|${normPos(p)}`;

  const idIndex = new Map();
  const put = (k, rec) => {
    if (k && !idIndex.has(k)) idIndex.set(k, rec);
  };

  for (const r of list) {
    const id = String(r.id);
    const nm0 = r.name ?? r.player ?? r.Player;
    let pos0 = r.pos ?? r.Pos ?? r.POS ?? r.Position ?? r.position;
    let tm0 = r.team ?? r.Team ?? r.TEAM ?? r.team_abbr ?? r.TeamAbbrev;
    const nm = nm0 ? String(nm0) : "";
    const pos = normPos(pos0);
    let tm = normTeam(tm0);
    if (pos === "DST" && !tm) tm = inferTeamFromNameForDST(nm) || "";

        const rec = {
      id,
      early_id: r.early_id ?? null,
      late_id: r.late_id ?? null,
      thu_mon_id: r.thu_mon_id ?? null,
      nameFromSite: nm,
      team: tm,
      pos,
    };

    if (nm) {
      put(keyStrict(nm, tm, pos), rec);
      put(keyLoose(nm, pos), rec);
    }
    if (pos === "DST" && tm) {
      put(keyTeam(tm, "DST"), rec);
      const meta = NFL_TEAMS[tm];
      if (meta) {
        put(keyStrict(meta.nickname, tm, "DST"), rec);
        put(keyLoose(meta.nickname, "DST"), rec);
        put(keyStrict(`${meta.city} ${meta.nickname}`, tm, "DST"), rec);
        put(keyLoose(`${meta.city} ${meta.nickname}`, "DST"), rec);
        put(keyStrict(meta.city, tm, "DST"), rec);
        put(keyLoose(meta.city, "DST"), rec);
      }
    }
  }

  const rowsByName = new Map(rows.map((r) => [r.name, r]));

  const lines = lineups.map((L) => {
    const ordered = orderPlayersForSite(L.players, rowsByName);
    const cells = ordered.slice(0, slotList.length).map((meta) => {
      const name = meta.name;
      const pos = normPos(meta.pos);
      const tm = normTeam(meta.team);

      const ks = keyStrict(name, tm, pos);
      const kl = keyLoose(name, pos);
      let rec = idIndex.get(ks) || idIndex.get(kl);

      if (!rec && pos === "DST") {
        rec = idIndex.get(keyTeam(tm, "DST")) || rec;
        const metaTeam = NFL_TEAMS[tm];
        if (!rec && metaTeam) {
          const tryKeys = [
            keyStrict(metaTeam.nickname, tm, "DST"),
            keyLoose(metaTeam.nickname, "DST"),
            keyStrict(`${metaTeam.city} ${metaTeam.nickname}`, tm, "DST"),
            keyLoose(`${metaTeam.city} ${metaTeam.nickname}`, "DST"),
            keyStrict(metaTeam.city, tm, "DST"),
            keyLoose(metaTeam.city, "DST"),
          ];
          for (const k of tryKeys) {
            rec = rec || idIndex.get(k);
            if (rec) break;
          }
        }
        if (!rec) {
          const inferred = inferTeamFromNameForDST(name);
          if (inferred) {
            rec =
              idIndex.get(keyTeam(inferred, "DST")) ||
              idIndex.get(keyStrict(name, inferred, "DST")) ||
              idIndex.get(keyLoose(name, "DST"));
          }
        }
      }

      if (!rec) return escapeCSV(name); // still export name

      // Choose the correct raw ID based on slate
      let rawId = rec.id;
      if (slate === "early" && rec.early_id) {
        rawId = rec.early_id;
      } else if (slate === "late" && rec.late_id) {
        rawId = rec.late_id;
      } else if (slate === "thu_mon" && rec.thu_mon_id) {
        rawId = rec.thu_mon_id;
      }

      if (siteKey === "fd") {
        const outId = fdPrefix ? `${fdPrefix}-${rawId}` : rawId;
        const displayName = rec.nameFromSite || name;
        return escapeCSV(`${outId}:${displayName}`);
      }
      // DK
      return escapeCSV(`${name} (${rawId})`);
    });

    while (cells.length < slotList.length) cells.push("");
    return cells.join(",");
  });

  const csv = [header, ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}
// ---- Detailed CSV Export for NFL CLASSIC (DK: "Name (id)" | FD: "prefix-id:name") ----
function downloadDetailedLineupsCSV({
  lineups,
  site,
  slots,
  siteIds,
  rows,
  slate = "main",
  fname = "nfl_lineups_detailed.csv",
}) {
  if (!Array.isArray(lineups) || !lineups.length) {
    alert("No lineups to export.");
    return;
  }

  const siteKey = String(site).toLowerCase() === "fd" ? "fd" : "dk";
  const slotList = Array.isArray(slots) ? slots : SITES[siteKey]?.slots || [];

  // ---------- Build headers (site-aware DST label for FD) ----------
  const slotHeaders = slotList.map((s) => {
    const base = String(s.name).replace(/\d+$/, "");
    return siteKey === "fd" && base === "DST" ? "DEF" : base;
  });
  const headers = [
    "Lineup #",
    "Site",
    "Salary",
    "Total Proj",
    "Total pOWN%",
    "Total Opt%",
    ...slotHeaders,
  ];

  // ---------- Gather siteIds for current site ----------
  const list =
    (siteIds && Array.isArray(siteIds?.[siteKey]) && siteIds?.[siteKey]) ||
    (siteIds?.sites && siteIds?.sites?.[siteKey]) ||
    [];

  // ---------- Compute FD slate/group prefix (same logic as IDs export) ----------
  let fdPrefix = null;
  if (siteKey === "fd") {
    const prefCounts = new Map();
    for (const r of list) {
      const px =
        r.slateId ??
        r.slate_id ??
        r.groupId ??
        r.group_id ??
        r.lid ??
        r.prefix;
      if (px != null && px !== "") {
        const key = String(px);
        prefCounts.set(key, (prefCounts.get(key) || 0) + 1);
      }
    }
    if (prefCounts.size === 1) fdPrefix = [...prefCounts.keys()][0];
    else if (prefCounts.size > 1)
      fdPrefix = [...prefCounts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  }

  // ---------- Build robust ID index (same approach as IDs export) ----------
  const keyStrict = (n, t, p) => `${normName(n)}|${normTeam(t)}|${normPos(p)}`;
  const keyLoose = (n, p) => `${normName(n)}|${normPos(p)}`;
  const keyTeam = (t, p) => `${normTeam(t)}|${normPos(p)}`;

  const idIndex = new Map();
  const put = (k, rec) => {
    if (k && !idIndex.has(k)) idIndex.set(k, rec);
  };

  for (const r of list) {
    const id = String(r.id);
    const nm0 = r.name ?? r.player ?? r.Player;
    const nm = nm0 ? String(nm0) : "";
    let pos0 = r.pos ?? r.Pos ?? r.POS ?? r.Position ?? r.position;
    let tm0 = r.team ?? r.Team ?? r.TEAM ?? r.team_abbr ?? r.TeamAbbrev;

    const pos = normPos(pos0);
    let tm = normTeam(tm0);
    if (pos === "DST" && !tm) tm = inferTeamFromNameForDST(nm) || "";

    // ✅ carry slate-specific IDs into the record
    const rec = {
      id,
      early_id: r.early_id ?? null,
      late_id: r.late_id ?? null,
      thu_mon_id: r.thu_mon_id ?? null,
      nameFromSite: nm,
      team: tm,
      pos,
    };

    if (nm) {
      put(keyStrict(nm, tm, pos), rec);
      put(keyLoose(nm, pos), rec);
    }
    if (pos === "DST" && tm) {
      put(keyTeam(tm, "DST"), rec);
      const meta = NFL_TEAMS[tm];
      if (meta) {
        put(keyStrict(meta.nickname, tm, "DST"), rec);
        put(keyLoose(meta.nickname, "DST"), rec);
        put(keyStrict(`${meta.city} ${meta.nickname}`, tm, "DST"), rec);
        put(keyLoose(`${meta.city} ${meta.nickname}`, "DST"), rec);
        put(keyStrict(meta.city, tm, "DST"), rec);
        put(keyLoose(meta.city, "DST"), rec);
      }
    }
  }

  // ---------- For totals ----------
  const rowsByName = new Map(rows.map((r) => [r.name, r]));

  const totalize = (ordered) => {
    let proj = 0,
      pown = 0,
      opt = 0,
      salary = 0;
    for (const meta of ordered) {
      if (!meta) continue;
      const r = rowsByName.get(meta.name);
      if (!r) continue;
      proj += r.proj || 0;
      pown += (r.pown || 0) * 100;
      opt += (r.opt || 0) * 100;
      salary += Math.round(r.salary || 0);
    }
    return { proj, pown, opt, salary };
  };

  // ---------- Build CSV ----------
  const lines = [headers.map(escapeCSV).join(",")];

  for (let i = 0; i < lineups.length; i++) {
    const L = lineups[i];

    // Order by classic slots
    const rowsByNameForOrder = new Map(rows.map((r) => [r.name, r]));
    const ordered = orderPlayersForSite(L.players, rowsByNameForOrder);
    const { proj, pown, opt, salary } = totalize(ordered);
    const outSalary = Number.isFinite(L.salary) ? L.salary : salary;

    // Slot-wise display with site-specific formatting
    const slotCells = ordered.slice(0, slotList.length).map((meta) => {
      if (!meta) return "";
      const name = meta.name;
      const pos = normPos(meta.pos);
      const tm = normTeam(meta.team);

      const ks = keyStrict(name, tm, pos);
      const kl = keyLoose(name, pos);
      let rec = idIndex.get(ks) || idIndex.get(kl);

      if (!rec && pos === "DST") {
        rec = idIndex.get(keyTeam(tm, "DST")) || rec;
        const metaTeam = NFL_TEAMS[tm];
        if (!rec && metaTeam) {
          const tryKeys = [
            keyStrict(metaTeam.nickname, tm, "DST"),
            keyLoose(metaTeam.nickname, "DST"),
            keyStrict(`${metaTeam.city} ${metaTeam.nickname}`, tm, "DST"),
            keyLoose(`${metaTeam.city} ${metaTeam.nickname}`, "DST"),
            keyStrict(metaTeam.city, tm, "DST"),
            keyLoose(metaTeam.city, "DST"),
          ];
          for (const k of tryKeys) {
            rec = rec || idIndex.get(k);
            if (rec) break;
          }
        }
        if (!rec) {
          const inferred = inferTeamFromNameForDST(name);
          if (inferred) {
            rec =
              idIndex.get(keyTeam(inferred, "DST")) ||
              idIndex.get(keyStrict(name, inferred, "DST")) ||
              idIndex.get(keyLoose(name, "DST"));
          }
        }
      }

      if (!rec) return escapeCSV(name);

      // ✅ pick correct ID for the active slate
      let rawId = rec.id;
      if (slate === "early" && rec.early_id) {
        rawId = rec.early_id;
      } else if (slate === "late" && rec.late_id) {
        rawId = rec.late_id;
      } else if (slate === "thu_mon" && rec.thu_mon_id) {
        rawId = rec.thu_mon_id;
      }

      if (siteKey === "fd") {
        const outId = fdPrefix ? `${fdPrefix}-${rawId}` : rawId;
        const displayName = rec.nameFromSite || name;
        return escapeCSV(`${outId}:${displayName}`);
      }
      // DK
      return escapeCSV(`${name} (${rawId})`);
    });

    while (slotCells.length < slotList.length) slotCells.push("");

    const row = [
      i + 1,
      siteKey.toUpperCase(),
      outSalary,
      proj.toFixed(1),
      slateHasRates(slate) ? pown.toFixed(1) : "",
      slateHasRates(slate) ? opt.toFixed(1) : "",
      ...slotCells,
    ].map(escapeCSV);

    lines.push(row.join(","));
  }

  // ---------- Save file ----------
  const csv = lines.join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

/* ============================== page =============================== */
/* ---------------------- player game logs ---------------------- */
const { GameLogModal: OptimizerGameLogModal, useLazyPreparedGameLogs: useOptimizerGameLogs, GAME_LOG_YEARS: OPT_GAME_LOG_YEARS } = (() => {
  const LOGO_BASE = "/logos/nfl";
  const LOGO_EXT = "png";
  const SHOW_TEAM_LOGOS = true;
  const TEAM_ABBR_MAP = { WSH: "WAS", JAC: "JAX", OAK: "LV", SD: "LAC", STL: "LAR" };
  const keynorm = (value) => String(value ?? "").trim().toLowerCase().replace(/[%]/g, "pct").replace(/[\s._-]/g, "").replace(/[()]/g, "");
function fixAbbr(code) {
  const c = String(code || "").toUpperCase();
  return TEAM_ABBR_MAP[c] || c;
}
function TeamWithLogo({ code }) {
  const c = fixAbbr(code);
  if (!c) return null;
  if (!SHOW_TEAM_LOGOS) return <span>{c}</span>;
  const src = `${LOGO_BASE}/${c}.${LOGO_EXT}`;
  return (
    <span className="inline-flex items-center gap-1">
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        src={src}
        className="h-4 w-4 shrink-0"
        onError={(e) => (e.currentTarget.style.display = "none")}
      />
      <span>{c}</span>
    </span>
  );
}
// Prepared game-log files are generated by the NFL stat puller.
// Add a season here when that season folder is available under /public/data/nfl/std/.
const GAME_LOG_YEARS = [2026, 2025];
const DEFAULT_GAME_LOG_YEAR = 2026;

const opportunityUrl = (year) => `/data/nfl/std/${year}/ff_opportunity.json`;

function gameLogUrl(year, position) {
  return `/data/nfl/std/${year}/game_logs_${String(position || "QB").toLowerCase()}.json`;
}

// Cache by season + position so changing positions or years does not redownload files.
const GAME_LOG_PREPARED_CACHE = new Map();
const GAME_LOG_REQUEST_CACHE = new Map();


async function loadPreparedGameLogs(position, year) {
  const posKey = String(position || "QB").toUpperCase();
  const season = Number(year) || DEFAULT_GAME_LOG_YEAR;
  const cacheKey = `${season}:${posKey}`;

  if (GAME_LOG_PREPARED_CACHE.has(cacheKey)) {
    return GAME_LOG_PREPARED_CACHE.get(cacheKey);
  }
  if (GAME_LOG_REQUEST_CACHE.has(cacheKey)) {
    return GAME_LOG_REQUEST_CACHE.get(cacheKey);
  }

  const request = Promise.all([
    // The main log is required; the opportunity file may be absent for older seasons.
    fetch(gameLogUrl(season, posKey), { cache: "no-store" }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    }),
    fetch(opportunityUrl(season), { cache: "no-store" })
      .then((response) => response.ok ? response.json() : [])
      .catch(() => []),
  ])
    .then(([json, opportunityJson]) => {
      const rows = Array.isArray(json) ? json : json?.rows || json?.data || [];
      const opportunityRows = Array.isArray(opportunityJson)
        ? opportunityJson : opportunityJson?.rows || opportunityJson?.data || [];
      const prepared = mergeOpportunityIntoGameLogs(rows, opportunityRows, posKey);
      GAME_LOG_PREPARED_CACHE.set(cacheKey, prepared);
      GAME_LOG_REQUEST_CACHE.delete(cacheKey);
      return prepared;
    })
    .catch((error) => {
      GAME_LOG_REQUEST_CACHE.delete(cacheKey);
      throw error;
    });

  GAME_LOG_REQUEST_CACHE.set(cacheKey, request);
  return request;
}

function useLazyPreparedGameLogs(position, year, enabled) {
  const posKey = String(position || "QB").toUpperCase();
  const season = Number(year) || DEFAULT_GAME_LOG_YEAR;
  const cacheKey = `${season}:${posKey}`;
  const [rows, setRows] = useState(() => GAME_LOG_PREPARED_CACHE.get(cacheKey) || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const cached = GAME_LOG_PREPARED_CACHE.get(cacheKey);

    if (cached) {
      setRows(cached);
      setLoading(false);
      setError("");
      return () => { alive = false; };
    }

    setRows([]);
    if (!enabled) {
      setLoading(false);
      setError("");
      return () => { alive = false; };
    }

    setLoading(true);
    setError("");
    loadPreparedGameLogs(posKey, season)
      .then((prepared) => { if (alive) setRows(prepared); })
      .catch((e) => { if (alive) setError(String(e)); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [cacheKey, posKey, season, enabled]);

  return { rows, loading, error };
}


const NFL_SUFFIXES = new Set(["JR", "SR", "II", "III", "IV", "V"]);

function normalizePlayerName(name = "") {
  const parts = String(name)
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  if (parts.length > 1 && NFL_SUFFIXES.has(parts[parts.length - 1])) parts.pop();
  return parts.join(" ");
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function firstValue(row, keys, fallback = "") {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null && row?.[k] !== "") return row[k];
  }

  // Feeds use a mix of snake_case, spaced labels, and uppercase headers.
  // Try a normalized-key lookup before giving up.
  if (row && typeof row === "object") {
    const normalized = new Map(Object.keys(row).map((key) => [keynorm(key), key]));
    for (const k of keys) {
      const actual = normalized.get(keynorm(k));
      if (actual && row[actual] !== undefined && row[actual] !== null && row[actual] !== "") return row[actual];
    }
  }
  return fallback;
}


function nullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(String(value).replace(/[%,$]/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePreparedGameLogRow(row = {}, fallbackPosition = "") {
  const position = String(firstValue(row, ["position", "pos"], fallbackPosition)).toUpperCase();
  const name = firstValue(row, ["player", "name", "player_name"], "");
  const team = fixAbbr(firstValue(row, ["team", "posteam", "recent_team"], ""));

  return {
    raw: row,
    season: num(firstValue(row, ["season"], 0)),
    week: num(firstValue(row, ["week"], 0)),
    seasonType: String(firstValue(row, ["season_type", "game_type"], "REG")),
    playerId: String(firstValue(row, ["player_id", "player_gsis_id", "gsis_id"], "") || "").trim().toLowerCase(),
    name,
    pos: position,
    team,
    opp: fixAbbr(firstValue(row, ["opponent", "opp", "opponent_team"], "")),
    result: String(firstValue(row, ["result"], "")),
    teamScore: firstValue(row, ["team_score"], null),
    opponentScore: firstValue(row, ["opponent_score"], null),
    _playerKey: normalizePlayerName(firstValue(row, ["player_key", "player", "name"], name)),

    passCmp: num(firstValue(row, ["pass_completions", "passCmp"], 0)),
    passAtt: num(firstValue(row, ["pass_attempts", "passAtt"], 0)),
    passYds: num(firstValue(row, ["passing_yards", "passYds"], 0)),
    passTd: num(firstValue(row, ["passing_tds", "passTd"], 0)),
    interceptions: num(firstValue(row, ["interceptions"], 0)),
    compPct: num(firstValue(row, ["completion_pct", "compPct"], 0)),
    ypa: num(firstValue(row, ["yards_per_attempt", "ypa"], 0)),
    sacks: num(firstValue(row, ["sacks"], 0)),

    rushAtt: num(firstValue(row, ["rushing_attempts", "rushAtt"], 0)),
    rushYds: num(firstValue(row, ["rushing_yards", "rushYds"], 0)),
    rushTd: num(firstValue(row, ["rushing_tds", "rushTd"], 0)),
    ypc: num(firstValue(row, ["yards_per_carry", "ypc"], 0)),

    targets: num(firstValue(row, ["targets"], 0)),
    rec: num(firstValue(row, ["receptions", "rec"], 0)),
    recYds: num(firstValue(row, ["receiving_yards", "recYds"], 0)),
    recTd: num(firstValue(row, ["receiving_tds", "recTd"], 0)),
    ypr: num(firstValue(row, ["yards_per_reception", "ypr"], 0)),
    airYards: num(firstValue(row, ["air_yards", "airYards"], 0)),
    targetShare: firstValue(row, ["target_share", "targetShare"], null),
    airShare: firstValue(row, ["air_yards_share", "airShare"], null),
    snapPct: firstValue(row, ["snap_pct", "snapPct"], null),
    fumblesLost: num(firstValue(row, ["fumbles_lost", "fumblesLost"], 0)),
    dk: num(firstValue(row, ["dk_points", "dk"], 0)),
    fd: num(firstValue(row, ["fd_points", "fd"], 0)),

    // Filled from ff_opportunity.json when available.
    xFp: null,
    fpDiff: null,
    opportunities: null,

    pointsAllowed: num(firstValue(row, ["points_allowed"], 0)),
    fumbleRecoveries: num(firstValue(row, ["fumble_recoveries"], 0)),
    defensiveTds: num(firstValue(row, ["defensive_tds"], 0)),
  };
}

function mergeOpportunityIntoGameLogs(logRows = [], opportunityRows = [], fallbackPosition = "") {
  const prepared = logRows.map((row) => normalizePreparedGameLogRow(row, fallbackPosition));

  const byId = new Map();
  const byName = new Map();

  for (const raw of opportunityRows || []) {
    const season = num(firstValue(raw, ["season"], 0));
    const week = num(firstValue(raw, ["week"], 0));
    if (!season || !week) continue;

    const playerId = String(
      firstValue(raw, ["player_id", "player_gsis_id", "gsis_id"], "") || ""
    ).trim().toLowerCase();

    const name = normalizePlayerName(
      firstValue(raw, ["full_name", "player", "player_name", "name"], "")
    );
    const team = fixAbbr(firstValue(raw, ["posteam", "team", "recent_team"], ""));

    const entry = {
      xFp: nullableNumber(
        firstValue(raw, ["total_fantasy_points_exp", "fantasy_points_exp", "xfp"], null)
      ),
      fpDiff: nullableNumber(
        firstValue(raw, ["total_fantasy_points_diff", "fantasy_points_diff", "fpoe"], null)
      ),
      opportunities:
        num(firstValue(raw, ["rush_attempt", "rushing_attempts"], 0)) +
        num(firstValue(raw, ["rec_attempt", "targets"], 0)),
    };

    if (playerId) {
      byId.set(`${season}__${week}__${playerId}`, entry);
    }

    if (name) {
      byName.set(`${season}__${week}__${team}__${name}`, entry);
      byName.set(`${season}__${week}____${name}`, entry);
    }
  }

  return prepared.map((row) => {
    let opportunity = null;

    if (row.playerId) {
      opportunity = byId.get(`${row.season}__${row.week}__${row.playerId}`) || null;
    }

    if (!opportunity) {
      opportunity =
        byName.get(`${row.season}__${row.week}__${row.team}__${row._playerKey}`) ||
        byName.get(`${row.season}__${row.week}____${row._playerKey}`) ||
        null;
    }

    if (!opportunity) return row;

    return {
      ...row,
      xFp: opportunity.xFp,
      fpDiff: opportunity.fpDiff,
      opportunities: opportunity.opportunities,
    };
  });
}

function pct(v) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  const x = Math.abs(n) <= 1.5 ? n * 100 : n;
  return `${x.toFixed(1).replace(/\.0$/, "")}%`;
}

function one(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(1).replace(/\.0$/, "") : "—";
}



function GameLogModal({ player, position, preparedRows = [], loading, error, onClose, isDark, gameLogYear, gameLogYears, onGameLogYearChange }) {
  const logs = useMemo(() => {
    const wanted = normalizePlayerName(player);
    return preparedRows
      .filter((r) => {
        const rowKey = r._playerKey || normalizePlayerName(
          firstValue(r, ["player_key", "player", "name", "full_name", "player_display_name"], "")
        );
        const rowPos = String(firstValue(r, ["pos", "position", "player_position"], "")).toUpperCase();
        return rowKey === wanted && (!rowPos || rowPos === position || (position === "RB" && rowPos === "FB"));
      })
      .sort((a, b) => a.season - b.season || a.week - b.week);
  }, [preparedRows, player, position]);

  useEffect(() => {
    const fn = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const logNumber = (value) => value == null || value === "" ? "—" : one(value);
  const avg = (k) => {
    const values = logs.filter((r) => r[k] != null && r[k] !== "").map((r) => Number(r[k])).filter(Number.isFinite);
    return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
  };
  const signed = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return "—";
    const n = Number(v);
    const body = Math.abs(n).toFixed(1).replace(/\.0$/, "");
    return n > 0 ? `+${body}` : n < 0 ? `-${body}` : "0";
  };
  const fpoeCell = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return <span className="text-slate-500">—</span>;
    const n = Number(v);
    const tone = n > 0.05 ? (isDark ? "text-emerald-400" : "text-emerald-700") : n < -0.05 ? (isDark ? "text-rose-400" : "text-rose-700") : C.muted;
    return <span className={`font-extrabold ${tone}`}>{signed(n)}</span>;
  };
  const team = logs[0]?.team || "";
  const C = isDark
    ? { shell: "bg-slate-950 border-slate-700 text-slate-100", muted: "text-slate-400", head: "bg-slate-900", row: "border-slate-800 hover:bg-sky-500/10", card: "bg-slate-900/70 border-slate-800" }
    : { shell: "bg-white border-slate-300 text-slate-900", muted: "text-slate-500", head: "bg-slate-100", row: "border-slate-200 hover:bg-sky-50", card: "bg-slate-50 border-slate-200" };

  const common = [
    ["Season", r => r.season],
    ["Wk", r => r.week],
    ["Opp", r => (
      <div className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none">
        <TeamWithLogo code={r.opp} />
        {r.result && Number.isFinite(r.teamScore) && Number.isFinite(r.opponentScore) ? (
          <span
            className={`text-[9px] font-extrabold ${
              r.result === "W"
                ? "text-emerald-400"
                : r.result === "L"
                  ? "text-red-400"
                  : "text-slate-400"
            }`}
            title={`${r.result} ${r.teamScore}-${r.opponentScore}`}
          >
            {r.result} {r.teamScore}–{r.opponentScore}
          </span>
        ) : null}
      </div>
    )],
  ];
  const fantasy = [["DK", r => logNumber(r.dk)], ["FD", r => logNumber(r.fd)], ["xFP", r => r.xFp == null ? "—" : logNumber(r.xFp)], ["FPOE", r => fpoeCell(r.fpDiff)]];
  const passing = [
    ["Cmp", (r) => r.passCmp], ["Att", (r) => r.passAtt], ["Cmp%", (r) => pct(r.compPct)],
    ["Yds", (r) => r.passYds], ["TD", (r) => r.passTd],
    ["INT", (r) => r.interceptions], ["YPA", (r) => logNumber(r.ypa)],
  ];
  const rushing = [
    ["Att", (r) => r.rushAtt], ["Yds", (r) => r.rushYds], ["TD", (r) => r.rushTd],
    ...(position === "RB" ? [["YPC", (r) => logNumber(r.ypc)]] : []),
  ];
  const receiving = [
    ["Tgt", (r) => r.targets], ["Rec", (r) => r.rec], ["Yds", (r) => r.recYds],
    ["TD", (r) => r.recTd], ["YPR", (r) => logNumber(r.ypr)],
    ...(position !== "RB" ? [["Air Yds", (r) => r.airYards]] : []),
  ];
  const usage = [
    ...(position === "RB" ? [["Rush + Tgts", (r) =>
      r.opportunities == null ? r.rushAtt + r.targets : r.opportunities,
      "Rushing attempts plus targets"]] : []),
    ["Tgt%", (r) => pct(r.targetShare), "Share of team targets"],
    ...(position !== "RB" ? [["Air%", (r) => pct(r.airShare), "Share of team air yards"]] : []),
    ["Snap%", (r) => pct(r.snapPct), "Offensive snap share"],
  ];
  const groups = [
    { label: "Game", tone: isDark ? "text-slate-400 bg-slate-800/50" : "text-slate-600 bg-slate-200/60", columns: common },
    ...(position === "QB" ? [
      { label: "Passing", tone: isDark ? "text-sky-300 bg-sky-500/10" : "text-sky-700 bg-sky-500/10", columns: passing },
      { label: "Rushing", tone: isDark ? "text-emerald-300 bg-emerald-500/10" : "text-emerald-700 bg-emerald-500/10", columns: rushing },
    ] : [
      ...(position === "RB" ? [{ label: "Rushing", tone: isDark ? "text-emerald-300 bg-emerald-500/10" : "text-emerald-700 bg-emerald-500/10", columns: rushing }] : []),
      { label: "Receiving", tone: isDark ? "text-violet-300 bg-violet-500/10" : "text-violet-700 bg-violet-500/10", columns: receiving },
      { label: "Usage", tone: isDark ? "text-slate-300 bg-slate-800/60" : "text-slate-600 bg-slate-200/60", columns: usage },
    ]),
    { label: "Fantasy", tone: isDark ? "text-amber-300 bg-amber-500/10" : "text-amber-700 bg-amber-500/10", columns: fantasy },
  ];
  const columns = groups.flatMap((group) => group.columns.map(([label, render, title], index) => ({
    label, render, title: title || `${group.label}: ${label}`, group: group.label, start: index === 0,
  })));

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-2 md:p-5" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={`${player} game log`} className={`w-full max-w-[1500px] max-h-[92vh] flex flex-col overflow-hidden rounded-2xl border shadow-2xl ${C.shell}`}>
        <div className="flex shrink-0 flex-wrap items-start justify-between gap-4 border-b border-current/10 px-4 py-3 md:px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {team ? <img src={`${LOGO_BASE}/${team}.${LOGO_EXT}`} className="h-7 w-7 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} /> : null}
              <h2 className="truncate text-lg md:text-2xl font-extrabold">{player}</h2>
              <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-bold text-sky-400">{position}</span>
            </div>
            <p className={`mt-0.5 text-xs ${C.muted}`}>{gameLogYear} season / Weekly game log / Week 1 first</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <label className={`flex h-9 items-center gap-2 rounded-lg border px-3 ${C.card}`}>
              <span className={`text-[10px] font-bold uppercase tracking-wide ${C.muted}`}>Season</span>
              <select
                value={gameLogYear}
                onChange={(e) => onGameLogYearChange(Number(e.target.value))}
                className={
                  "bg-transparent text-sm font-bold outline-none " +
                  (isDark ? "text-slate-100" : "text-slate-900")
                }
              >
                {gameLogYears.map((year) => (
                  <option
                    key={year}
                    value={year}
                    className={isDark ? "bg-slate-900 text-slate-100" : "bg-white text-slate-900"}
                  >
                    {year}
                  </option>
                ))}
              </select>
            </label>
            <button onClick={onClose} className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border ${C.card}`} aria-label="Close game log">✕</button>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2 px-3 py-3 sm:grid-cols-4 md:px-5">
          {[
            ["Games", logs.length],
            ["Avg DK", logNumber(avg("dk"))],
            ["Avg xFP", avg("xFp") == null ? "—" : logNumber(avg("xFp"))],
            [position === "QB" ? "Avg Pass Yds" : position === "RB" ? "Rush + Tgts/G" : "Avg Targets", position === "QB" ? logNumber(avg("passYds")) : position === "RB" ? one(logs.length ? logs.reduce((sum, r) => sum + Number(r.opportunities == null ? r.rushAtt + r.targets : r.opportunities), 0) / logs.length : 0) : logNumber(avg("targets"))],
          ].map(([label, value]) => (
            <div key={label} className={`rounded-xl border px-3 py-2 ${C.card}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wide ${C.muted}`}>{label}</div>
              <div className="text-lg font-extrabold leading-tight">{loading ? "—" : value}</div>
            </div>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto border-t border-current/10">
          {loading ? <div className={`p-8 text-center text-sm ${C.muted}`}>Loading game logs…</div> : error ? <div className="p-8 text-center text-sm text-red-400">Unable to load game logs. Please try again.</div> : logs.length === 0 ? <div className={`p-8 text-center text-sm ${C.muted}`}>No weekly game logs found for this player.</div> : (
            <table className="w-max min-w-full border-separate border-spacing-0 text-[11px] tabular-nums md:text-xs">
              <caption className="sr-only">{player} weekly game log for {gameLogYear}, earliest week first</caption>
              <thead className={`sticky top-0 z-20 ${C.head}`}>
                <tr>
                  {groups.map((group, index) => (
                    <th key={group.label} scope="colgroup" colSpan={group.columns.length}
                      className={`h-8 border-b border-current/10 px-3 text-center text-[10px] font-extrabold uppercase tracking-[0.14em] ${group.tone} ${index ? (isDark ? "border-l border-l-slate-700" : "border-l border-l-slate-300") : ""}`}>
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columns.map(({ label, title, group, start }, i) => (
                    <th key={`${group}-${label}-${i}`} scope="col" title={title}
                      className={`h-9 whitespace-nowrap border-b ${isDark ? "border-slate-700" : "border-slate-300"} ${C.head} px-3 text-center font-bold ${start && i ? (isDark ? "border-l border-l-slate-700" : "border-l border-l-slate-300") : ""} ${
                        label === "DK" || label === "FD" ? (isDark ? "text-sky-400" : "text-sky-700") :
                        label === "xFP" || label === "FPOE" ? (isDark ? "text-amber-300" : "text-amber-700") : (isDark ? "text-slate-300" : "text-slate-600")
                      }`}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((r, ri) => (
                  <tr key={`${r.season}-${r.week}-${ri}`} className={`${ri % 2 ? (isDark ? "bg-slate-900/40" : "bg-slate-50") : ""} transition-colors ${C.row}`}>
                    {columns.map(({ label, render, group, start }, ci) => (
                      <td key={`${group}-${label}-${ci}`}
                        className={`h-10 whitespace-nowrap border-b ${isDark ? "border-slate-800/70" : "border-slate-200"} px-3 text-center ${start && ci ? (isDark ? "border-l border-l-slate-700" : "border-l border-l-slate-300") : ""} ${
                          label === "DK" || label === "FD" ? (isDark ? "bg-sky-500/[0.04] font-extrabold text-sky-400" : "bg-sky-500/[0.04] font-extrabold text-sky-700") :
                          label === "xFP" ? (isDark ? "bg-amber-500/[0.04] font-extrabold text-amber-300" : "bg-amber-500/[0.04] font-extrabold text-amber-700") : ""
                        }`}>
                        {render(r) ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className={`flex shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-current/10 px-4 py-2.5 text-[10px] leading-relaxed md:px-5 ${C.card} ${C.muted}`}>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><span className={`font-bold ${isDark ? "text-amber-300" : "text-amber-700"}`}>xFP</span> Expected fantasy points</span>
            <span><span className={`font-bold ${isDark ? "text-amber-300" : "text-amber-700"}`}>FPOE</span> Fantasy points over expected</span>
            <span><span className="text-emerald-400">+ Above</span> / <span className="text-rose-400">− Below</span> expected</span>
          </div>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}


return { GameLogModal, useLazyPreparedGameLogs, GAME_LOG_YEARS };
})();

export default function NFLOptimizer({ activeTab = "optimizer", setActiveTab = () => {} }) {
  const { data: mainProjectionData, err, loading } = useJson(SOURCE);
  const { data: simulationData } = useJson(SIMULATIONS_SOURCE);
  const { data: siteIds } = useJson(SITE_IDS_SOURCE);
  const { data: vegasData } = useJson(VEGAS_SOURCE);
  const projectionsUpdatedAt = useStableLastUpdated({
    dataUrl: SIMULATIONS_SOURCE,
    metaUrl: "/data/nfl/latest/meta.json",
    artifactEndsWith: SIMULATIONS_SOURCE,
  });
  const ownershipUpdatedAt = useStableLastUpdated({
    dataUrl: SOURCE,
    metaUrl: SOURCE.replace(/[^/]+$/, "meta.json"),
    artifactEndsWith: SOURCE,
  });

  const [theme, setTheme] = useStickyState("nflOpt.theme", "dark");
  const isDark = theme === "dark";
  const T = isDark ? OPT_DARK_THEME : OPT_LIGHT_THEME;

  const [gameLogPlayer, setGameLogPlayer] = useState(null);
  const [gameLogYear, setGameLogYear] = useState(2026);
  const {
    rows: preparedGameLogRows,
    loading: gameLogsLoading,
    error: gameLogsError,
  } = useOptimizerGameLogs(gameLogPlayer?.pos || "QB", gameLogYear, Boolean(gameLogPlayer));


    const ghostBtnClass = `${cls.btn.ghost} ${
    isDark
      ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
      : "border-gray-200 bg-white text-slate-800 hover:bg-gray-50"
  }`;

  const iconBtnClass = `${cls.btn.iconSm} ${
    isDark
      ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
      : "border-gray-300 bg-white text-slate-800 hover:bg-gray-50"
  }`;

  const glassShellClass = isDark
    ? "bg-white/5 backdrop-blur-xl border border-white/10"
    : "bg-white/80 backdrop-blur-xl border border-slate-300 shadow-sm";

  const glassActiveClass = isDark
    ? "bg-white/12 backdrop-blur-md border border-white/20 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]"
    : "bg-sky-500 border border-sky-500 text-slate-950 shadow-sm";

  const glassIdleClass = isDark
    ? "bg-white/5 backdrop-blur-md border border-white/10 text-slate-300 hover:bg-white/8"
    : "bg-white/70 backdrop-blur-md border border-slate-300 text-slate-700 hover:bg-slate-100";

  const [site, setSite] = useStickyState("nflOpt.site", "dk");
  const cfg = SITES[site];

  const [slate, setSlate] = useState("main");
  const data = useMemo(() => mergeSimulationProjections(mainProjectionData, simulationData, slate, vegasData), [mainProjectionData, simulationData, slate, vegasData]);
  const [selectedSlate, setSelectedSlate] = useState("main");
  const [isSlatePending, startSlateTransition] = useTransition();
  const selectSlate = (nextSlate) => {
    setSelectedSlate(nextSlate);
    if (!slateHasRates(nextSlate) && ["pown", "opt"].includes(optBy)) setOptBy("proj");
    startSlateTransition(() => setSlate(nextSlate));
  };
  


  const [optBy, setOptBy] = useStickyState("nflOpt.optBy", "proj");
  const [numLineups, setNumLineups] = useStickyState("nflOpt.N", 20);

  const [maxSalary, setMaxSalary] = useStickyState(`nflOpt.${site}.cap`, cfg.cap);
  useEffect(() => {
    setMaxSalary(SITES[site].cap);
  }, [site]);

  const [globalMax, setGlobalMax] = useStickyState("nflOpt.gmax", 100);
  const [randomness, setRandomness] = useStickyState("nflOpt.rand", 0);

  // per-player auto-exclude cap
  const [maxPownCap, setMaxPownCap] = useStickyState("nflOpt.maxPownCap", "");
  const [minValue, setMinValue] = useStickyState("nflOpt.minValue", "");

  // lineup-level pOWN% cap (sum)
  const [maxLineupPown, setMaxLineupPown] = useStickyState("nflOpt.maxLineupPown", "");

  // lineup-level geo mean pOWN% cap
const [maxGeoMeanPown, setMaxGeoMeanPown] = useStickyState("nflOpt.maxGeoMeanPown", "");

  // constraints
  const [qbStackMin, setQbStackMin] = useStickyState("nflOpt.qbStackMin", 0);
  const [bringbackMin, setBringbackMin] = useStickyState("nflOpt.bringbackMin", 0);
  const [allowTeInFlex, setAllowTeInFlex] = useStickyState("nflOpt.allowTeInFlex", true);
  const [stackAllowRB, setStackAllowRB] = useStickyState("nflOpt.stackAllowRB", false);
  const [avoidRbVsOppDst, setAvoidRbVsOppDst] = useStickyState("nflOpt.avoidRbVsOppDst", true);
  const [avoidOffenseVsOppDst, setAvoidOffenseVsOppDst] = useStickyState(
    "nflOpt.avoidOffVsOppDst",
    false
  );
  const [maxFromTeam, setMaxFromTeam] = useStickyState("nflOpt.maxFromTeam", "");

  // Only allow stacks to originate from selected teams
  const [restrictStacksToTeams, setRestrictStacksToTeams] = useStickyState(
    "nflOpt.restrictStacksToTeams",
    false
  );

  const [posFilter, setPosFilter] = useState("ALL");
  const [selectedGames, setSelectedGames] = useState(() => new Set());
  const [selectedTeams, setSelectedTeams] = useStickySet(`nflOpt.${site}.selectedTeams.${slate}`, new Set());

  const [q, setQ] = useState("");

  // Persist per-site so refresh doesn't clear them
  const buildsKey = (k) => `nflOpt.${site}.${k}`;
  const [locks, setLocks] = useStickySet(buildsKey("locks"), new Set());
  const [excls, setExcls] = useStickySet(buildsKey("excls"), new Set());
  const [minPct, setMinPct] = useStickyState(buildsKey("minPct"), {});
  const [maxPct, setMaxPct] = useStickyState(buildsKey("maxPct"), {});
  const [boost, setBoost] = useStickyState(buildsKey("boost"), {});
  const [teamBoost, setTeamBoost] = useStickyState(buildsKey("teamBoost"), {});

  // Custom projection overrides + full player-pool display controls.
  // Blank custom projection = use original projection.
  const [customProj, setCustomProj] = useStickyState(`nflOpt.${site}.${slate}.customProj`, {});
  const [projDraft, setProjDraft] = useState({});

  useEffect(() => {
    setProjDraft({});
  }, [site, slate]);
  const [hideUnprojected, setHideUnprojected] = useStickyState(
    `nflOpt.${site}.${slate}.hideUnprojected.checkboxDefault`,
    true
  );

  const [hideExcluded, setHideExcluded] = useState(false);

  // Unprojected players stay excluded by default even when visible.
  // This set tracks the ones you manually allow back into builds.
  const [unprojectedAllowed, setUnprojectedAllowed] = useStickySet(
    `nflOpt.${site}.${slate}.unprojectedAllowed`,
    new Set()
  );

  // groups / team rules
  const [groups, setGroups] = useStickyState(`nflOpt.${site}.groups`, []);
  const [pairCaps, setPairCaps] = useStickyState(`nflOpt.${site}.pairCaps`, []);
  const [teamStacks, setTeamStacks] = useStickyState(`nflOpt.${site}.teamStacks`, []);
  const [ifThenRules, setIfThenRules] = useStickyState(`nflOpt.${site}.ifThenRules`, []);
  const [advancedOpen, setAdvancedOpen] = useStickyState(`nflOpt.${site}.advancedOpen`, {
    stacks: false,
    conditional: false,
    groups: false,
    pairCaps: false,
  });

  // builds per site
  const [builds, setBuilds] = useStickyState(buildsKey("builds"), []);
  const [activeBuildId, setActiveBuildId] = useStickyState(buildsKey("active"), null);

  const [lineups, setLineups] = useState([]);
  const [stopInfo, setStopInfo] = useState(null);
  const [selectedExposurePlayer, setSelectedExposurePlayer] = useState(null);

  const [progressActual, setProgressActual] = useState(0);
  const [progressUI, setProgressUI] = useState(0);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const tickRef = useRef(null);
  const [showBuildAnalysis, setShowBuildAnalysis] = useState(false);

  // live clock for build chips "time ago"
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!isOptimizing) return;
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setProgressUI((p) => {
        const N = Math.max(1, Number(numLineups) || 1);
        const target = Math.max(progressActual, 1);
        const ceiling = Math.max(0, N - 1);
        return Math.min(Math.max(p + 1, target), ceiling);
      });
    }, 250);
    return () => clearInterval(tickRef.current);
  }, [isOptimizing, progressActual, numLineups]);

  // On site switch, clear live results but keep sticky constraints
  useEffect(() => {
    setLineups([]);
    setStopInfo(null);
    setProgressActual(0);
    setProgressUI(0);
    setIsOptimizing(false);
  }, [site]);

  // If there is an active build (persisted), auto-load its lineups
  useEffect(() => {
    if (!isOptimizing && activeBuildId != null) {
      const b = builds.find((x) => x.id === activeBuildId);
      if (b) {
        const N = (b.lineups || []).length;
        setLineups(b.lineups || []);
        setProgressActual(N);
        setProgressUI(N);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBuildId, builds, isOptimizing]);

  /* ------------------------------ rows ------------------------------ */
  const rows = useMemo(() => {
    if (!data) return [];

    const arr = Array.isArray(data?.rows)
      ? data.rows
      : Array.isArray(data?.players)
      ? data.players
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data)
      ? data
      : [];

      const pct = (v) => {
        if (v == null) return 0;

        // numbers: if already 0..1 it's a decimal; if >1 it's a percent value (e.g. 35.3)
        if (typeof v === "number") {
          return Number.isFinite(v) ? (v > 1 ? v / 100 : v) : 0;
        }

        const raw = String(v).trim();
        if (!raw) return 0;

        // remove commas/spaces and optional % sign
        const s = raw.replace(/[,\s]/g, "").replace(/%/g, "");
        const n = Number(s);
        if (!Number.isFinite(n)) return 0;

        // if > 1 assume "35.3" style; else assume "0.353" style
        return n > 1 ? n / 100 : n;
      };

    const siteKey = cfg.key; // dk or fd
    const projKeyLC = `${siteKey}_proj`;
    const salKeyLC = `${siteKey}_sal`;
    const pownKeyLC = `${siteKey}_pown`;
    const optKeyLC = `${siteKey}_opt`;

        // Map NFL site_ids by normalized name for this site (dk/fd)
    const siteKeyForIds = site === "fd" ? "fd" : "dk";
    const list =
      Array.isArray(siteIds?.[siteKeyForIds])
        ? siteIds[siteKeyForIds]
        : siteIds?.sites?.[siteKeyForIds] ?? [];

    const siteIdsByName = new Map();
    // For FD defenses, we also want a team-based map: team -> D record
    const fdDefenseByTeam = new Map();

    for (const rec of list) {
      const nm = (rec.name || "").trim();
      if (nm) {
        siteIdsByName.set(nm.toLowerCase(), rec);
      }

      // Special case: FanDuel defenses use pos "D" and full team name
      if (siteKeyForIds === "fd" && rec.pos === "D" && rec.team) {
        fdDefenseByTeam.set(String(rec.team).toUpperCase(), rec);
      }
    }


    const mapped = arr.map((r) => {
      const name =
        r.player ??
        r.Player ??
        r.Name ??
        r.playerName ??
        r.name ??
        "";
      const pos = String(
        r.pos ?? r.Pos ?? r.POS ?? r.Position ?? r.position ?? ""
      ).toUpperCase();
      const team =
        r.team ??
        r.Team ??
        r.Tm ??
        r.TEAM ??
        r.TEAM_ABBR ??
        r.team_abbr ??
        "";
      const opp =
        r.opp ??
        r.Opp ??
        r.OPP ??
        r.Opponent ??
        r.opponent ??
        "";

      // --- lookup site_ids record by name ---
      const keyName = String(name || "").toLowerCase();

      // Start with name-based lookup
      let rec = siteIdsByName.get(keyName) || null;

      // FanDuel DST special case:
      // projections use "Seahawks", site_ids use "Seattle Seahawks" with pos "D"
      if (!rec && site === "fd" && pos === "DST" && team) {
        rec = fdDefenseByTeam.get(String(team).toUpperCase()) || null;
      }

      const hasRec = !!rec;

      // Main / Early / Late / Thu–Mon IDs + salaries from site_ids.json
      const mainId = hasRec ? rec.id ?? null : null;
      const mainSalaryId = hasRec ? rec.salary ?? null : null;

      const earlyId = hasRec ? rec.early_id ?? null : null;
      const earlySalaryId = hasRec ? rec.early_salary ?? null : null;

      const lateId = hasRec ? rec.late_id ?? null : null;
      const lateSalaryId = hasRec ? rec.late_salary ?? null : null;
      const thuMonId = hasRec ? rec.thu_mon_id ?? null : null;
      const thuMonSalaryId = hasRec ? rec.thu_mon_salary ?? null : null;

      const hasMain = String(mainId ?? "").trim() !== "" && num(mainSalaryId) > 0;
      const hasEarly = String(earlyId ?? "").trim() !== "" && num(earlySalaryId) > 0;
      const hasLate = String(lateId ?? "").trim() !== "" && num(lateSalaryId) > 0;
      const hasThuMon = !!thuMonId && thuMonSalaryId != null && num(thuMonSalaryId) > 0;

      // Slate availability:
      // - If site_ids has a row, use its exact availability
      // - Without a matching site ID, the player is unavailable on every slate.
      let slates;
      if (hasRec) {
        slates = { main: hasMain, early: hasEarly, late: hasLate, thu_mon: hasThuMon };
      } else {
        slates = { main: false, early: false, late: false, thu_mon: false };
      }

      // Choose salary + ID ONLY if that slate is actually available
      let salaryFromIds = null;
      let chosenId = null;

      if (slate === "main" && hasMain) {
        salaryFromIds = mainSalaryId;
        chosenId = mainId;
      } else if (slate === "early" && hasEarly) {
        salaryFromIds = earlySalaryId;
        chosenId = earlyId;
      } else if (slate === "late" && hasLate) {
        salaryFromIds = lateSalaryId;
        chosenId = lateId;
      } else if (slate === "thu_mon" && hasThuMon) {
        salaryFromIds = thuMonSalaryId;
        chosenId = thuMonId;
      }

      // Start from projections salary as fallback
      let salary = num(
        r[salKeyLC] ?? r[`${cfg.label} Sal`] ?? r.Salary ?? r.salary
      );

      // Override with site_ids salary if valid for this slate
      if (salaryFromIds != null && Number.isFinite(+salaryFromIds)) {
        salary = +salaryFromIds;
      }

      const proj = num(
        r[projKeyLC] ?? r[`${cfg.label} Proj`] ?? r.Projection ?? r.Points
      );
      const val =
        Number.isFinite(proj) && salary > 0 ? (proj / salary) * 1000 : 0;
      const floor = num(
        r[`${cfg.key}_floor`] ?? r[`${cfg.label} Floor`] ?? r.Floor
      );
      const ceil = num(
        r[`${cfg.key}_ceiling`] ??
          r[`${cfg.key}_ceil`] ??
          r[`${cfg.label} Ceiling`] ??
          r[`${cfg.label} Ceil`] ??
          r.Ceiling
      );
      const pown = pct(
        r[pownKeyLC] ??
          r["DK pOWN%"] ??
          r["FD pOWN%"] ??
          r["DK pOWN"] ??
          r["FD pOWN"]
      );
      const opt = pct(
        r[optKeyLC] ??
          r["DK Opt%"] ??
          r["FD Opt%"] ??
          r["DK Opt"] ??
          r["FD Opt"]
      );

      return {
        name,
        ratesAvailable: r.ratesAvailable,
        pos,
        team,
        opp,
        salary,
        proj,
        val,
        floor,
        ceil,
        pown: r.ratesAvailable ? pown : null,
        opt: r.ratesAvailable ? opt : null,
        slates, // { main, early, late, thu_mon } strict to IDs
        siteId: chosenId || null, // active-slate site id
        time: r.Time ?? r["Time ET"] ?? r.Start ?? r.time ?? "",
        gameKey: team && opp ? `${team}@${opp}` : "",
        pairKey: team && opp ? [team, opp].sort().join("@") : "",
        __raw: r,
      };
    });

    const projectedRows = estimateSlateRates(mapped, slate, cfg.slots)
      .filter((r) => r.name && r.pos)
      .map((r) => ({
        ...r,
        baseProj: r.proj,
        hasProjection: true,
        unprojected: false,
      }));

    // Matchup data usually lives in projections/qb_data, while site_ids.json is mostly IDs/salary/pos/team.
    // Use the projected slate pool to fill Opp/Time for unprojected site-list players from the same team.
    const matchupByTeam = new Map();
    for (const r of projectedRows) {
      const tm = normTeam(r.team);
      const op = normTeam(r.opp);
      if (!tm || !op) continue;
      if (!matchupByTeam.has(tm)) {
        matchupByTeam.set(tm, {
          opp: op,
          time: r.time || "",
          gameKey: `${tm}@${op}`,
          pairKey: [tm, op].sort().join("@"),
        });
      }
      if (!matchupByTeam.has(op)) {
        matchupByTeam.set(op, {
          opp: tm,
          time: r.time || "",
          gameKey: `${op}@${tm}`,
          pairKey: [tm, op].sort().join("@"),
        });
      }
    }

    const vegasSourceRows = Array.isArray(vegasData?.rows)
      ? vegasData.rows
      : Array.isArray(vegasData?.players)
      ? vegasData.players
      : Array.isArray(vegasData?.data)
      ? vegasData.data
      : Array.isArray(vegasData)
      ? vegasData
      : [];

    for (const r of vegasSourceRows) {
      const tm = normTeam(r.team ?? r.Team ?? "");
      const op = normTeam(r.opponent ?? r.Opponent ?? r.opp ?? r.Opp ?? "");
      const home = normTeam(r.field ?? r.Field ?? r.home ?? r.Home ?? "");
      if (!tm || !op) continue;

      const t = formatVegasTime(r.gameDate ?? r.game_date ?? "");
      const pairKey = [tm, op].sort().join("@");
      const gameKey = home
        ? `${tm === home ? op : tm}@${home}`
        : pairKey;

      matchupByTeam.set(tm, {
        opp: op,
        time: t,
        gameKey,
        pairKey,
        homeTeam: home,
      });

      matchupByTeam.set(op, {
        opp: tm,
        time: t,
        gameKey,
        pairKey,
        homeTeam: home,
      });
    }

    // Add players that exist in site_ids.json but are missing from projections.json.
    // They start hidden and default-excluded, but can be shown, assigned a custom projection,
    // and manually unchecked from Excl to become usable.
    const seenNames = new Set(projectedRows.map((r) => normName(r.name)));
    const seenActiveIds = new Set(
      projectedRows
        .map((r) => String(r.siteId || "").trim())
        .filter(Boolean)
    );

    const extraRows = [];
    const extraSeen = new Set();

    for (const rec of list) {
      const rawName = String(rec.name ?? rec.player ?? rec.Player ?? rec.Name ?? "").trim();
      if (!rawName) continue;

      let rawPos = normPos(rec.pos ?? rec.Pos ?? rec.POS ?? rec.Position ?? rec.position ?? "");
      let team = normTeam(rec.team ?? rec.Team ?? rec.TEAM ?? rec.team_abbr ?? rec.TeamAbbrev ?? "");
      let opp = normTeam(rec.opp ?? rec.Opp ?? rec.OPP ?? rec.Opponent ?? rec.opponent ?? "");

      if (rawPos === "D") rawPos = "DST";
      if (rawPos === "DEF") rawPos = "DST";

      if (rawPos === "DST" && !team) team = inferTeamFromNameForDST(rawName) || "";

      const inferredMatchup = team ? matchupByTeam.get(team) : null;
      if (!opp && inferredMatchup?.opp) opp = inferredMatchup.opp;

      const mainId = rec.id ?? null;
      const mainSalary = rec.salary ?? null;
      const earlyId = rec.early_id ?? rec.earlyId ?? null;
      const earlySalary = rec.early_salary ?? rec.earlySalary ?? null;
      const lateId = rec.late_id ?? rec.lateId ?? null;
      const lateSalary = rec.late_salary ?? rec.lateSalary ?? null;
      const thuMonId = rec.thu_mon_id ?? null;
      const thuMonSalary = rec.thu_mon_salary ?? null;

      const hasMain = String(mainId ?? "").trim() !== "" && num(mainSalary) > 0;
      const hasEarly = String(earlyId ?? "").trim() !== "" && num(earlySalary) > 0;
      const hasLate = String(lateId ?? "").trim() !== "" && num(lateSalary) > 0;
      const hasThuMon = !!thuMonId && thuMonSalary != null && num(thuMonSalary) > 0;

      let chosenId = null;
      let salary = 0;
      if (slate === "main" && hasMain) {
        chosenId = mainId;
        salary = num(mainSalary);
      } else if (slate === "early" && hasEarly) {
        chosenId = earlyId;
        salary = num(earlySalary);
      } else if (slate === "late" && hasLate) {
        chosenId = lateId;
        salary = num(lateSalary);
      } else if (slate === "thu_mon" && hasThuMon) {
        chosenId = thuMonId;
        salary = num(thuMonSalary);
      }

      if (!chosenId || salary <= 0 || !rawPos) continue;

      const uniqueKey = `${String(chosenId).trim()}|${rawPos}|${normName(rawName)}`;
      if (extraSeen.has(uniqueKey)) continue;
      extraSeen.add(uniqueKey);

      if (seenNames.has(normName(rawName)) || seenActiveIds.has(String(chosenId).trim())) continue;

      const slates = {
        main: hasMain,
        early: hasEarly,
        late: hasLate,
        thu_mon: hasThuMon,
      };

      const time = rec.Time ?? rec["Time ET"] ?? rec.Start ?? rec.time ?? inferredMatchup?.time ?? "";
      const gameKey = team && opp ? `${team}@${opp}` : inferredMatchup?.gameKey || "";
      const pairKey = team && opp ? [team, opp].sort().join("@") : inferredMatchup?.pairKey || "";

      extraRows.push({
        name: rawName,
        pos: rawPos,
        team,
        opp,
        salary,
        proj: 0,
        baseProj: 0,
        val: 0,
        floor: 0,
        ceil: 0,
        pown: 0,
        opt: 0,
        slates,
        siteId: chosenId || null,
        time,
        gameKey,
        pairKey,
        hasProjection: false,
        unprojected: true,
        __raw: rec,
      });
    }

    return [...projectedRows, ...extraRows];
    }, [data, site, cfg, siteIds, slate, vegasData]);

    const vegasRows = useMemo(() => {
  if (!vegasData) return [];

  return Array.isArray(vegasData?.rows)
    ? vegasData.rows
    : Array.isArray(vegasData?.players)
    ? vegasData.players
    : Array.isArray(vegasData?.data)
    ? vegasData.data
    : Array.isArray(vegasData)
    ? vegasData
    : [];
}, [vegasData]);

const teamVegasMap = useMemo(() => {
  const m = new Map();

  for (const r of vegasRows) {
    const team = normTeam(r.team ?? r.Team ?? "");
    const opponent = normTeam(r.opponent ?? r.Opponent ?? r.opp ?? r.Opp ?? "");
    const field = normTeam(r.field ?? r.Field ?? r.home ?? r.Home ?? "");
    if (!team) continue;

    const teamTotalRaw = r.team_total ?? r.teamTotal ?? r["Team Total"];
    const ouRaw = r.over_under ?? r.OU ?? r["O/U"];
    const spreadRaw = r.spread ?? r.Spread;
    const moneylineRaw = r.moneyline ?? r.Moneyline;
    const gameDate = r.gameDate ?? r.game_date ?? "";

    m.set(team, {
      opponent,
      implied:
        teamTotalRaw == null || teamTotalRaw === ""
          ? null
          : num(teamTotalRaw),
      ou:
        ouRaw == null || ouRaw === ""
          ? null
          : num(ouRaw),
      spread:
        spreadRaw == null || spreadRaw === ""
          ? null
          : num(spreadRaw),
      moneyline:
        moneylineRaw == null || moneylineRaw === ""
          ? null
          : num(moneylineRaw),
      isHome: field ? field === team : null,
      field,
      gameDate,
      time: formatVegasTime(gameDate),
    });
  }

  return m;
}, [vegasRows]);

const teamImpliedMap = useMemo(() => {
  const m = new Map();
  for (const [team, vals] of teamVegasMap.entries()) {
    if (Number.isFinite(vals?.implied)) {
      m.set(team, vals.implied);
    }
  }
  return m;
}, [teamVegasMap]);

// ---- Shared active-site/slate filter
// Used by table, game/team chips, bulk actions, and solver

const slateOKRow = (r) => {
  if (!r) return false;

  // Active-site salary must be valid
  // Example: FD salary = 0 => hidden on FD only
  //          DK salary > 0 => still available on DK
  const salary = Number(r.salary) || 0;
  if (salary <= 0) return false;

  // Team must be valid
  const team = String(r.team ?? "").trim().toUpperCase();

  if (
    !team ||
    team === "N/A" ||
    team === "NA" ||
    team === "N.A." ||
    team === "-" ||
    team === "NULL" ||
    team === "NONE"
  ) {
    return false;
  }

  // If no slate info, treat as MAIN only
  if (!r.slates) return false;

  if (slate === "early") return !!r.slates.early;

  if (slate === "late") return !!r.slates.late;

  if (slate === "thu_mon") return !!r.slates.thu_mon;

  return !!r.slates.main;
};

// Rows available on the active slate (for chips + bulk actions)
const rowsForSlate = useMemo(
  () => rows.filter(slateOKRow),
  [rows, slate]
);

const unprojectedNamesForSlate = useMemo(
  () => rowsForSlate.filter((r) => r.unprojected).map((r) => r.name),
  [rowsForSlate]
);

  // When slate changes, clear game selection (team selections are stored per-slate).
  useEffect(() => {
    setSelectedGames(new Set());
  }, [slate, site]);

  const allTeams = useMemo(() => {
    const s = new Set();
    for (const r of rowsForSlate) {
      if (r.team) s.add(r.team);
      if (r.opp) s.add(r.opp);
    }
    return [...s].sort();
  }, [rowsForSlate]);

  // helper to get correct logo path
const logoSrc = (abbr) => `/logos/nfl/${String(abbr || "").toUpperCase()}.png`;

// Unique games built from vegas.json so away/home order is deterministic.
const uniqueGames = useMemo(() => {
  const m = new Map();

  for (const r of rowsForSlate) {
    const team = normTeam(r.team);
    const opp = normTeam(r.opp);
    if (!team || !opp) continue;

    const pairKey = [team, opp].sort().join("@");
    if (m.has(pairKey)) continue;

    const teamVegas = teamVegasMap.get(team) || {};
    const oppVegas = teamVegasMap.get(opp) || {};

    let home = "";
    if (teamVegas.isHome === true) home = team;
    else if (oppVegas.isHome === true) home = opp;
    else if (teamVegas.field === team || teamVegas.field === opp) home = teamVegas.field;
    else if (oppVegas.field === team || oppVegas.field === opp) home = oppVegas.field;

    // Vegas should normally resolve this. The fallback keeps the card usable
    // while preserving a stable order if a matchup is temporarily absent.
    if (!home) home = opp;

    const away = home === team ? opp : team;
    const gameDate = teamVegas.gameDate || oppVegas.gameDate || "";
    const time = teamVegas.time || oppVegas.time || r.time || "";
    const kick = gameDate
      ? vegasKickMinutes(gameDate)
      : (() => {
          const parsed = parseKick(time);
          return parsed >= 0 ? parsed : Number.POSITIVE_INFINITY;
        })();

    m.set(pairKey, {
      gameKey: pairKey,
      pairKey,
      away,
      home,
      teams: [away, home], // away always top, home always bottom
      time,
      kick,
      kickoffStamp: slateKickoffStamp(gameDate),
      gameDate,
    });
  }

  return [...m.values()];
}, [rowsForSlate, teamVegasMap]);

// Sort by full kickoff date first, then time when dates are unavailable.
const sortedGames = useMemo(() => {
  return uniqueGames
    .slice()
    .sort((a, b) => {
      const ad = Number.isFinite(a.kickoffStamp) ? a.kickoffStamp : Number.POSITIVE_INFINITY;
      const bd = Number.isFinite(b.kickoffStamp) ? b.kickoffStamp : Number.POSITIVE_INFINITY;
      if (ad !== bd) return ad - bd;
      const ak = a.kick ?? Number.POSITIVE_INFINITY;
      const bk = b.kick ?? Number.POSITIVE_INFINITY;
      if (ak !== bk) return ak - bk;
      return `${a.away}@${a.home}`.localeCompare(`${b.away}@${b.home}`);
    });
}, [uniqueGames]);

// ==== Bulk exclude helpers ===============================================

// Build quick lookups for bulk operations
const playersByTeam = useMemo(() => {
  const m = new Map();
  for (const r of rowsForSlate) {
    if (!r.team) continue;
    if (!m.has(r.team)) m.set(r.team, new Set());
    m.get(r.team).add(r.name);
  }
  return m;
}, [rowsForSlate]);

const playersByGame = useMemo(() => {
  const m = new Map(); // key: pairKey ("BUF@MIA") -> Set(playerNames)
  for (const r of rowsForSlate) {
    if (!r.pairKey) continue;
    if (!m.has(r.pairKey)) m.set(r.pairKey, new Set());
    m.get(r.pairKey).add(r.name);
  }
  return m;
}, [rowsForSlate]);

const rowByNameForExclusion = useMemo(
  () => new Map(rowsForSlate.map((r) => [r.name, r])),
  [rowsForSlate]
);

const isEffectivelyExcluded = (name) => {
  const r = rowByNameForExclusion.get(name);
  if (r?.unprojected && !unprojectedAllowed.has(name)) return true;
  return excls.has(name);
};

const baseExcludedSet = () => {
  const next = new Set(excls);
  for (const name of unprojectedNamesForSlate) {
    if (!unprojectedAllowed.has(name)) next.add(name);
  }
  return next;
};

// Is an entire team fully excluded right now?
const isTeamFullyExcluded = (team) => {
  const names = playersByTeam.get(team);
  if (!names || names.size === 0) return false;
  for (const n of names) if (!isEffectivelyExcluded(n)) return false;
  return true;
};

// Is an entire game fully excluded right now?
const isGameFullyExcluded = (gameKey) => {
  const names = playersByGame.get(gameKey);
  if (!names || names.size === 0) return false;
  for (const n of names) if (!isEffectivelyExcluded(n)) return false;
  return true;
};

// Toggle bulk team exclude
const toggleExcludeTeam = (team) =>
  setExcls((S) => {
    const names = playersByTeam.get(team) || new Set();
    const next = new Set(S);
    const fullyExcluded = isTeamFullyExcluded(team);
    if (fullyExcluded) {
      for (const n of names) next.delete(n);
    } else {
      for (const n of names) next.add(n);
    }
    return next;
  });

// Toggle bulk game exclude
const toggleExcludeGame = (gameKey) =>
  setExcls((S) => {
    const names = playersByGame.get(gameKey) || new Set();
    const next = new Set(S);
    const fullyExcluded = isGameFullyExcluded(gameKey);
    if (fullyExcluded) {
      for (const n of names) next.delete(n);
    } else {
      for (const n of names) next.add(n);
    }
    return next;
  });

  // ---- Quick clear helpers -----------------------------------------------

  // Clear excludes for all currently displayed chips (i.e., all sortedGames)
  const clearExcludesShown = () => {
    setExcls((S) => {
      const next = new Set(S);
      const gameKeys = sortedGames.map((g) => g.gameKey);

      for (const gk of gameKeys) {
        const names = playersByGame.get(gk) || new Set();
        for (const n of names) next.delete(n);
      }

      return next;
    });
    // Keep unprojected players default-excluded unless you manually allow them again.
    setUnprojectedAllowed(new Set());
  };

  // Clear everything variants
  const clearAllExcludes = () => { setExcls(new Set()); setUnprojectedAllowed(new Set()); };
  const clearAllLocks = () => setLocks(new Set());
  const clearAllBoosts = () => {
  setBoost({});
  setTeamBoost({});
};
  const clearBoostsMinMaxProj = () => {
    setBoost({});
    setTeamBoost({});
    setMinPct({});
    setMaxPct({});
    setCustomProj({});
  };
  const clearAllMinMax = () => {
    setMinPct({});
    setMaxPct({});
  };

  // 🔄 Toggle: Exclude players at the current position filter
  // - posFilter === "ALL"  → acts like "exclude all players" toggle
  // - posFilter === "FLEX" → RB/WR/TE group toggle
  // - otherwise            → specific position (QB/RB/WR/TE/DST)
  const excludeCurrentPosition = () => {
    const isFlex = posFilter === "FLEX";
    const isAll = posFilter === "ALL";

    // Players matching current pos filter (based on full rows, not just visible)
    const playersAtPos = rowsForSlate.filter((r) => {
      if (hideUnprojected && r.unprojected) return false;
      if (isAll) return true;
      if (isFlex) return ["RB", "WR", "TE"].includes(r.pos);
      return r.pos === posFilter;
    });

    const namesAtPos = playersAtPos.map((r) => r.name);
    if (namesAtPos.length === 0) return;

    // Are ALL players at this position already excluded?
    const allExcluded = namesAtPos.every((name) => isEffectivelyExcluded(name));

    if (allExcluded) {
      // Undo: remove just these players from the excluded set
      setExcls((prev) => {
        const next = new Set(prev);
        namesAtPos.forEach((name) => next.delete(name));
        setUnprojectedAllowed((prev) => {
          const allowed = new Set(prev);
          for (const name of namesAtPos) {
            if (rowByNameForExclusion.get(name)?.unprojected) allowed.add(name);
          }
          return allowed;
        });
        return next;
      });
      return;
    }

    // Else → exclude all players at this position
    setExcls((prev) => {
      const next = new Set(prev);
      namesAtPos.forEach((name) => next.add(name));
      setUnprojectedAllowed((prev) => {
        const allowed = new Set(prev);
        for (const name of namesAtPos) allowed.delete(name);
        return allowed;
      });
      return next;
    });
  };

/* ----------------------- order / filter / sort -------------------- */
const [order, setOrder] = useState([]);
const sortRef = useRef({ col: "proj", dir: "desc" });

useEffect(() => {
  const hasProjections = rows.some((row) => Number(row.proj) > 0);

  const initialSort = hasProjections
    ? { col: "proj", dir: "desc" }
    : { col: "salary", dir: "desc" };

  sortRef.current = initialSort;

  const initial = [...rows].sort((a, b) => {
    if (hasProjections) {
      return (
        (Number(b.proj) || 0) - (Number(a.proj) || 0) ||
        (Number(b.salary) || 0) - (Number(a.salary) || 0) ||
        a.name.localeCompare(b.name)
      );
    }

    return (
      (Number(b.salary) || 0) - (Number(a.salary) || 0) ||
      a.name.localeCompare(b.name)
    );
  });

  setOrder(initial.map((row) => row.name));
}, [site, rows]);

  const usagePct = useMemo(() => {
    if (!lineups.length) return {};
    const m = new Map();
    for (const L of lineups) for (const p of L.players) m.set(p, (m.get(p) || 0) + 1);
    const total = Math.max(1, lineups.length);
    const out = {};
    for (const [name, cnt] of m.entries()) out[name] = (cnt / total) * 100;
    return out;
  }, [lineups]);

    const rowsWithBoost = useMemo(() => {
  return rows.map((r) => {
    const playerSteps = Number(boost[r.name] || 0);
    const teamSteps = r.pos !== "DST" ? Number(teamBoost[r.team] || 0) : 0;
    const totalSteps = playerSteps + teamSteps;

    const rawCustom = customProj?.[r.name];
    const hasCustomProj = rawCustom != null && String(rawCustom).trim() !== "";
    const activeProj = hasCustomProj ? Math.max(0, num(rawCustom)) : (r.baseProj ?? r.proj ?? 0);

    const boostedProj =
      (activeProj || 0) * (1 + 0.03 * playerSteps + 0.10 * teamSteps);

    const boostedVal =
      Number.isFinite(boostedProj) && r.salary > 0
        ? (boostedProj / r.salary) * 1000
        : 0;

    return {
      ...r,
      proj: activeProj,
      customProj: rawCustom ?? "",
      hasCustomProj,
      boostedProj,
      boostedVal,
      val: Number.isFinite(activeProj) && r.salary > 0 ? (activeProj / r.salary) * 1000 : 0,
      boostSteps: totalSteps,
      playerBoostSteps: playerSteps,
      teamBoostSteps: teamSteps,
    };
  });
}, [rows, boost, teamBoost, customProj]);

const displayRows = useMemo(() => {
  const posOK = (pos) =>
    posFilter === "ALL"
      ? true
      : posFilter === "FLEX"
      ? ["RB", "WR", "TE"].includes(pos)
      : pos === posFilter;

  const needle = q.trim().toLowerCase();
  const textOK = (r) =>
    !needle ||
    r.name.toLowerCase().includes(needle) ||
    r.team.toLowerCase().includes(needle) ||
    r.opp.toLowerCase().includes(needle) ||
    r.pos.toLowerCase().includes(needle) ||
    String(r.salary).includes(needle);

  const gameOK = (r) => selectedGames.size === 0 || selectedGames.has(r.pairKey);
  const teamOK = (r) => true; // still ignore team chip filtering in the table

  // Slate filter — strict: handled by slateOKRow (shared)

  const byName = new Map(rowsWithBoost.map((r) => [r.name, r]));
  const ordered = order.map((n) => byName.get(n)).filter(Boolean);
  const others = rowsWithBoost.filter((r) => !order.includes(r.name));
  const base = [...ordered, ...others];

  return base.filter(
    (r) =>
      (!hideUnprojected || !r.unprojected) &&
      (!hideExcluded || !excls.has(r.name)) &&
      posOK(r.pos) &&
      textOK(r) &&
      gameOK(r) &&
      teamOK(r) &&
      slateOKRow(r)
  );
}, [rowsWithBoost, order, q, posFilter, selectedGames, selectedTeams, slate, hideUnprojected, hideExcluded, excls]);

  const sortable = new Set([
    "pos",
    "team",
    "opp",
    "salary",
    "time",
    "proj",
    "val",
    "floor",
    "ceil",
    "pown",
    "opt",
    "usage",
  ]);
  const setSort = (col) => {
    if (!sortable.has(col)) return;
    const dir =
      sortRef.current.col === col ? (sortRef.current.dir === "asc" ? "desc" : "asc") : "desc";
    sortRef.current = { col, dir };
    const mult = dir === "asc" ? 1 : -1;
    const sorted = [...displayRows].sort((a, b) => {
      if (["pos", "team", "opp", "time"].includes(col)) {
        const va = (a[col] || "").toString();
        const vb = (b[col] || "").toString();
        if (va < vb) return -1 * mult;
        if (va > vb) return 1 * mult;
        return a.name.localeCompare(b.name) * mult;
      }
      const va =
        col === "pown" || col === "opt" || col === "usage"
          ? ((col === "usage" ? usagePct[a.name] : a[col]) || 0) * 100
          : a[col] ?? 0;
      const vb =
        col === "pown" || col === "opt" || col === "usage"
          ? ((col === "usage" ? usagePct[b.name] : b[col]) || 0) * 100
          : b[col] ?? 0;
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return a.name.localeCompare(b.name) * mult;
    });
    setOrder(sorted.map((r) => r.name));
  };
  const sortArrow = (key) =>
    sortRef.current.col === key ? (sortRef.current.dir === "asc" ? " ▲" : " ▼") : "";

  
  const rowsByNameParent = useMemo(() => new Map(rowsWithBoost.map((r) => [r.name, r])), [rowsWithBoost]);

const [lineupSort, setLineupSort] = useState({ col: "total", dir: "desc" });
const sortedLineups = useMemo(() => {
  const mult = lineupSort.dir === "asc" ? 1 : -1;
  const avgPct = (L, key) => {
    if (!L?.players?.length) return 0;
    let s = 0;
    for (const n of L.players) {
      const r = rowsByNameParent.get(n);
      if (!r) continue;
      s += (key === "pown" ? (r.pown || 0) : (r.opt || 0)) * 100;
    }
    return s / L.players.length;
  };
  const getter = (L) => {
    if (lineupSort.col === "total") {
      return (L.players || []).reduce((s, n) => s + (Number(rowsByNameParent.get(n)?.proj) || 0), 0) || Number(L.total) || 0;
    }
    if (lineupSort.col === "salary") return Number(L.salary) || 0;
    if (lineupSort.col === "pown") return avgPct(L, "pown");
    if (lineupSort.col === "opt") return avgPct(L, "opt");
    return 0;
  };
  return [...lineups].sort((a, b) => (getter(a) - getter(b)) * mult);
}, [lineups, lineupSort, rowsByNameParent]);

const selectedPlayerPairings = useMemo(() => {
  if (!selectedExposurePlayer || !lineups.length) return [];

  const counts = new Map();
  let lineupCount = 0;

  for (const L of lineups) {
    const players = Array.isArray(L?.players) ? L.players : [];
    if (!players.includes(selectedExposurePlayer)) continue;

    lineupCount += 1;

    for (const name of players) {
      if (name === selectedExposurePlayer) continue;
      counts.set(name, (counts.get(name) || 0) + 1);
    }
  }

  const meta = new Map(rowsWithBoost.map((r) => [r.name, r]));

  return [...counts.entries()]
    .map(([name, count]) => ({
      name,
      count,
      pct: lineupCount > 0 ? (count / lineupCount) * 100 : 0,
      pos: meta.get(name)?.pos || "?",
      team: meta.get(name)?.team || "",
    }))
    .sort(
      (a, b) =>
        b.count - a.count ||
        b.pct - a.pct ||
        a.name.localeCompare(b.name)
    );
}, [selectedExposurePlayer, lineups, rowsWithBoost]);

const buildAnalysis = useMemo(() => {
  if (!lineups.length) {
    return {
      avgSalaryUsed: 0,
      avgSalaryRemaining: 0,
      avgProjection: 0,
      avgLineupPown: 0,
      avgGeoMeanPown: 0,
      avgQbStackSize: 0,
      avgBringBackSize: 0,
      topPlayers: [],
      topPairs: [],
      topTriples: [],
      topQbTeams: [],
      topGameCombos: [],
    };
  }

  const playerCounts = new Map();
  const pairCounts = new Map();
  const tripleCounts = new Map();
  const qbTeamCounts = new Map();
  const gameComboCounts = new Map();

  let totalSalary = 0;
  let totalProjection = 0;
  let totalLineupPown = 0;
  let totalGeoMeanPown = 0;
  let totalQbStackSize = 0;
  let totalBringBackSize = 0;

  const makeCombos = (arr, size) => {
    const out = [];
    const n = arr.length;

    if (size === 2) {
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          out.push([arr[i], arr[j]]);
        }
      }
    } else if (size === 3) {
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          for (let k = j + 1; k < n; k++) {
            out.push([arr[i], arr[j], arr[k]]);
          }
        }
      }
    }

    return out;
  };

  const isStackEligible = (r) => {
    if (!r || r.pos === "DST" || r.pos === "QB") return false;
    if (r.pos === "WR" || r.pos === "TE") return true;
    if (stackAllowRB && r.pos === "RB") return true;
    return false;
  };

  for (const L of lineups) {
    const chosen = (L.players || [])
      .map((n) => rowsByNameParent.get(n))
      .filter(Boolean);

    const names = chosen
      .filter((r) => r && r.pos !== "DST")
      .map((r) => r.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    const lineupPown = chosen.reduce((acc, r) => acc + ((r.pown || 0) * 100), 0);
    const geoPown = geoMeanPct(chosen, (r) => ((r.pown || 0) * 100));

    totalSalary += Number(L.salary) || 0;
    totalProjection += chosen.reduce((s, r) => s + (Number(r?.proj) || 0), 0) || Number(L.total) || 0;
    totalLineupPown += lineupPown;
    totalGeoMeanPown += geoPown;

    for (const name of names) {
      playerCounts.set(name, (playerCounts.get(name) || 0) + 1);
    }

    for (const combo of makeCombos(names, 2)) {
      const key = combo.join(" | ");
      pairCounts.set(key, (pairCounts.get(key) || 0) + 1);
    }

    for (const combo of makeCombos(names, 3)) {
      const key = combo.join(" | ");
      tripleCounts.set(key, (tripleCounts.get(key) || 0) + 1);
    }

    const qb = chosen.find((r) => r.pos === "QB");
    if (qb?.team) {
      qbTeamCounts.set(qb.team, (qbTeamCounts.get(qb.team) || 0) + 1);

      const stackPartners = chosen.filter(
        (r) => r !== qb && r.team === qb.team && isStackEligible(r)
      );
      const bringBacks = chosen.filter(
        (r) => r !== qb && qb.opp && r.team === qb.opp && isBringBackEligible(r)
      );

      totalQbStackSize += stackPartners.length;
      totalBringBackSize += bringBacks.length;

      const gameLabel = qb.opp
        ? [qb.team, qb.opp].sort().join(" • ")
        : `${qb.team} • —`;

      gameComboCounts.set(gameLabel, (gameComboCounts.get(gameLabel) || 0) + 1);
    }
  }

  const lineupCount = Math.max(1, lineups.length);

  return {
    avgSalaryUsed: totalSalary / lineupCount,
    avgSalaryRemaining: cfg.cap - totalSalary / lineupCount,
    avgProjection: totalProjection / lineupCount,
    avgLineupPown: totalLineupPown / lineupCount,
    avgGeoMeanPown: totalGeoMeanPown / lineupCount,
    avgQbStackSize: totalQbStackSize / lineupCount,
    avgBringBackSize: totalBringBackSize / lineupCount,

    topPlayers: [...playerCounts.entries()]
      .map(([player, count]) => ({
        player,
        count,
        pct: (count / lineupCount) * 100,
      }))
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.player.localeCompare(b.player))
      .slice(0, 12),

    topPairs: [...pairCounts.entries()]
      .map(([key, count]) => {
        const players = key.split(" | ");
        return {
          players,
          label: players.join(" • "),
          count,
          pct: (count / lineupCount) * 100,
        };
      })
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.label.localeCompare(b.label))
      .slice(0, 12),

    topTriples: [...tripleCounts.entries()]
      .map(([key, count]) => {
        const players = key.split(" | ");
        return {
          players,
          label: players.join(" • "),
          count,
          pct: (count / lineupCount) * 100,
        };
      })
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.label.localeCompare(b.label))
      .slice(0, 12),

    topQbTeams: [...qbTeamCounts.entries()]
      .map(([team, count]) => ({
        team,
        count,
        pct: (count / lineupCount) * 100,
      }))
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.team.localeCompare(b.team))
      .slice(0, 12),

    topGameCombos: [...gameComboCounts.entries()]
      .map(([label, count]) => ({
        label,
        count,
        pct: (count / lineupCount) * 100,
      }))
      .sort((a, b) => b.count - a.count || b.pct - a.pct || a.label.localeCompare(b.label))
      .slice(0, 12),
  };
}, [lineups, cfg.cap, rowsByNameParent, stackAllowRB]);

const [cardsSortBy, setCardsSortBy] = useStickyState(`nflOpt.${site}.cardsSortBy`, "proj");
const [cardsSortDir, setCardsSortDir] = useStickyState(`nflOpt.${site}.cardsSortDir`, "desc");

const lineupsForCards = useMemo(() => {
  const dir = cardsSortDir === "asc" ? 1 : -1;

  const getKey = (L) => {
    const ordered = orderPlayersForSite(L.players || [], rowsByNameParent);

    const totalProj = ordered.reduce((s, r) => s + (Number(r?.proj) || 0), 0);
    const totalPown = ordered.reduce((s, r) => s + ((Number(r?.pown) || 0) * 100), 0);
    const totalOpt = ordered.reduce((s, r) => s + ((Number(r?.opt) || 0) * 100), 0);
    const geoMeanPown = geoMeanPct(ordered, (r) => ((Number(r?.pown) || 0) * 100));

    if (cardsSortBy === "salary") return Number(L.salary) || 0;
    if (cardsSortBy === "pown") return totalPown || 0;
    if (cardsSortBy === "geo") return geoMeanPown || 0;
    if (cardsSortBy === "opt") return totalOpt || 0;

    return totalProj || Number(L.total) || 0;
  };

  return [...(lineups || [])].sort((a, b) => {
    const ka = getKey(a);
    const kb = getKey(b);
    if (ka < kb) return -1 * dir;
    if (ka > kb) return 1 * dir;
    return 0;
  });
}, [lineups, cardsSortBy, cardsSortDir, rowsByNameParent]);

/* ----------------------------- actions ---------------------------- */
  const bumpBoost = (name, step) =>
    setBoost((m) => ({ ...m, [name]: clamp((m[name] || 0) + step, -6, 6) }));
  const bumpTeamBoost = (team, step) =>
  setTeamBoost((m) => ({
    ...m,
    [team]: clamp((m[team] || 0) + step, -6, 6),
  }));
  const toggleLock = (name) =>
    setLocks((s) => {
      const n = new Set(s);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });
  const toggleExcl = (rowOrName) => {
    const name = typeof rowOrName === "string" ? rowOrName : rowOrName?.name;
    const row = typeof rowOrName === "string" ? rowByNameForExclusion.get(name) : rowOrName;
    if (!name) return;

    // Unprojected rows are excluded by default. Checking/unchecking them controls
    // whether they are in the manual allow-list.
    if (row?.unprojected) {
      const currentlyExcluded = isEffectivelyExcluded(name);
      if (currentlyExcluded) {
        setExcls((s) => {
          const n = new Set(s);
          n.delete(name);
          return n;
        });
        setUnprojectedAllowed((s) => {
          const n = new Set(s);
          n.add(name);
          return n;
        });
      } else {
        setUnprojectedAllowed((s) => {
          const n = new Set(s);
          n.delete(name);
          return n;
        });
        setExcls((s) => {
          const n = new Set(s);
          n.add(name);
          return n;
        });
      }
      return;
    }

    setExcls((s) => {
      const n = new Set(s);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });
  };

const resetConstraints = () => {
  // --- player-level clears ---
  clearAllLocks();
  clearAllExcludes();
  clearAllBoosts();
  clearAllMinMax();
  setCustomProj({});
  setUnprojectedAllowed(new Set());
  setHideUnprojected(true);
  setHideExcluded(false);
  setOptBy("proj");

  // --- groups / stacks / teams ---
  setGroups([]);
  setPairCaps([]);
  setTeamStacks([]);
  setIfThenRules([]);
  setAdvancedOpen({ stacks: false, conditional: false, groups: false, pairCaps: false });
  setTeamBoost({});

    // ✅ NEW: selected teams / stack origin
  setSelectedTeams(new Set());
  setRestrictStacksToTeams(false);
  setShowBuildAnalysis(false);
  // --- optimizer constraints ---
  setRandomness(0);
  setNumLineups(20);
  setGlobalMax(100);
  setMaxPownCap("");
  setMinValue("");
  setMaxLineupPown("");
  setMaxGeoMeanPown("");
  setQbStackMin(0);
  setBringbackMin(0);
  setMaxFromTeam("");

  // --- builds + results ---
  setBuilds([]);
  setActiveBuildId(null);
  setLineups([]);
  setStopInfo(null);
  setProgressActual(0);
  setProgressUI(0);
  setIsOptimizing(false);
};

  const resetExposures = () => {
  // player-level exposure controls
  setMinPct({});
  setMaxPct({});
};


/* --------------------------- optimize (SSE) ------------------------ */
async function optimize() {
  if (!rowsWithBoost.length) return;

  // reset UI state
  setLineups([]);
  setStopInfo(null);
  setProgressActual(0);
  setProgressUI(0);
  setIsOptimizing(true);
  setSelectedExposurePlayer(null);

  // Respect TE-in-FLEX toggle
  const slotsForSolve = cfg.slots.map((s) =>
    s.name === "FLEX" ? { ...s, eligible: allowTeInFlex ? ["RB", "WR", "TE"] : ["RB", "WR"] } : s
  );

  const N = Math.max(1, Number(numLineups) || 1);
  const capVal = Math.min(cfg.cap, Number(maxSalary) || cfg.cap);

  // per-player auto-exclude by pOWN%
  const cap = clamp(Number(maxPownCap), 0, 100);
  const autoExcludesByPown =
    !slateHasRates(slate) || String(maxPownCap).trim() === ""
      ? []
      : rowsWithBoost.filter((r) => ((r.pown || 0) * 100) > cap).map((r) => r.name);

  // Auto-exclude players below the entered points-per-$1,000 value.
  // The threshold itself is inclusive: entering 1 keeps players at exactly 1.0x.
  const minValueThreshold = Number(minValue);
  const autoExcludesByValue =
    String(minValue).trim() === "" || !Number.isFinite(minValueThreshold)
      ? []
      : rowsWithBoost
          .filter((r) => Number(r.boostedVal ?? r.val ?? 0) < minValueThreshold)
          .map((r) => r.name);

  // If the toggle is ON and you have selected teams, exclude QBs from all other teams.
  const qbExclByTeam =
    restrictStacksToTeams && selectedTeams && selectedTeams.size > 0
      ? rowsWithBoost.filter((r) => r.pos === "QB" && !selectedTeams.has(r.team)).map((r) => r.name)
      : [];

  // merge with existing excludes; keep unique
  const mergedExcludes = Array.from(new Set([
    ...Array.from(baseExcludedSet()),
    ...autoExcludesByPown,
    ...autoExcludesByValue,
    ...qbExclByTeam,
  ]));

  // lineup-level pOWN% handling
  const rowsByName = new Map(rows.map((r) => [r.name, r]));

  const lineupPownPct = (names) =>
    names.reduce((s, n) => s + (((rowsByName.get(n)?.pown) || 0) * 100), 0);

  const lineupGeoMeanPownPct = (names) =>
    geoMeanPct(
      (names || []).map((n) => rowsByName.get(n)).filter(Boolean),
      (r) => ((r?.pown) || 0) * 100
    );

  const lineupCap = !slateHasRates(slate) || String(maxLineupPown).trim() === ""
    ? null
    : clamp(Number(maxLineupPown) || 0, 0, 1000);

  const geoMeanCap = !slateHasRates(slate) || String(maxGeoMeanPown).trim() === ""
    ? null
    : clamp(Number(maxGeoMeanPown) || 0, 0, 100);

  // Only use players from the active slate for solving
  const rowsForSolve = rowsWithBoost.filter(slateOKRow);

  // Base payload used for all passes
  const basePayload = {
    site,
    slots: slotsForSolve,
    players: rowsForSolve.map((r) => ({
      name: r.name,
      pos: r.pos,
      team: r.team,
      opp: r.opp,
      salary: Math.round(r.salary || 0),
      proj: r.proj || 0,
      floor: r.floor || 0,
      ceil: r.ceil || 0,
      pown: r.pown || 0,
      opt: r.opt || 0,
    })),
    cap: capVal,
    objective: !slateHasRates(slate) && ["pown", "opt"].includes(optBy) ? "proj" : optBy,
    locks: Array.from(locks),
    excludes: mergedExcludes,
    boosts: Object.fromEntries(
  rowsWithBoost.map((r) => {
    const playerSteps = Number(boost[r.name] || 0);
    const teamSteps = r.pos !== "DST" ? Number(teamBoost[r.team] || 0) : 0;
    return [r.name, playerSteps + teamSteps];
  })
),
    randomness: clamp(Number(randomness) || 0, 0, 100),
    global_max_pct: clamp(Number(globalMax) || 100, 0, 100),
    min_pct: Object.fromEntries(
      Object.entries(minPct).map(([k, v]) => [
        k,
        clamp(Number(v) || 0, 0, 100),
      ])
    ),
    max_pct: Object.fromEntries(
      Object.entries(maxPct).map(([k, v]) => [
        k,
        clamp(Number(v) || 100, 0, 100),
      ])
    ),
    min_diff: 1,
    time_limit_ms: 1500, // fast first pass
    qb_stack_min: Math.max(0, Number(qbStackMin) || 0),
    stack_allow_rb: !!stackAllowRB,
    bringback_min: Math.max(0, Number(bringbackMin) || 0),
    max_from_team:
      String(maxFromTeam).trim() === ""
        ? null
        : Math.max(1, Number(maxFromTeam) || 1),
    avoid_rb_vs_opp_dst: !!avoidRbVsOppDst,
    avoid_offense_vs_opp_dst: !!avoidOffenseVsOppDst,
    groups: (groups || []).map((g) => ({
      mode: g.mode || "at_most",
      count: Math.max(0, Number(g.count) || 0),
      players: Array.isArray(g.players) ? g.players : [],
    })),
    pair_caps: (pairCaps || [])
      .map((p) => {
        const players = Array.isArray(p.players)
          ? p.players.filter(Boolean).slice(0, 2)
          : [];
        return {
          players: [...players].sort((a, b) => a.localeCompare(b)),
          max_pct: clamp(Number(p.maxPct) || 0, 0, 100),
        };
      })
      .filter((p) => p.players.length === 2),
    team_stack_rules: (teamStacks || []).map((t) => ({
      team: t.team,
      ...teamStackPercentageFields(t),
      qb_stack_min:
        t.qb_stack_min == null || String(t.qb_stack_min).trim() === ""
          ? undefined
          : Math.max(0, Number(t.qb_stack_min) || 0),
      bringback_min:
        t.bringback_min == null || String(t.bringback_min).trim() === ""
          ? undefined
          : Math.max(0, Number(t.bringback_min) || 0),
      allow_rb_in_stack: !!t.allow_rb_in_stack,
      bringback_teams: Array.isArray(t.bringback_teams)
        ? t.bringback_teams
        : undefined,
      max_from_team: (() => {
        const raw = t.max_from_team;
        if (raw == null || String(raw).trim() === "") return undefined;
        const n = Number(raw);
        return Number.isFinite(n) ? Math.max(1, n) : undefined;
      })(),
    })),
    if_then_rules: (ifThenRules || [])
      .filter((r) => {
        const hasIf = r.if_type === "players" ? (r.if_players || []).length : (r.if_pos || []).length;
        const hasThen = r.then_type === "players" ? (r.then_players || []).length : (r.then_pos || []).length;
        return hasIf && hasThen;
      })
      .map((r) => ({
      if_mode: r.if_mode || "at_least",
      if_count: Math.max(0, Number(r.if_count) || 0),
      if_pos: r.if_type === "players" ? [] : (r.if_pos || []),
      if_players: r.if_type === "players" ? (r.if_players || []) : [],
      if_scope: r.if_scope || "each_team",
      if_team_exact: r.if_scope === "exact_team" ? (r.if_team_exact || null) : null,
      then_mode: r.then_mode || "at_least",
      then_count: Math.max(0, Number(r.then_count) || 0),
      then_pos: r.then_type === "players" ? [] : (r.then_pos || []),
      then_players: r.then_type === "players" ? (r.then_players || []) : [],
      then_scope: r.then_scope || "same_team",
      })),
    max_lineup_pown_pct: lineupCap == null ? null : lineupCap,
    geo_mean_pown_max: geoMeanCap == null ? null : geoMeanCap,
  };

  const out = [];

  // helper: one streaming run
  const runOnce = async (pl) => {
    await solveStreamNFL(
      pl,
      (evt) => {
        const names = evt.drivers || evt.players || [];

        if (lineupCap != null && lineupPownPct(names) > lineupCap) return;
        if (geoMeanCap != null && lineupGeoMeanPownPct(names) > geoMeanCap) return;

        const L = { players: names, salary: evt.salary, total: evt.total };
        out.push(L);
        setLineups((prev) => [...prev, L]);
        setProgressActual(out.length);
      },
      (done) => {
        if (done?.reason) setStopInfo(done);
      }
    );
  };

  try {
    // ---------- PASS 1: quick ----------
    await runOnce({ ...basePayload, n: N });

    // ---------- PASS 2 (adaptive): only if needed ----------
    if (out.length < N) {
      const need = N - out.length;
      const SECOND_PASS_TIME_MS = Math.max(5000, basePayload.time_limit_ms || 0);
      const SECOND_PASS_RANDOMNESS = Math.max(15, basePayload.randomness || 0);

      await runOnce({
        ...basePayload,
        n: need,
        time_limit_ms: SECOND_PASS_TIME_MS,
        randomness: SECOND_PASS_RANDOMNESS,
      });
    }

    // Finalize UI
    if (out.length < N && (lineupCap != null || geoMeanCap != null)) {
      const reasons = [];
      if (lineupCap != null) reasons.push(`max lineup pOWN% (${lineupCap}%)`);
      if (geoMeanCap != null) reasons.push(`max Geo Mean pOWN% (${geoMeanCap}%)`);

      setStopInfo({
        produced: out.length,
        requested: N,
        reason: "ownership_cap",
        detail: `Some lineups exceeded ${reasons.join(" and ")} and were filtered out.`,
      });
    }
    setProgressActual(out.length || N);
    setProgressUI(out.length || N);
    setIsOptimizing(false);
    clearInterval(tickRef.current);

    saveBuild(nextBuildNameForSite(site), out);
  } catch (e) {
    // Fallback non-streaming endpoint
    const res = await fetch(`${API_BASE}/solve_nfl`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...basePayload, n: N }),
    });
    if (!res.ok) {
      alert(`Solve failed: ${await res.text()}`);
      setIsOptimizing(false);
      clearInterval(tickRef.current);
      return;
    }
    const j = await res.json();
    const raw = (j.lineups || []).map((L) => ({ players: L.drivers, salary: L.salary, total: L.total })) || [];
    const out2 = raw.filter((L) => {
  if (lineupCap != null && lineupPownPct(L.players) > lineupCap) return false;
  if (geoMeanCap != null && lineupGeoMeanPownPct(L.players) > geoMeanCap) return false;
  return true;
});

    setLineups(out2);
    setProgressActual(out2.length);
    setProgressUI(out2.length);
    setIsOptimizing(false);
    clearInterval(tickRef.current);
    if (out2.length < N) {
      setStopInfo({
        produced: out2.length,
        requested: N,
        reason: "ownership_cap",
      });
    }
    saveBuild(nextBuildNameForSite(site), out2);
  }
}


  /* -------------------------- builds (per site) ---------------------- */
  function nextBuildNameForSite(site) {
    try {
      const raw = localStorage.getItem(`nflOpt.${site}.builds`);
      const arr = raw ? JSON.parse(raw) : [];
      const nums = arr
        .map((b) => (b?.name ? String(b.name).match(/^Build\s+(\d+)$/i) : null))
        .filter(Boolean)
        .map((m) => Number(m[1]))
        .filter((n) => Number.isFinite(n));
      const next = nums.length ? Math.max(...nums) + 1 : 1;
      return `Build ${next}`;
    } catch {
      return "Build 1";
    }
  }
  function saveBuild(name, data) {
    const id = Date.now();
    const rec = {
      id,
      name,
      site,
      ts: new Date().toISOString(),
      settings: {
        site,
        optBy,
        numLineups: Math.max(1, Number(numLineups) || 1),
        cap: Math.min(cfg.cap, Number(maxSalary) || cfg.cap),
        globalMax,
        randomness,
        qbStackMin,
        bringbackMin,
        stackAllowRB,
        avoidRbVsOppDst,
        avoidOffenseVsOppDst,
        maxFromTeam,
        locks: [...locks],
        excls: [...excls],
        minPct,
        maxPct,
        boost,
        groups,
        pairCaps,
        teamStacks,
        customProj,
        hideUnprojected,
        unprojectedAllowed: [...unprojectedAllowed],
      },
      lineups: data,
    };
    const next = [...builds, rec];
    setBuilds(next);
    setActiveBuildId(id);
  }
  function loadBuild(id) {
    const b = builds.find((x) => x.id === id);
    if (!b) return;
    setActiveBuildId(id);
    setLineups(b.lineups || []);
    setSelectedExposurePlayer(null);
    setProgressActual((b.lineups || []).length);
    setProgressUI((b.lineups || []).length);
  }
  function renameBuild(id, newName) {
    setBuilds((B) => B.map((b) => (b.id === id ? { ...b, name: newName || b.name } : b)));
  }
  function deleteBuild(id) {
    setBuilds((B) => B.filter((b) => b.id !== id));
    if (activeBuildId === id) {
      setActiveBuildId(null);
      setLineups([]);
      setProgressActual(0);
      setProgressUI(0);
    }
  }
  const clearBuilds = () => {
    setBuilds([]);
    setActiveBuildId(null);
    setLineups([]);
    setProgressActual(0);
    setProgressUI(0);
  };

  /* ------------------------------- UI -------------------------------- */
  const metricLabel =
    optBy === "proj"
      ? "Proj"
      : optBy === "floor"
      ? "Floor"
      : optBy === "ceil"
      ? "Ceiling"
      : optBy === "pown"
      ? "pOWN%"
      : "Opt%";

  const cell = "px-2 py-0.5 text-center align-middle leading-tight";
  const header = "px-2 py-0.5 font-semibold text-center select-none leading-tight text-xs";
  const textSz = "text-[12px] leading-tight";

  const TABLE_COLS = [
    { key: "lock", label: "Lock" },
    { key: "excl", label: "Excl" },
    { key: "pos", label: "Pos", sortable: true },
    { key: "boosts", label: "Boosts" },
    { key: "name", label: "Player" },
    { key: "team", label: "Tm", sortable: true },
    { key: "opp", label: "Opp", sortable: true },
    { key: "salary", label: "Salary", sortable: true },
    { key: "time", label: "Time", sortable: true },
    { key: "proj", label: "Proj", sortable: true },
    { key: "val", label: "Val", sortable: true },
    { key: "floor", label: "Floor", sortable: true },
    { key: "ceil", label: "Ceiling", sortable: true },
    { key: "pown", label: "pOWN%", sortable: true },
    { key: "opt", label: "Opt%", sortable: true },
    { key: "min", label: "Min%" },
    { key: "max", label: "Max%" },
    { key: "usage", label: "Usage%", sortable: true },
  ];

  const allPlayerNames = useMemo(() => rowsForSlate.map((r) => r.name), [rowsForSlate]);
  const slateDetails = useMemo(() => buildSlateDetails(siteIds, vegasData, site), [siteIds, vegasData, site]);
  const stackActiveCount = [
    Number(qbStackMin) > 0,
    Number(bringbackMin) > 0,
    !allowTeInFlex,
    stackAllowRB,
    !avoidRbVsOppDst,
    avoidOffenseVsOppDst,
    String(maxFromTeam).trim() !== "",
    restrictStacksToTeams,
    selectedTeams.size > 0,
    teamStacks.length > 0,
  ].filter(Boolean).length;

  return (
    <div className={`${T.page} px-4 md:px-6 py-5`}>
      {/* header row + theme toggle */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3 lg:flex-nowrap">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1
            className={`whitespace-nowrap text-2xl md:text-3xl font-extrabold ${T.headerText}`}
          >
            NFL — Optimizer
          </h1>
          <div className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs ${T.subtext}`}>
            <span className="italic">{`Projections updated: ${fmtUpdated(projectionsUpdatedAt)}`}</span>
            <span className="italic">{`Ownership updated: ${fmtUpdated(ownershipUpdatedAt)}`}</span>
            <span>{`${(rows || []).length.toLocaleString()} rows`}</span>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <div
            className={
              "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
              T.pill
            }
          >
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={
                "px-3 py-1 rounded-full font-medium transition " +
                (isDark ? T.pillDarkBtnActive : T.pillDarkBtnIdle)
              }
            >
              Dark
            </button>
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={
                "px-3 py-1 rounded-full font-medium transition " +
                (!isDark ? T.pillLightBtnActive : T.pillLightBtnIdle)
              }
            >
              Light
            </button>
          </div>
  <div className="flex shrink-0 gap-2 items-center">
    <span className="text-xs text-slate-400">
      Click here if new slate →
    </span>

    <button
      className="h-8 px-4 rounded-md bg-red-600 text-white hover:bg-red-700 active:bg-red-800"
      onClick={resetConstraints}
    >
      Reset
    </button>
  </div>
        </div>
      </div>



      <section aria-label="Slate and site" className={`${T.card} ${cls.card} mb-3 p-3`}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4 lg:flex-nowrap">
{/* Optimizer / Late Swap */}
<div className={`inline-flex shrink-0 items-center gap-x-6 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
  <button
    type="button"
    onClick={() => setActiveTab("optimizer")}
    className={`relative pb-2 text-sm font-semibold transition ${
      activeTab === "optimizer"
        ? isDark ? "text-white" : "text-slate-900"
        : isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"
    }`}
  >
    Optimizer

    {activeTab === "optimizer" && (
      <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />
    )}
  </button>

  <button
    type="button"
    onClick={() => setActiveTab("lateSwap")}
    className={`relative pb-2 text-sm font-semibold transition ${
      activeTab === "lateSwap"
        ? isDark ? "text-white" : "text-slate-900"
        : isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"
    }`}
  >
    Late Swap

    {activeTab === "lateSwap" && (
      <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />
    )}
  </button>
</div>

      {/* Site selector */}
      <div className={`inline-flex shrink-0 items-center gap-x-6 border-b ${
        isDark ? "border-slate-800" : "border-slate-200"
      }`}>
        {["dk", "fd"].map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={site === s}
            onClick={() => setSite(s)}
            className={`relative pb-2 text-sm font-semibold inline-flex items-center gap-2 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 ${
              site === s
                ? isDark ? "text-white" : "text-slate-900"
                : isDark
                  ? "text-slate-500 hover:text-slate-300"
                  : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <img src={SITES[s].logo} alt="" className="w-4 h-4" />
            <span>{SITES[s].label}</span>
            {site === s && (
              <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />
            )}
          </button>
        ))}
      </div>
    {/* Slate selector */}
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2 sm:flex-nowrap">
      <label htmlFor="nfl-slate-select" className={`shrink-0 text-xs font-medium ${T.subtext}`}>Slate:</label>
      <select
        id="nfl-slate-select"
        value={selectedSlate}
        aria-busy={isSlatePending}
        onChange={(e) => selectSlate(e.target.value)}
        className={`${T.input} h-10 min-w-0 max-w-full flex-1 sm:max-w-md cursor-pointer rounded-md px-3 py-1 text-sm leading-normal transition-colors hover:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400`}
      >
        {[
          { key: "main", label: "Main" },
          { key: "early", label: "Early" },
          { key: "late", label: "Late" },
          { key: "thu_mon", label: "Thu–Mon" },
        ].map((sl) => {
          const detail = slateDetails[sl.key];
          return (
            <option key={sl.key} value={sl.key}>
              {sl.label}{detail ? ` · ${detail.games} games${detail.kickoff ? ` · ${detail.kickoff}` : ""}` : ""}
            </option>
          );
        })}
      </select>
      {["early", "late"].includes(slate) && (
        <span className={`text-[11px] ${T.subtext}`} title="Ownership and optimal rates are estimates rescaled from Main using inclusion odds by position, including FLEX. They are calculated before your pool filters and exclusions.">
          pOWN / Opt: estimated from Main
        </span>
      )}
    </div>
        </div>
      </section>

{/* controls */}
<div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end mb-2">
  {/* Top controls row with subtle border */}
  <div className="md:col-span-12">
    <div className={`${T.card} ${cls.card} p-3`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">

        <div className="md:col-span-2">
          <label className="block text-[11px] text-slate-400 mb-1">Optimize by</label>
          <select
            className={`${cls.input} ${T.input} w-full`}
            value={optBy}
            onChange={(e) => setOptBy(e.target.value)}
          >
            <option value="proj">Projection</option>
            <option value="floor">Floor</option>
            <option value="ceil">Ceiling</option>
            <option disabled={!slateHasRates(slate)} value="pown">pOWN%</option>
            <option disabled={!slateHasRates(slate)} value="opt">Opt%</option>
          </select>
        </div>

        <div className="md:col-span-1">
          <label className="block text-[11px] text-slate-400 mb-1">Lineups</label>
          <input
            className={`${cls.input} ${T.input} w-full`}
            value={numLineups}
            onChange={(e) => setNumLineups(e.target.value)}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-[11px] text-slate-400 mb-1">Max salary</label>
          <input
            className={`${cls.input} ${T.input} w-full`}
            value={maxSalary}
            onChange={(e) => setMaxSalary(e.target.value)}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-[11px] text-slate-400 mb-1">Global Max %</label>
          <input
            className={`${cls.input} ${T.input} w-full`}
            value={globalMax}
            onChange={(e) => setGlobalMax(e.target.value)}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-[11px] text-slate-400 mb-1">Randomness %</label>
          <input
            className={`${cls.input} ${T.input} w-full`}
            value={randomness}
            onChange={(e) => setRandomness(e.target.value)}
          />
        </div>

        {/* per-player auto-exclude */}
        <div className="md:col-span-2">
          <label className="block text-[11px] text-slate-400 mb-1">
            Max player pOWN% (auto-exclude &gt;)
          </label>
          <input
            className={`${cls.input} ${T.input} w-full`}
            placeholder="—"
            disabled={!slateHasRates(slate)}
            value={maxPownCap}
            onChange={(e) => setMaxPownCap(e.target.value)}
          />
        </div>

        {/* lineup-level cap */}
        <div className="md:col-span-2">
          <label className="block text-[11px] text-slate-400 mb-1">
            Max lineup pOWN% (sum)
          </label>
          <input
            className={`${cls.input} ${T.input} w-full`}
            placeholder="—"
            disabled={!slateHasRates(slate)}
            value={maxLineupPown}
            onChange={(e) => setMaxLineupPown(e.target.value)}
          />
        </div>

        {/* lineup-level geo mean cap */}
      <div className="md:col-span-2">
        <label className="block text-[11px] text-slate-400 mb-1">
          Max Geo Mean pOWN%
        </label>
        <input
          className={`${cls.input} ${T.input} w-full`}
          placeholder="—"
          disabled={!slateHasRates(slate)}
            value={maxGeoMeanPown}
          onChange={(e) => setMaxGeoMeanPown(e.target.value)}
        />
      </div>

      </div>
    </div>
  </div>

  {/* Keep advanced build rules together; collapsed by default. */}
  <details className={`md:col-span-12 ${T.card} ${cls.card} p-3`}>
    <summary className="cursor-pointer select-none text-sm font-semibold">
      Build Rules
    </summary>
    <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-2">
  {/* Stacks / Bring-back */}
  <details
    className={`md:col-span-12 ${T.card} ${cls.card} p-3`}
    open={!!advancedOpen.stacks}
    onToggle={(e) => {
      const open = e.currentTarget.open;
      setAdvancedOpen((v) => ({ ...v, stacks: open }));
    }}
  >
    <summary className="cursor-pointer select-none text-sm font-semibold">
      Stacks / Bring-back
      {stackActiveCount > 0 && (
        <span className="ml-2 rounded-full bg-sky-500/20 px-2 py-0.5 text-[11px] font-medium text-sky-400">
          Active ({stackActiveCount})
        </span>
      )}
    </summary>
    <div className="mt-3">

    <div className="flex flex-wrap items-center gap-2 mb-2">
      <label className="text-sm">QB Stack Min</label>
      <input
        type="number"
        className={`${cls.input} ${T.input} w-16`}
        value={qbStackMin}
        onChange={(e) => setQbStackMin(e.target.value)}
      />

      <label className="text-sm ml-2" title="Opposing RBs, WRs, and TEs count toward this minimum.">Bring-back Min (RB/WR/TE)</label>
      <input
        type="number"
        className={`${cls.input} ${T.input} w-16`}
        value={bringbackMin}
        onChange={(e) => setBringbackMin(e.target.value)}
      />

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={allowTeInFlex}
          onChange={(e) => setAllowTeInFlex(e.target.checked)}
        />
        Allow TE in FLEX
      </label>

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={stackAllowRB}
          onChange={(e) => setStackAllowRB(e.target.checked)}
        />
        Include RB in Stacks
      </label>

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={avoidRbVsOppDst}
          onChange={(e) => setAvoidRbVsOppDst(e.target.checked)}
        />
        Avoid RB vs opp DST
      </label>

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={avoidOffenseVsOppDst}
          onChange={(e) => setAvoidOffenseVsOppDst(e.target.checked)}
        />
        Don’t allow offense vs opp DST
      </label>

      <label className="inline-flex items-center gap-2 text-sm">
        Max from team
        <input
          className={`${cls.input} ${T.input} w-16`}
          placeholder="—"
          value={maxFromTeam}
          onChange={(e) => setMaxFromTeam(e.target.value)}
        />
      </label>

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={restrictStacksToTeams}
          onChange={(e) => setRestrictStacksToTeams(e.target.checked)}
        />
        Only stack from selected teams
      </label>
    </div>

          {/* Team chips + selected summary with caps */}
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Chips */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <button
                  className={ghostBtnClass}
                  onClick={() => setSelectedTeams(new Set(allTeams))}
                >
                  Select all
                </button>
                <button
                  className={ghostBtnClass}
                  onClick={() => {
                  setSelectedTeams(new Set());
                  setQbStackMin(0);
                  setBringbackMin(0);
                }}
                >
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {allTeams.map((t) => {
                  const active = selectedTeams.has(t);
                  const bg = TEAM_COLORS[t] || "#E5E7EB";
                  const fg = readableText(bg);
                  return (
                    <button
                      key={t}
                      onClick={() =>
                        setSelectedTeams((S) => {
                          const n = new Set(S);
                          active ? n.delete(t) : n.add(t);
                          return n;
                        })
                      }
                      className="relative px-2 py-1 rounded-md border text-sm"
                      style={{
                        backgroundColor: bg,
                        color: fg,
                        borderColor: active ? "#111" : "rgba(0,0,0,0.15)",
                        boxShadow: active ? "inset 0 0 0 1px #111" : "none",
                      }}
                      title={t}
                    >
                      {t}
                    {active && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center">✓</span>
                    )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected stack teams */}
            <TeamCapEditor
              selectedTeams={selectedTeams}
              setSelectedTeams={setSelectedTeams}
              isDark={isDark}
            />
          </div>

          {/* Team-specific rules */}
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between items-center mb-2">
              <div className="text-[11px] text-slate-400">
                Team-specific stack rules (override globals)
              </div>
              <button
                className={ghostBtnClass}
                onClick={() => setTeamStacks((T) => [...T, { team: "" }])}
              >
                + Add team rule
              </button>
            </div>

            {teamStacks.length === 0 ? (
              <div className="text-xs text-gray-500">
                No team rules yet. Add one to override globals for a specific team.
              </div>
            ) : (
              <div className="space-y-2">
                {teamStacks.map((r, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 border rounded-md p-2">
                    <TeamSelect
                      teams={allTeams}
                      value={r.team || ""}
                      onChange={(v) =>
                        setTeamStacks((T) => T.map((x, j) => (i === j ? { ...x, team: v } : x)))
                      }
                      isDark={isDark}
                    />

                    <label className="text-sm">QB stack</label>
                    <input
                      className={`${cls.input} ${T.input} w-14`}
                      placeholder="—"
                      value={r.qb_stack_min ?? ""}
                      onChange={(e) =>
                        setTeamStacks((T) =>
                          T.map((x, j) => (i === j ? { ...x, qb_stack_min: e.target.value } : x))
                        )
                      }
                    />

                    <label className="text-sm" title="Opposing RBs, WRs, and TEs count toward this minimum.">Bring-back (RB/WR/TE)</label>
                    <input
                      className={`${cls.input} ${T.input} w-14`}
                      placeholder="—"
                      value={r.bringback_min ?? ""}
                      onChange={(e) =>
                        setTeamStacks((T) =>
                          T.map((x, j) => (i === j ? { ...x, bringback_min: e.target.value } : x))
                        )
                      }
                    />

                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!r.allow_rb_in_stack}
                        onChange={(e) =>
                          setTeamStacks((T) =>
                            T.map((x, j) =>
                              i === j ? { ...x, allow_rb_in_stack: e.target.checked } : x
                            )
                          )
                        }
                      />
                      Allow RB with QB
                    </label>

                    <label className="text-sm">Max from team</label>
                    <input
                      className={`${cls.input} ${T.input} w-16`}
                      placeholder="—"
                      value={r.max_from_team ?? ""}
                      onChange={(e) =>
                        setTeamStacks((T) =>
                          T.map((x, j) => (i === j ? { ...x, max_from_team: e.target.value } : x))
                        )
                      }
                    />

                    <div className="ml-auto" />
                    <button
                      className={ghostBtnClass}
                      onClick={() => setTeamStacks((T) => T.filter((_, j) => j !== i))}
                    >
                      Remove
                    </button>

                    <div className="w-full border-t pt-2 mt-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <label htmlFor={`team-stack-pct-min-${i}`}>Percentage-based minimum: QB +</label>
                        <input
                          id={`team-stack-pct-min-${i}`}
                          type="number"
                          min="1"
                          max="8"
                          step="1"
                          className={`${cls.input} ${T.input} w-16`}
                          placeholder="Off"
                          value={r.qb_stack_pct_min ?? ""}
                          title="Additional same-team teammates required. Leave blank to disable."
                          onChange={(e) => {
                            const value = e.target.value;
                            setTeamStacks((rules) => rules.map((x, j) => i === j ? {
                              ...x,
                              qb_stack_pct_min: value === "" ? "" : clamp(Math.floor(Number(value) || 1), 1, 8),
                            } : x));
                          }}
                        />
                        <label htmlFor={`team-stack-pct-${i}`}>teammates in at least</label>
                        <input
                          id={`team-stack-pct-${i}`}
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          className={`${cls.input} ${T.input} w-20 disabled:opacity-50`}
                          placeholder="100"
                          disabled={r.qb_stack_pct_min == null || String(r.qb_stack_pct_min).trim() === ""}
                          value={r.qb_stack_pct ?? ""}
                          title="Minimum percentage of this team's QB lineups. Blank means 100%."
                          onChange={(e) => {
                            const value = e.target.value;
                            setTeamStacks((rules) => rules.map((x, j) => i === j ? {
                              ...x,
                              qb_stack_pct: value === "" ? "" : clamp(Number(value) || 0, 0, 100),
                            } : x));
                          }}
                        />
                        <span>% of this team's QB lineups.</span>
                      </div>
                      <p className={`text-xs ${T.subtext}`}>
                        Leave the size blank to turn off. Example: QB stack 0, percentage minimum 1,
                        and 60% requires QB + 1 or more in at least 60% of this team's QB lineups.
                        Remaining lineups follow the regular QB stack minimum and may still stack.
                        WR/TE count; RB counts when Allow RB with QB is enabled. Whole-lineup targets round up.
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
  </details>

        {/* CLASSIC IF -> THEN RULES (collapsed by default) */}
        <details
          className={`md:col-span-12 ${T.card} ${cls.card} p-3`}
          open={!!advancedOpen.conditional}
          onToggle={(e) => {
            const open = e.currentTarget.open;
            setAdvancedOpen((v) => ({ ...v, conditional: open }));
          }}
        >
          <summary className="cursor-pointer select-none text-sm font-semibold">
            Conditional Rules <span className="ml-2 text-[11px] font-normal text-slate-400">IF → THEN</span>
            {ifThenRules.length > 0 && (
              <span className="ml-2 rounded-full bg-sky-500/20 px-2 py-0.5 text-[11px] font-medium text-sky-400">
                {ifThenRules.length}
              </span>
            )}
          </summary>

          <div className="flex items-center justify-between gap-3 mt-3 mb-2">
            <div className="text-[11px] text-slate-400">
              Build custom team, game, position, or player relationships.
            </div>
            <button
              className={ghostBtnClass}
              onClick={() => setIfThenRules((R) => [...R, {
                if_mode: "at_least", if_count: 1, if_type: "positions", if_pos: [],
                if_players: [], if_scope: "each_team", if_team_exact: "",
                then_mode: "at_least", then_count: 1, then_type: "positions", then_pos: [],
                then_players: [], then_scope: "same_team",
              }])}
            >
              + Add Rule
            </button>
          </div>

          {ifThenRules.length === 0 ? (
            <div className="text-xs text-gray-500 py-2">No conditional rules.</div>
          ) : (
            <div className="space-y-3">
              {ifThenRules.map((r, i) => {
                const ruleButton = (active, color = "blue") =>
                  `px-2 py-1 rounded-md border text-sm ${active
                    ? color === "green"
                      ? "bg-emerald-500 border-emerald-500 text-slate-950"
                      : "bg-sky-500 border-sky-500 text-slate-950"
                    : isDark
                      ? "border-slate-700 text-slate-200"
                      : "border-gray-300 bg-white text-slate-700"}`;
                const updateRule = (patch) =>
                  setIfThenRules((R) => R.map((x, j) => j === i ? { ...x, ...patch } : x));

                return (
                  <div key={i} className={`rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-950/30" : "border-gray-200 bg-white"}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold">IF lineup has</span>
                      <select className={`${cls.input} ${T.input}`} value={r.if_mode || "at_least"} onChange={(e) => updateRule({ if_mode: e.target.value })}>
                        <option value="at_least">at least</option>
                        <option value="at_most">at most</option>
                        <option value="exactly">exactly</option>
                      </select>
                      <input type="number" min="0" className={`${cls.input} ${T.input} w-16`} value={r.if_count ?? 1} onChange={(e) => updateRule({ if_count: e.target.value })} />
                      <select className={`${cls.input} ${T.input}`} value={r.if_type || "positions"} onChange={(e) => updateRule({ if_type: e.target.value })}>
                        <option value="positions">positions</option>
                        <option value="players">specific players</option>
                      </select>

                      {(r.if_type || "positions") === "positions" && ["QB", "RB", "WR", "TE", "DST"].map((p) => (
                        <button key={p} className={ruleButton((r.if_pos || []).includes(p))} onClick={() => {
                          const next = new Set(r.if_pos || []); next.has(p) ? next.delete(p) : next.add(p); updateRule({ if_pos: [...next] });
                        }}>{p}</button>
                      ))}

                      <span className="text-sm">from</span>
                      <select
                        className={`${cls.input} ${T.input}`}
                        value={r.if_scope === "exact_team" ? `exact:${r.if_team_exact || ""}` : (r.if_scope || "each_team")}
                        onChange={(e) => e.target.value.startsWith("exact:")
                          ? updateRule({ if_scope: "exact_team", if_team_exact: e.target.value.slice(6) })
                          : updateRule({ if_scope: e.target.value, if_team_exact: "" })}
                      >
                        <option value="each_team">any single team</option>
                        <option value="anywhere">anywhere</option>
                        {allTeams.map((t) => <option key={t} value={`exact:${t}`}>{t}</option>)}
                      </select>

                      <span className="text-sm font-semibold ml-1">THEN require</span>
                      <select className={`${cls.input} ${T.input}`} value={r.then_mode || "at_least"} onChange={(e) => updateRule({ then_mode: e.target.value })}>
                        <option value="at_least">at least</option>
                        <option value="at_most">at most</option>
                        <option value="exactly">exactly</option>
                      </select>
                      <input type="number" min="0" className={`${cls.input} ${T.input} w-16`} value={r.then_count ?? 1} onChange={(e) => updateRule({ then_count: e.target.value })} />
                      <select className={`${cls.input} ${T.input}`} value={r.then_type || "positions"} onChange={(e) => updateRule({ then_type: e.target.value })}>
                        <option value="positions">positions</option>
                        <option value="players">specific players</option>
                      </select>

                      {(r.then_type || "positions") === "positions" && ["QB", "RB", "WR", "TE", "DST"].map((p) => (
                        <button key={p} className={ruleButton((r.then_pos || []).includes(p), "green")} onClick={() => {
                          const next = new Set(r.then_pos || []); next.has(p) ? next.delete(p) : next.add(p); updateRule({ then_pos: [...next] });
                        }}>{p}</button>
                      ))}

                      <span className="text-sm">from</span>
                      <select className={`${cls.input} ${T.input}`} value={r.then_scope || "same_team"} onChange={(e) => updateRule({ then_scope: e.target.value })}>
                        <option value="same_team">same team</option>
                        <option value="opp_team">opponent</option>
                        <option value="same_game">same game</option>
                        <option value="anywhere">anywhere</option>
                      </select>

                      <button className={`${ghostBtnClass} ml-auto`} onClick={() => setIfThenRules((R) => R.filter((_, j) => j !== i))}>Delete</button>
                    </div>

                    {((r.if_type || "positions") === "players" || (r.then_type || "positions") === "players") && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                        {(r.if_type || "positions") === "players" && (
                          <div>
                            <div className="text-[11px] text-slate-400 mb-1">IF players</div>
                            <PlayerMultiPicker allPlayers={allPlayerNames} value={r.if_players || []} onChange={(vals) => updateRule({ if_players: vals })} placeholder="Select IF players…" isDark={isDark} />
                          </div>
                        )}
                        {(r.then_type || "positions") === "players" && (
                          <div>
                            <div className="text-[11px] text-slate-400 mb-1">THEN players</div>
                            <PlayerMultiPicker allPlayers={allPlayerNames} value={r.then_players || []} onChange={(vals) => updateRule({ then_players: vals })} placeholder="Select THEN players…" isDark={isDark} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </details>

        {/* GROUPS */}
        <details
          className={`md:col-span-12 ${T.card} ${cls.card} p-3`}
          open={!!advancedOpen.groups}
          onToggle={(e) => {
            const open = e.currentTarget.open;
            setAdvancedOpen((v) => ({ ...v, groups: open }));
          }}
        >
          <summary className="cursor-pointer select-none text-sm font-semibold">
            Groups
            {groups.length > 0 && (
              <span className="ml-2 rounded-full bg-sky-500/20 px-2 py-0.5 text-[11px] font-medium text-sky-400">{groups.length}</span>
            )}
          </summary>
          <div className="flex items-center justify-between mt-3 mb-2">
            <div className="text-[11px] text-slate-400">At least / at most / exactly N from a player list</div>
            <button className={ghostBtnClass} onClick={() => setGroups((G) => [...G, { mode: "at_most", count: 1, players: [] }])}>
              + Add group
            </button>
          </div>
          {groups.length === 0 ? (
            <div className="text-xs text-gray-500">No groups yet.</div>
          ) : (
            <div className="space-y-2">
              {groups.map((g, i) => (
                <div key={i} className="border rounded-md p-2">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <select
                      className={`${cls.input} ${T.input}`}
                      value={g.mode || "at_most"}
                      onChange={(e) => setGroups((G) => G.map((x, j) => j === i ? { ...x, mode: e.target.value } : x))}
                    >
                      <option value="at_least">at least</option>
                      <option value="at_most">at most</option>
                      <option value="exactly">exactly</option>
                    </select>
                    <input
                      className={`${cls.input} ${T.input} w-20`}
                      type="number"
                      value={g.count ?? 1}
                      onChange={(e) => setGroups((G) => G.map((x, j) => j === i ? { ...x, count: e.target.value } : x))}
                    />
                    <button className={`${cls.btn.ghost} ml-auto`} onClick={() => setGroups((G) => G.filter((_, j) => j !== i))}>
                      Remove
                    </button>
                  </div>
                  <PlayerMultiPicker
                    allPlayers={allPlayerNames}
                    value={Array.isArray(g.players) ? g.players : []}
                    onChange={(vals) =>
                      setGroups((G) =>
                        G.map((x, j) => (j === i ? { ...x, players: vals } : x))
                      )
                    }
                    placeholder="Add players to this group…"
                    isDark={isDark}
                  />
                </div>
              ))}
            </div>
          )}
        </details>

        {/* PLAYER PAIR CAPS */}
        <details
          className={`md:col-span-12 ${T.card} ${cls.card} p-3`}
          open={!!advancedOpen.pairCaps}
          onToggle={(e) => {
            const open = e.currentTarget.open;
            setAdvancedOpen((v) => ({ ...v, pairCaps: open }));
          }}
        >
          <summary className="cursor-pointer select-none text-sm font-semibold">
            Player Pair Caps
            {pairCaps.length > 0 && (
              <span className="ml-2 rounded-full bg-sky-500/20 px-2 py-0.5 text-[11px] font-medium text-sky-400">{pairCaps.length}</span>
            )}
          </summary>
          <div className="flex items-center justify-between mt-3 mb-2">
            <div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                Limit how often two specific players can appear together across this build.
              </div>
            </div>

            <button
              className={ghostBtnClass}
              onClick={() =>
                setPairCaps((prev) => [
                  ...(prev || []),
                  { players: [], maxPct: 25 },
                ])
              }
              type="button"
            >
              + Add pair cap
            </button>
          </div>

          {(pairCaps || []).length === 0 ? (
            <div className="text-xs text-gray-500">
              No pair caps yet. Example: Josh Allen + Dalton Kincaid max 40%.
            </div>
          ) : (
            <div className="space-y-3">
              {(pairCaps || []).map((p, idx) => {
                const players = Array.isArray(p.players) ? p.players : [];
                const lineupTotal = Math.max(1, Number(numLineups) || 1);
                const maxAllowed = Math.floor(
                  (lineupTotal * (Number(p.maxPct) || 0)) / 100
                );

                return (
                  <div
                    key={idx}
                    className={
                      "rounded-md border px-3 py-2 " +
                      (isDark
                        ? "border-slate-700"
                        : "border-slate-200")
                    }
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
                      <div className="lg:col-span-8">
                        <PlayerMultiPicker
                          allPlayers={allPlayerNames}
                          value={players}
                          onChange={(nextPlayers) =>
                            setPairCaps((prev) =>
                              (prev || []).map((pp, i) =>
                                i === idx
                                  ? {
                                      ...pp,
                                      players: [...nextPlayers].slice(0, 2),
                                    }
                                  : pp
                              )
                            )
                          }
                          placeholder="Add exactly 2 players…"
                          isDark={isDark}
                        />
                      </div>

                      <div className="lg:col-span-2">
                        <label className="block text-[11px] text-gray-500 mb-1">
                          Max Pair %
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          className={`${cls.input} ${T.input} w-full`}
                          value={p.maxPct ?? 25}
                          onChange={(e) =>
                            setPairCaps((prev) =>
                              (prev || []).map((pp, i) =>
                                i === idx
                                  ? {
                                      ...pp,
                                      maxPct: clamp(
                                        Number(e.target.value) || 0,
                                        0,
                                        100
                                      ),
                                    }
                                  : pp
                              )
                            )
                          }
                        />
                      </div>

                      <div className="lg:col-span-2 flex lg:justify-end">
                        <button
                          className={`${ghostBtnClass} mt-5`}
                          onClick={() =>
                            setPairCaps((prev) =>
                              (prev || []).filter((_, i) => i !== idx)
                            )
                          }
                          type="button"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div
                      className={`mt-2 text-xs ${
                        isDark ? "text-slate-400" : "text-gray-500"
                      }`}
                    >
                      {players.length === 2
                        ? `This pair can appear together in at most ${maxAllowed} of ${lineupTotal} lineups.`
                        : "Select exactly 2 players for this pair cap."}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </details>

    </div>
  </details>

      </div>

{/* Game cards */}
{sortedGames.length > 0 && (
  <section className={`${T.card} ${cls.card} p-3 mb-3`} aria-label="Games">
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <h2 className="text-sm font-semibold mr-1">Games</h2>
      <button type="button" className={ghostBtnClass + " text-xs"}
        onClick={() => setSelectedGames(new Set(sortedGames.map((g) => g.gameKey)))}>Select all</button>
      <button type="button" className={ghostBtnClass + " text-xs"}
        onClick={() => setSelectedGames(new Set())}>Clear all</button>
    </div>
    {/* horizontal game card strip */}
    <div className="overflow-x-auto pb-2">
      <div className="flex gap-2.5 min-w-max">
        {sortedGames.map((g) => {
          const active = selectedGames.has(g.gameKey);
          const away = g.away || g.teams?.[0] || "";
          const home = g.home || g.teams?.[1] || "";

          const gameExcluded = isGameFullyExcluded(g.gameKey);
          const awayExcluded = isTeamFullyExcluded(away);
          const homeExcluded = isTeamFullyExcluded(home);

          const awayImp = teamImpliedMap.get(away);
          const homeImp = teamImpliedMap.get(home);
          const gameOu =
            teamVegasMap.get(away)?.ou ??
            teamVegasMap.get(home)?.ou;

          return (
            <div
              key={g.gameKey}
              role="button"
              tabIndex={0}
              onClick={() =>
                setSelectedGames((S) => {
                  const n = new Set(S);
                  active ? n.delete(g.gameKey) : n.add(g.gameKey);
                  return n;
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedGames((S) => {
                    const n = new Set(S);
                    active ? n.delete(g.gameKey) : n.add(g.gameKey);
                    return n;
                  });
                }
              }}
              className={`w-[168px] shrink-0 rounded-xl border overflow-hidden transition-all duration-150 cursor-pointer ${
                gameExcluded
                  ? isDark
                    ? "border-red-700/50 bg-red-950/10 opacity-70"
                    : "border-red-300 bg-red-50 opacity-80"
                  : active
                  ? isDark
                    ? "border-sky-500/60 bg-slate-900/95 shadow-[0_0_0_1px_rgba(56,189,248,0.25)]"
                    : "border-sky-300 bg-white shadow-sm"
                  : isDark
                  ? "border-slate-800 bg-slate-900/90 hover:border-slate-700"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              {/* top bar */}
                <div
                  className={`px-2 py-1 border-b ${
                  isDark
                    ? "border-slate-800 bg-slate-800/40"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="grid grid-cols-[16px_1fr_22px] items-center gap-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedGames((S) => {
                        const n = new Set(S);
                        active ? n.delete(g.gameKey) : n.add(g.gameKey);
                        return n;
                      });
                    }}
                    className={`w-4 h-4 rounded-[5px] border flex items-center justify-center text-[9px] leading-none ${
                      active
                        ? isDark
                          ? "border-sky-400 bg-sky-500/15 text-sky-300"
                          : "border-sky-500 bg-sky-50 text-sky-700"
                        : isDark
                        ? "border-slate-700 text-slate-500"
                        : "border-slate-300 text-slate-400"
                    }`}
                    aria-label={active ? "Unselect game" : "Select game"}
                  >
                    {active ? "✓" : ""}
                  </button>

                  <div
                    className={`text-center text-[11px] font-semibold leading-tight ${
                      isDark ? "text-slate-300" : "text-slate-600"
                    }`}
                  >
                    <div>{g.time || "—"}</div>
                    <div className={isDark ? "text-slate-500" : "text-slate-500"}>
                      O/U {Number.isFinite(gameOu) ? fmt1(gameOu) : "—"}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExcludeGame(g.gameKey);
                    }}
                    className={`w-[22px] h-[22px] rounded-md border flex items-center justify-center text-[11px] leading-none ${
                      gameExcluded
                        ? "bg-red-600 text-white border-red-700"
                        : isDark
                        ? "border-slate-700 text-slate-400 hover:bg-slate-800"
                        : "border-slate-300 text-slate-500 hover:bg-slate-100"
                    }`}
                    title={gameExcluded ? "Un-exclude game" : "Exclude game"}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* teams */}
              <div className="p-2 space-y-1.5">
                {[
                  {
                    abbr: away,
                    implied: awayImp,
                    spread: teamVegasMap.get(away)?.spread,
                    excluded: awayExcluded,
                  },
                  {
                    abbr: home,
                    implied: homeImp,
                    spread: teamVegasMap.get(home)?.spread,
                    excluded: homeExcluded,
                  },
                ].map((teamRow, idx) => (
                  <div
                    key={`${g.gameKey}-${teamRow.abbr}-${idx}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExcludeTeam(teamRow.abbr);
                    }}
                    className={`grid grid-cols-[1fr_auto_auto] items-center gap-1.5 rounded-lg px-2 py-1.5 ${
                      teamRow.excluded
                        ? isDark
                          ? "bg-red-950/20 ring-1 ring-red-800/40"
                          : "bg-red-50 ring-1 ring-red-200"
                        : isDark
                        ? "bg-slate-800/45"
                        : "bg-slate-50"
                    }`}
                    title={
                      teamRow.excluded
                        ? `Un-exclude ${teamRow.abbr}`
                        : `Exclude ${teamRow.abbr}`
                    }
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <img
                        src={logoSrc(teamRow.abbr)}
                        alt={teamRow.abbr}
                        className="w-[18px] h-[18px] object-contain shrink-0"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <span
                        className={`font-semibold text-[11px] ${
                          isDark ? "text-slate-100" : "text-slate-900"
                        }`}
                      >
                        {teamRow.abbr}
                      </span>
                      {Number.isFinite(Number(teamRow.spread)) ? (
                        <span className={`text-[9px] font-semibold tabular-nums ${
                          isDark ? "text-slate-500" : "text-slate-500"
                        }`}>
                          {Number(teamRow.spread) > 0 ? "+" : ""}
                          {fmt1(Number(teamRow.spread)).replace(".0", "")}
                        </span>
                      ) : null}
                    </div>

                <div className="ml-auto flex items-center gap-1.5 shrink-0">
                  <div className="flex flex-col items-center justify-center leading-none -space-y-[1px]">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        bumpTeamBoost(teamRow.abbr, +1);
                      }}
                      className={`h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition ${
                        (teamBoost[teamRow.abbr] || 0) > 0
                          ? isDark
                            ? "text-emerald-300 bg-emerald-500/10"
                            : "text-emerald-700 bg-emerald-50"
                          : isDark
                          ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40"
                          : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"
                      }`}
                      title={`Boost ${teamRow.abbr} offense +10%`}
                    >
                      ▲
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        bumpTeamBoost(teamRow.abbr, -1);
                      }}
                      className={`h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition ${
                        (teamBoost[teamRow.abbr] || 0) < 0
                          ? isDark
                            ? "text-rose-300 bg-rose-500/10"
                            : "text-rose-700 bg-rose-50"
                          : isDark
                          ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40"
                          : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"
                      }`}
                      title={`Reduce ${teamRow.abbr} offense -10%`}
                    >
                      ▼
                    </button>
                  </div>

                  <div className="min-w-[30px] text-right leading-none">
                    <div
                      className={`text-[13px] font-semibold tabular-nums ${
                        isDark ? "text-slate-200" : "text-slate-700"
                      }`}
                    >
                      {Number.isFinite(teamRow.implied) ? fmt1(teamRow.implied) : "—"}
                    </div>

                    {!!(teamBoost[teamRow.abbr] || 0) && (
                      <div
                        className={`text-[9px] tabular-nums mt-0.5 ${
                          (teamBoost[teamRow.abbr] || 0) > 0 ? "text-emerald-500" : "text-rose-500"
                        }`}
                      >
                        {(teamBoost[teamRow.abbr] || 0) > 0
                          ? `+${teamBoost[teamRow.abbr]}`
                          : teamBoost[teamRow.abbr]}
                      </div>
                    )}
                  </div>
                </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
)}

<section className={`${T.card} ${cls.card} p-3 mb-3`} aria-label="Optimize">
      {/* progress + button */}
      <div className="flex flex-wrap items-center gap-3">
        <button className="h-9 px-6 rounded-md bg-sky-500 text-slate-950 hover:bg-sky-400 text-sm font-semibold" onClick={optimize}>
          {`Optimize ${numLineups}`}
        </button>

        {/* themed progress track, with border like Cup */}
        <div
          className={
            "flex-1 max-w-xs h-2 rounded-full overflow-hidden border " +
            (isDark
              ? "bg-slate-900/80 border-slate-700"
              : "bg-slate-200 border-slate-300")
          }
        >
          <div
            className={
              "h-full transition-all duration-300 " +
              (isDark ? "bg-sky-500" : "bg-blue-500")
            }
            style={{
              width: `${
                (Math.min(progressUI, Math.max(1, Number(numLineups) || 1)) /
                  Math.max(1, Number(numLineups) || 1)) *
                100
              }%`,
            }}
          />
        </div>

        <div
          className={
            "text-sm min-w-[60px] text-right " +
            (isDark ? "text-slate-400" : "text-gray-600")
          }
        >
          {progressUI}/{numLineups}
        </div>
      </div>

  <details className={`mt-3 border-t pt-3 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
    <summary className="cursor-pointer select-none text-sm font-semibold">Reset Tools</summary>
    <div className="mt-3 flex flex-wrap gap-2">
      <button type="button" className={ghostBtnClass + " text-xs"} onClick={clearAllBoosts}>Reset Boosts</button>
      <button type="button" className={ghostBtnClass + " text-xs"} onClick={clearAllLocks}>Reset Locks</button>
      <button type="button" className={ghostBtnClass + " text-xs"} onClick={clearAllExcludes}>Reset Excludes</button>
      <button type="button" className={ghostBtnClass + " text-xs"} onClick={clearAllMinMax}>Reset Min/Max</button>
      <button type="button" className={ghostBtnClass + " text-xs"} onClick={clearBoostsMinMaxProj}>Clear boosts/min/max/proj edits</button>
    </div>
  </details>
</section>

<section className={`${T.card} ${cls.card} overflow-hidden mb-6`} aria-label="Player pool">
  <div className={`p-3 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <h2 className="text-sm font-semibold">Player Pool</h2>
      <span className={`text-xs ${T.subtext}`}>{displayRows.length.toLocaleString()} players shown</span>
    </div>
    <div className="flex flex-wrap items-center gap-3 mb-3">
      {/* Position tabs */}
      <div className="flex flex-wrap items-center gap-3 text-sky-600 font-semibold text-xs">
        {["ALL", "QB", "RB", "WR", "TE", "FLEX", "DST"].map((p) => (
          <button
            key={p}
            onClick={() => setPosFilter(p)}
            className={posFilter === p ? "underline" : "opacity-80 hover:opacity-100"}
          >
            {p}
          </button>
        ))}
      </div>

      <div className="w-full sm:w-auto sm:ml-auto">
      <input
        className={`${cls.input} ${T.input} w-full sm:w-80 max-w-full`}
        placeholder="Search player / team / pos…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      </div>

    </div>
    {/* Player visibility and pool controls */}
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <label
        className={`${ghostBtnClass} flex items-center gap-2 text-xs cursor-pointer`}
        title="Hide unprojected players from the site player list"
      >
        <input
          type="checkbox"
          checked={hideUnprojected}
          onChange={(e) => setHideUnprojected(e.target.checked)}
        />
        Hide Unprojected
      </label>

      <label
        className={`${ghostBtnClass} flex items-center gap-2 text-xs cursor-pointer`}
        title="Hide players that you have manually excluded"
      >
        <input
          type="checkbox"
          checked={hideExcluded}
          onChange={(e) => setHideExcluded(e.target.checked)}
        />
        Hide Excluded
      </label>

      <label
        className={`${ghostBtnClass} flex items-center gap-2 text-xs`}
        title="Automatically exclude players whose Val is below this number. The cutoff itself is included."
      >
        Exclude value under
        <input
          type="number"
          min="0"
          step="0.1"
          className={`h-6 w-12 rounded-md border px-1 text-center text-xs outline-none focus:ring-2 focus:ring-sky-500/30 ${T.input}`}
          placeholder="—"
          value={minValue}
          onChange={(e) => setMinValue(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      </label>

      <button
        className={ghostBtnClass + " text-xs"}
        onClick={excludeCurrentPosition}
      >
        Exclude position ({posFilter}) (toggle)
      </button>

    </div>

  </div>
{/* Player table */}
<div
  className="overflow-auto max-h-[620px]"
>
  <table
    className={`w-full border-separate ${textSz}`}
    style={{ borderSpacing: 0 }}
  >
    <thead
      className={`${
        isDark ? "bg-slate-950" : "bg-gray-100"
      } sticky top-0 z-10`}
    >
      <tr>
        {TABLE_COLS.map(({ key, label, sortable }) => (
          <th
            key={key}
            className={`${header} whitespace-nowrap cursor-${
              sortable ? "pointer" : "default"
            } select-none`}
            onClick={() => sortable && setSort(key)}
          >
            {label}
            {sortable ? (
              <span className="opacity-60">{sortArrow(key)}</span>
            ) : null}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {loading && (
        <tr>
          <td
            className={`${cell} ${
              isDark ? "text-slate-400" : "text-gray-500"
            }`}
            colSpan={TABLE_COLS.length}
          >
            Loading…
          </td>
        </tr>
      )}
      {err && (
        <tr>
          <td
            className={`${cell} ${
              isDark ? "text-red-400" : "text-red-600"
            }`}
            colSpan={TABLE_COLS.length}
          >
            Failed to load: {String(err)}
          </td>
        </tr>
      )}
      {!loading &&
        !err &&
        displayRows.map((r) => (
          <tr
            key={r.name}
            className={
              "transition-colors " +
              (isDark
                ? "odd:bg-slate-950/80 even:bg-slate-900/80 hover:bg-slate-800"
                : "odd:bg-white even:bg-slate-100 hover:bg-blue-50/70")
            }
          >
            <td className={cell}>
              <input
                type="checkbox"
                checked={locks.has(r.name)}
                onChange={() => toggleLock(r.name)}
              />
            </td>
            <td className={cell}>
              <input
                type="checkbox"
                checked={isEffectivelyExcluded(r.name)}
                onChange={() => toggleExcl(r)}
                title={r.unprojected ? "Unprojected players are excluded by default. Uncheck to allow." : "Exclude player"}
              />
            </td>
            <td className={cell}>{r.pos}</td>
            <td className={cell}>
              <div className="inline-flex items-center gap-1">
                <button
                  className={iconBtnClass}
                  title="+3%"
                  onClick={() =>
                    setBoost((m) => ({
                      ...m,
                      [r.name]: clamp((m[r.name] || 0) + 1, -6, 6),
                    }))
                  }
                >
                  ▲
                </button>
                <span className="w-5 text-center">
                  {r.boostSteps || 0}
                </span>
                <button
                  className={iconBtnClass}
                  title="-3%"
                  onClick={() =>
                    setBoost((m) => ({
                      ...m,
                      [r.name]: clamp((m[r.name] || 0) - 1, -6, 6),
                    }))
                  }
                >
                  ▼
                </button>
              </div>
            </td>
            <td className={`${cell} whitespace-nowrap`}>
              {["QB", "RB", "WR", "TE"].includes(r.pos) ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setGameLogPlayer({ name: r.name, pos: r.pos });
                  }}
                  className="font-semibold text-inherit underline decoration-current/50 underline-offset-2 hover:text-sky-500 hover:decoration-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:rounded"
                  title={`View ${r.name} game log`}
                  aria-haspopup="dialog"
                >
                  {r.name}
                </button>
              ) : r.name}
            </td>
            <td className={cell}>
              <TeamPill abbr={r.team} />
            </td>
            <td className={cell}>
              <TeamPill abbr={r.opp} />
            </td>
            <td className={`${cell} tabular-nums`}>{fmt0(r.salary)}</td>
            <td className={cell}>{r.time || "—"}</td>
            <td className={`${cell} tabular-nums`}>
              <div className="inline-flex items-center gap-1 justify-center">
                <input
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  className="h-7 w-[64px] rounded px-1.5 py-0 text-center text-xs border focus:outline-none focus:ring-1 focus:ring-blue-500"
                  style={{
                    backgroundColor: r.hasCustomProj
                      ? (Number(r.proj) > Number(r.baseProj || 0)
                          ? "rgba(34,197,94,0.14)"
                          : Number(r.proj) < Number(r.baseProj || 0)
                          ? "rgba(239,68,68,0.14)"
                          : isDark ? "rgba(15,23,42,0.9)" : "#ffffff")
                      : isDark ? "rgba(15,23,42,0.9)" : "#ffffff",
                    color: r.hasCustomProj
                      ? (Number(r.proj) > Number(r.baseProj || 0)
                          ? "#22c55e"
                          : Number(r.proj) < Number(r.baseProj || 0)
                          ? "#ef4444"
                          : undefined)
                      : undefined,
                    borderColor: r.hasCustomProj
                      ? (Number(r.proj) > Number(r.baseProj || 0)
                          ? "rgba(34,197,94,0.7)"
                          : Number(r.proj) < Number(r.baseProj || 0)
                          ? "rgba(239,68,68,0.7)"
                          : isDark ? "#334155" : "#cbd5e1")
                      : isDark ? "#334155" : "#cbd5e1",
                  }}
                  value={
                    Object.prototype.hasOwnProperty.call(projDraft, r.name)
                      ? projDraft[r.name]
                      : fmt1(r.boostedProj ?? r.proj ?? 0)
                  }
                  onFocus={() => {
                    setProjDraft((m) => ({
                      ...m,
                      [r.name]: fmt1(r.boostedProj ?? r.proj ?? 0),
                    }));
                  }}
                  onBlur={() => {
                    setProjDraft((m) => {
                      const next = { ...m };
                      delete next[r.name];
                      return next;
                    });
                  }}
                  onChange={(e) => {
                    const value = e.target.value;

                    setProjDraft((m) => ({
                      ...m,
                      [r.name]: value,
                    }));

                    setCustomProj((m) => {
                      const next = { ...m };

                      if (value == null || String(value).trim() === "") {
                        delete next[r.name];
                        return next;
                      }

                      const shownProj = Number(value);
                      if (!Number.isFinite(shownProj)) return next;

                      const playerSteps = Number(boost[r.name] || 0);
                      const teamSteps = r.pos !== "DST" ? Number(teamBoost[r.team] || 0) : 0;
                      const mult = 1 + 0.03 * playerSteps + 0.10 * teamSteps;
                      const baseCustomProj = mult !== 0 ? shownProj / mult : shownProj;

                      next[r.name] = String(Math.max(0, baseCustomProj));
                      return next;
                    });
                  }}
                  title={
                    r.unprojected
                      ? "Type a projection to use this player manually"
                      : "Custom projection override. Boosts are shown in this box."
                  }
                />
                {r.hasCustomProj && (
                  <button
                    type="button"
                    className={iconBtnClass}
                    title="Reset projection"
                    onClick={() => {
                      setCustomProj((m) => {
                        const next = { ...m };
                        delete next[r.name];
                        return next;
                      });
                      setProjDraft((m) => {
                        const next = { ...m };
                        delete next[r.name];
                        return next;
                      });
                    }}
                  >
                    ↺
                  </button>
                )}
              </div>
            </td>
            <td className={`${cell} tabular-nums`}>{fmt1(r.boostedVal ?? r.val)}</td>
            <td className={`${cell} tabular-nums`}>{fmt1(r.floor)}</td>
            <td className={`${cell} tabular-nums`}>{fmt1(r.ceil)}</td>
            <td className={`${cell} tabular-nums`}>
              {r.ratesAvailable ? fmtPct(r.pown) : "—"}
            </td>
            <td className={`${cell} tabular-nums`}>
              {r.ratesAvailable ? fmtPct(r.opt) : "—"}
            </td>

            {/* Min% */}
            <td className={cell}>
              <div className="inline-flex items-center gap-1">
                <button
                  className={iconBtnClass}
                  onClick={() =>
                    setMinPct((m) => ({
                      ...m,
                      [r.name]: clamp(
                        (num(m[r.name]) || 0) - 5,
                        0,
                        100
                      ),
                    }))
                  }
                  title="-5%"
                >
                  –
                </button>
                <input
                  className={`
                    h-7 w-11 rounded px-1 py-0 text-center text-xs border
                    focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
                    ${
                      isDark
                        ? "bg-slate-900/90 text-slate-50 border-slate-700 placeholder:text-slate-500"
                        : "bg-white text-slate-900 border-gray-300 placeholder:text-slate-400"
                    }
                  `}
                  value={String(minPct[r.name] ?? "")}
                  onChange={(e) =>
                    setMinPct((m) => ({
                      ...m,
                      [r.name]: e.target.value,
                    }))
                  }
                  placeholder="—"
                />
                <button
                  className={iconBtnClass}
                  onClick={() =>
                    setMinPct((m) => ({
                      ...m,
                      [r.name]: clamp(
                        (num(m[r.name]) || 0) + 5,
                        0,
                        100
                      ),
                    }))
                  }
                  title="+5%"
                >
                  +
                </button>
              </div>
            </td>

            {/* Max% */}
            <td className={cell}>
              <div className="inline-flex items-center gap-1">
                <button
                  className={iconBtnClass}
                  onClick={() =>
                    setMaxPct((m) => ({
                      ...m,
                      [r.name]: clamp(
                        (num(m[r.name]) || 100) - 5,
                        0,
                        100
                      ),
                    }))
                  }
                  title="-5%"
                >
                  –
                </button>
                <input
                  className={`
                    h-7 w-11 rounded px-1 py-0 text-center text-xs border
                    focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
                    ${
                      isDark
                        ? "bg-slate-900/90 text-slate-50 border-slate-700 placeholder:text-slate-500"
                        : "bg-white text-slate-900 border-gray-300 placeholder:text-slate-400"
                    }
                  `}
                  value={String(maxPct[r.name] ?? "")}
                  onChange={(e) =>
                    setMaxPct((m) => ({
                      ...m,
                      [r.name]: e.target.value,
                    }))
                  }
                  placeholder="—"
                />
                <button
                  className={iconBtnClass}
                  onClick={() =>
                    setMaxPct((m) => ({
                      ...m,
                      [r.name]: clamp(
                        (num(m[r.name]) || 100) + 5,
                        0,
                        100
                      ),
                    }))
                  }
                  title="+5%"
                >
                  +
                </button>
              </div>
            </td>

            <td className={`${cell} tabular-nums`}>
              {usagePct[r.name] != null ? fmt1(usagePct[r.name]) : "—"}
            </td>
          </tr>
        ))}
    </tbody>
  </table>
</div>


</section>

      {/* Build manager (chips + actions) */}
      {builds.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-400">Builds:</span>
          <div className="flex flex-wrap gap-2">
            {builds
              .slice()
              .sort((a, b) => b.id - a.id)
              .map((b) => {
                const active = activeBuildId === b.id;

                const inactiveClasses = isDark
                  ? "border-slate-700 bg-slate-900/80 text-slate-200 hover:bg-slate-800"
                  : "border-gray-300 bg-white text-slate-800 hover:bg-gray-50";

                return (
                  <div
                    key={b.id}
                    className={`${cls.btn.chip} inline-flex items-center gap-2 ${
                      active
                        ? "border-blue-600 bg-blue-600 text-white"
                        : inactiveClasses
                    }`}
                    title={new Date(b.ts).toLocaleString()}
                  >
                    <button onClick={() => loadBuild(b.id)} className="underline underline-offset-2">
                      {b.name}
                    </button>
                    <span className="opacity-80">{b.lineups?.length ?? 0} • {timeAgo(b.ts)}</span>
                    <button
                      className="ml-1 rounded-full w-4 h-4 flex items-center justify-center bg-black/10 hover:bg-black/20"
                      title="Delete build"
                      onClick={() => deleteBuild(b.id)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
          </div>

          {/* keep controls close to chips */}
          <div className="flex items-center gap-2">
            {activeBuildId && (
              <>
                <button
                  className={ghostBtnClass}
                  onClick={() => {
                    const newName =
                      prompt(
                        "Rename build",
                        builds.find((x) => x.id === activeBuildId)?.name || ""
                      ) || "";
                    if (newName.trim()) renameBuild(activeBuildId, newName.trim());
                  }}
                >
                  Rename
                </button>
                <button className={ghostBtnClass} onClick={() => deleteBuild(activeBuildId)}>
                  Delete
                </button>
              </>
            )}
            <button className={ghostBtnClass} onClick={clearBuilds}>
              Clear All Builds
            </button>
          </div>
        </div>
      )}

      {/* results & exposures */}
      {!!lineups.length && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Lineups table (left) */}
          <section className={`lg:col-span-8 rounded-lg p-3 ${T.card}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold">Lineups ({lineups.length})</h2>
              <div className="flex items-center gap-2">
                <button
                  className={ghostBtnClass}
                  onClick={() =>
                    downloadSiteLineupsCSV({
                      lineups,
                      site,
                      slots: SITES[site].slots,
                      siteIds: siteIds || {},
                      rows: rowsWithBoost,
                      slate, // 👈 pass current slate
                      fname: `NFL_lineups_${site.toUpperCase()}_ids.csv`,
                    })
                  }
                >
                  Export CSV (IDs)
                </button>
                {/* 👇 Detailed Export Button */}
                <button
                  className={ghostBtnClass}
                  onClick={() =>
                    downloadDetailedLineupsCSV({
                      lineups,
                      site,
                      slots: SITES[site]?.slots,
                      siteIds: siteIds || {},
                      rows: rowsWithBoost,
                      slate, // 👈 pass current slate like the IDs export
                      fname: `NFL_lineups_${site.toUpperCase()}_detailed.csv`,
                    })
                  }
                >
                  Export CSV (Detailed)
                </button>
              </div>
            </div>

            <div className="overflow-auto max-h-[440px]">
              <table
                className="min-w-full text-[12px] border-separate"
                style={{ borderSpacing: 0 }}
              >
                <thead className={`sticky top-0 z-20 ${isDark ? "bg-slate-900" : "bg-gray-50"}`}>
                  <tr>
                    <th className={header}>#</th>
                    <th className={header}>Salary</th>
                    <th className={header}>Total pOWN%</th>
                    <th className={header}>
                      Total {optBy === "pown" || optBy === "opt" ? "Projection" : metricLabel}
                    </th>
                    <th className={header}>Players</th>
                  </tr>
                </thead>
                <tbody>
                  {lineups.map((L, i) => {
                    const rowsByName = new Map(rowsWithBoost.map((r) => [r.name, r]));
                    const ordered = orderPlayersForSite(L.players, rowsByName);
                    const totalPown = ordered.reduce(
                      (s, r) => s + ((r.pown || 0) * 100),
                      0
                    );
                    const totalProj = ordered.reduce(
                      (s, r) => s + (Number(r.proj) || 0),
                      0
                    );
                    return (
                      <tr
                        key={i}
                        className={
                          isDark
                            ? "odd:bg-slate-950/60 even:bg-slate-900/60"
                            : "odd:bg-white even:bg-gray-50"
                        }
                      >
                        <td className={cell}>{i + 1}</td>
                        <td className={`${cell} tabular-nums`}>{fmt0(L.salary)}</td>
                        <td className={`${cell} tabular-nums`}>{slateHasRates(slate) ? `${fmt1(totalPown)}%` : "—"}</td>
                        <td className={`${cell} tabular-nums`}>{fmt1(totalProj || L.total)}</td>
                        <td className={`${cell} leading-snug`}>
                          <span className="break-words">
                            {ordered.map((r) => r.name).join(" • ")}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {stopInfo && (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                {stopInfo.detail ||
                  `Stopped early (${stopInfo.produced}/${stopInfo.requested}) due to ${stopInfo.reason}.`}
              </div>
            )}
          </section>

          {/* Exposure (right of lineups) */}
          <section className={`lg:col-span-4 rounded-lg p-3 ${T.card}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold">Exposure</h2>
              <button
                className={ghostBtnClass}
                onClick={() =>
                  downloadExposuresCSV(
                    lineups,
                    `NFL_exposures_${site.toUpperCase()}.csv`
                  )
                }
              >
                Export Exposures
              </button>
            </div>

            <ExposureTable
              lineups={lineups}
              rows={rowsWithBoost}
              maxHeightClass="max-h-[440px]"
              isDark={isDark}
              selectedPlayer={selectedExposurePlayer}
              onSelectPlayer={(player) =>
                setSelectedExposurePlayer((prev) => (prev === player ? null : player))
              }
            />
          </section>

          {/* Pairing Analysis */}
          <section className={`lg:col-span-4 rounded-lg p-3 ${T.card}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold">
                {selectedExposurePlayer
                  ? `Paired with ${selectedExposurePlayer}`
                  : "Pairing Analysis"}
              </h2>

              {selectedExposurePlayer && (
                <button
                  className={ghostBtnClass}
                  onClick={() => setSelectedExposurePlayer(null)}
                  type="button"
                >
                  Clear
                </button>
              )}
            </div>

            {!selectedExposurePlayer ? (
              <div className={isDark ? "text-slate-400 text-sm" : "text-gray-500 text-sm"}>
                Click a player in Exposure to see their highest pairings.
              </div>
            ) : selectedPlayerPairings.length === 0 ? (
              <div className={isDark ? "text-slate-400 text-sm" : "text-gray-500 text-sm"}>
                No pairings found.
              </div>
            ) : (
              <div className="overflow-auto max-h-[440px]">
                <table
                  className="min-w-full text-[12px] border-separate"
                  style={{ borderSpacing: 0 }}
                >
                  <thead
                    className={`sticky top-0 z-20 ${
                      isDark
                        ? "bg-slate-900 text-slate-100"
                        : "bg-gray-50 text-slate-900"
                    }`}
                  >
                    <tr>
                      <th className={header}>Player</th>
                      <th className={header}>Pos</th>
                      <th className={header}>Team</th>
                      <th className={header}>Count</th>
                      <th className={header}>Pair %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPlayerPairings.map((r) => (
                      <tr
                        key={r.name}
                        className={
                          isDark
                            ? "odd:bg-slate-950 even:bg-slate-900/70"
                            : "odd:bg-white even:bg-gray-50"
                        }
                      >
                        <td className={cell}>{r.name}</td>
                        <td className={cell}>{r.pos}</td>
                        <td className={cell}>{r.team || "—"}</td>
                        <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                        <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className={`lg:col-span-12 rounded-lg p-2.5 ${T.card}`}>
  <button
    type="button"
    onClick={() => setShowBuildAnalysis((v) => !v)}
    className={
      "w-full flex items-center justify-between rounded-md px-2 py-2 text-left transition " +
      (isDark ? "hover:bg-slate-900/60" : "hover:bg-gray-50")
    }
  >
    <h3 className="text-base font-semibold">Build Analysis</h3>
    <span className={isDark ? "text-slate-400 text-sm" : "text-gray-500 text-sm"}>
      {showBuildAnalysis ? "Hide ▲" : "Show ▼"}
    </span>
  </button>

  {showBuildAnalysis && (
    <div className="mt-3 grid grid-cols-1 xl:grid-cols-3 gap-4">
      {/* Summary / top players / QB teams */}
      <div className={`rounded-lg p-3 ${isDark ? "border border-slate-800 bg-slate-950/40" : "border border-slate-200 bg-slate-50"}`}>
        <h4 className="text-sm font-semibold mb-2">Summary</h4>

        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Salary Used</div>
          <div className="text-right tabular-nums">{fmt0(buildAnalysis.avgSalaryUsed)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Salary Left</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgSalaryRemaining)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Projection</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgProjection)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Lineup pOWN%</div>
          <div className="text-right tabular-nums">{slateHasRates(slate) ? fmt1(buildAnalysis.avgLineupPown) : "—"}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Geo Mean pOWN%</div>
          <div className="text-right tabular-nums">{slateHasRates(slate) ? fmt1(buildAnalysis.avgGeoMeanPown) : "—"}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg QB Stack Size</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgQbStackSize)}</div>

          <div className={isDark ? "text-slate-400" : "text-gray-500"}>Avg Bring-back Size</div>
          <div className="text-right tabular-nums">{fmt1(buildAnalysis.avgBringBackSize)}</div>
        </div>

        <div className="mt-4">
          <h5 className="text-xs font-semibold mb-2">Top Players</h5>
          <div className="overflow-auto max-h-[240px]">
            <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
              <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
                <tr>
                  <th className={header}>Player</th>
                  <th className={header}>Count</th>
                  <th className={header}>Exp %</th>
                </tr>
              </thead>
              <tbody>
                {buildAnalysis.topPlayers.map((r) => (
                  <tr
                    key={r.player}
                    className={
                      isDark
                        ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                        : "odd:bg-white even:bg-gray-50"
                    }
                  >
                    <td className={cell}>{r.player}</td>
                    <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                    <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4">
          <h5 className="text-xs font-semibold mb-2">Top QB Teams</h5>
          <div className="overflow-auto max-h-[220px]">
            <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
              <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
                <tr>
                  <th className={header}>Team</th>
                  <th className={header}>Count</th>
                  <th className={header}>Build %</th>
                </tr>
              </thead>
              <tbody>
                {buildAnalysis.topQbTeams.map((r) => (
                  <tr
                    key={r.team}
                    className={
                      isDark
                        ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                        : "odd:bg-white even:bg-gray-50"
                    }
                  >
                    <td className={cell}>
                      <TeamPill abbr={r.team} />
                    </td>
                    <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                    <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Top pairs / triples */}
      <div className={`rounded-lg p-3 ${isDark ? "border border-slate-800 bg-slate-950/40" : "border border-slate-200 bg-slate-50"}`}>
        <h4 className="text-sm font-semibold mb-2">Top 2-Player Pairings</h4>
        <div className="overflow-auto max-h-[300px]">
          <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
            <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
              <tr>
                <th className={header}>Pairing</th>
                <th className={header}>Count</th>
                <th className={header}>Build %</th>
              </tr>
            </thead>
            <tbody>
              {buildAnalysis.topPairs.map((r) => (
                <tr
                  key={r.label}
                  className={
                    isDark
                      ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                      : "odd:bg-white even:bg-gray-50"
                  }
                >
                  <td className={cell}>{r.label}</td>
                  <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                  <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4 className="text-sm font-semibold mt-4 mb-2">Top 3-Player Pairings</h4>
        <div className="overflow-auto max-h-[300px]">
          <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
            <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
              <tr>
                <th className={header}>Pairing</th>
                <th className={header}>Count</th>
                <th className={header}>Build %</th>
              </tr>
            </thead>
            <tbody>
              {buildAnalysis.topTriples.map((r) => (
                <tr
                  key={r.label}
                  className={
                    isDark
                      ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                      : "odd:bg-white even:bg-gray-50"
                  }
                >
                  <td className={cell}>{r.label}</td>
                  <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                  <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top game combos */}
      <div className={`rounded-lg p-3 ${isDark ? "border border-slate-800 bg-slate-950/40" : "border border-slate-200 bg-slate-50"}`}>
        <h4 className="text-sm font-semibold mb-2">Top Game Stacks</h4>
        <div className="overflow-auto max-h-[420px]">
          <table className="min-w-full text-[12px] border-separate" style={{ borderSpacing: 0 }}>
            <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
              <tr>
                <th className={header}>Game</th>
                <th className={header}>Count</th>
                <th className={header}>Build %</th>
              </tr>
            </thead>
            <tbody>
              {buildAnalysis.topGameCombos.map((r) => (
                <tr
                  key={r.label}
                  className={
                    isDark
                      ? "odd:bg-slate-950/80 even:bg-slate-900/80"
                      : "odd:bg-white even:bg-gray-50"
                  }
                >
                  <td className={cell}>{r.label}</td>
                  <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                  <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )}
</section>

          {/* Team Exposure (bottom left) */}
          <section className={`lg:col-span-6 rounded-lg p-3 ${T.card}`}>
            <h2 className="text-base font-bold mb-2">Team Exposure</h2>
            <TeamExposureTable lineups={lineups} rows={rowsWithBoost} isDark={isDark} />
          </section>

          {/* Stack Shapes (bottom right) */}
          <section className={`lg:col-span-6 rounded-lg p-3 ${T.card}`}>
            <h2 className="text-base font-bold mb-2">Stack Shapes</h2>
            <StackShapesTable
              lineups={lineups}
              rows={rowsWithBoost}
              allowRB={stackAllowRB}  // <- hook up your "count RB in stacks" toggle
              isDark={isDark}
            />
          </section>

          {/* Cards (full-width bottom) */}
          <section className={`lg:col-span-12 rounded-lg p-2.5 ${T.card}`}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h3 className="text-base font-semibold">Cards</h3>

              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-sm text-slate-400">Sort lineups by:</label>

                <select
                  className={`${cls.input} ${T.input}`}
                  value={cardsSortBy}
                  onChange={(e) => setCardsSortBy(e.target.value)}
                >
                  <option value="proj">Projection</option>
                  <option value="salary">Salary</option>
                  <option disabled={!slateHasRates(slate)} value="pown">pOWN%</option>
                  <option value="geo">Geo Mean%</option>
                  <option disabled={!slateHasRates(slate)} value="opt">Opt%</option>
                </select>

                <button
                  type="button"
                  className={ghostBtnClass}
                  onClick={() =>
                    setCardsSortDir((d) => (d === "asc" ? "desc" : "asc"))
                  }
                  title="Toggle sort direction"
                >
                  {cardsSortDir === "asc" ? "Asc ▲" : "Desc ▼"}
                </button>
              </div>
            </div>

            <LineupCards lineups={lineupsForCards} rows={rowsWithBoost} isDark={isDark} site={site} />
          </section>
        </div>
      )}
      {gameLogPlayer ? (
        <OptimizerGameLogModal
          player={gameLogPlayer.name}
          position={gameLogPlayer.pos}
          preparedRows={preparedGameLogRows}
          loading={gameLogsLoading}
          error={gameLogsError}
          onClose={() => setGameLogPlayer(null)}
          isDark={isDark}
          gameLogYear={gameLogYear}
          gameLogYears={OPT_GAME_LOG_YEARS}
          onGameLogYearChange={setGameLogYear}
        />
      ) : null}
    </div>
  );
}

/* ---------------------- CSV exposures helper ---------------------- */
function downloadExposuresCSV(lineups, fname = "nfl_exposures.csv") {
  const m = new Map();
  for (const L of lineups) for (const p of L.players) m.set(p, (m.get(p) || 0) + 1);
  const total = Math.max(1, lineups.length);
  const rows = [...m.entries()]
    .map(([name, cnt]) => [name, cnt, (cnt / total) * 100])
    .sort((a, b) => b[2] - a[2] || a[0].localeCompare(b[0]));
  const header = "Player,Count,Exposure %";
  const body = rows.map((r) => `${escapeCSV(r[0])},${r[1]},${r[2].toFixed(1)}`).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------------------- small UI components ------------------------ */
function PlayerMultiPicker({
  allPlayers,
  value,
  onChange,
  placeholder = "Add players…",
  isDark = false,
}) {
  const [q, setQ] = React.useState("");
  const selected = React.useMemo(() => new Set(value), [value]);
  const suggestions = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return allPlayers
      .filter((n) => !selected.has(n) && n.toLowerCase().includes(needle))
      .slice(0, 12);
  }, [q, allPlayers, selected]);
  const add = (name) => {
    if (!selected.has(name)) onChange([...value, name]);
    setQ("");
  };
  const remove = (name) => onChange(value.filter((v) => v !== name));

  return (
    <div className="mt-2">
      {/* chips */}
      <div className="flex flex-wrap gap-2 mb-1">
        {value.map((name) => (
          <span
            key={name}
            className={
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[12px]" +
              (isDark
                ? " bg-slate-900 text-slate-100 border-slate-600"
                : " bg-blue-50 text-blue-800 border-blue-200")
            }
          >
            {name}
            <button
              onClick={() => remove(name)}
              className={
                "leading-none hover:opacity-80" +
                (isDark ? " text-slate-300" : " text-blue-600 hover:text-blue-900")
              }
              title="Remove"
            >
              ×
            </button>
          </span>
        ))}
      </div>

      {/* input box */}
      <div
        className={
          "border rounded-md px-2 py-1 flex flex-wrap items-center gap-2" +
          (isDark
            ? " bg-slate-950/70 border-slate-700"
            : " bg-white border-slate-200")
        }
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && suggestions.length) {
              e.preventDefault();
              add(suggestions[0]);
            }
          }}
          className={
            "flex-1 min-w-[160px] outline-none text-sm py-1" +
            (isDark ? " bg-transparent text-slate-100 placeholder:text-slate-500" : "")
          }
          placeholder={placeholder}
        />
      </div>

      {/* dropdown suggestions */}
      {!!q && suggestions.length > 0 && (
        <div
          className={
            "mt-1 max-h-48 overflow-auto rounded-md shadow-sm border" +
            (isDark
              ? " bg-slate-950 border-slate-700"
              : " bg-white border-slate-200")
          }
        >
          {suggestions.map((n) => (
            <button
              key={n}
              className={
                "block w-full text-left px-3 py-1.5 text-sm" +
                (isDark ? " text-slate-100 hover:bg-slate-800" : " hover:bg-blue-50")
              }
              onClick={() => add(n)}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TeamSelect({ teams, value, onChange, isDark }) {
  const themeInput = isDark ? OPT_DARK_THEME.input : OPT_LIGHT_THEME.input;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${cls.input} ${themeInput} w-24`}
    >
      <option value="">— pick team —</option>
      {teams.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}

/** Compact summary of teams allowed to originate QB stacks. */

function TeamCapEditor({
  selectedTeams,
  setSelectedTeams,
  isDark,
}) {
  const sel = useMemo(() => [...selectedTeams].sort(), [selectedTeams]);

  return (
    <div
      className={
        "lg:w-[320px] shrink-0 border rounded-xl p-3 " +
        (isDark
          ? "border-slate-700 bg-slate-950/40"
          : "border-slate-200 bg-white/70")
      }
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] font-medium text-slate-400">
          Selected teams
        </div>
        <span
          className={
            "px-2 py-0.5 rounded-full text-[11px] font-semibold " +
            (isDark
              ? "bg-slate-800 text-slate-200"
              : "bg-slate-100 text-slate-600")
          }
        >
          {sel.length}
        </span>
      </div>

      {sel.length === 0 ? (
        <div className="text-xs text-gray-500">None selected</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {sel.map((t) => {
            const bg = TEAM_COLORS[t] || "#E5E7EB";
            const fg = readableText(bg);

            return (
              <span
                key={t}
                className="inline-flex items-center overflow-hidden rounded-md text-sm font-medium"
                style={{ backgroundColor: bg, color: fg }}
              >
                <span className="pl-2 pr-1.5 py-1">{t}</span>
                <button
                  type="button"
                  onClick={() => {
                    const next = new Set(selectedTeams);
                    next.delete(t);
                    setSelectedTeams(next);
                  }}
                  className="self-stretch px-1.5 text-[13px] leading-none border-l border-white/20 hover:bg-black/20"
                  title={`Remove ${t}`}
                  aria-label={`Remove ${t}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
/* --------------------------- Exposure tables --------------------------- */
function ExposureTable({
  lineups,
  rows,
  maxHeightClass = "",
  isDark,
  selectedPlayer = null,
  onSelectPlayer,
}) {
  const [tab, setTab] = React.useState("ALL");
  const meta = useMemo(() => new Map(rows.map((r) => [r.name, r])), [rows]);

  const allRows = useMemo(() => {
    const m = new Map();
    for (const L of lineups) for (const p of L.players) m.set(p, (m.get(p) || 0) + 1);
    const total = Math.max(1, lineups.length);
    return [...m.entries()]
      .map(([name, cnt]) => {
        const r = meta.get(name);
        return { name, count: cnt, pct: (cnt / total) * 100, pos: r?.pos || "?" };
      })
      .sort((a, b) => b.pct - a.pct || a.name.localeCompare(b.name));
  }, [lineups, meta]);

  const filtered = tab === "ALL" ? allRows : allRows.filter((r) => r.pos === tab);
  if (!allRows.length) return null;

  const cell = "px-2 py-1 text-center";
  const header = "px-2 py-1 font-semibold text-center";

  const headerBg = isDark ? "bg-slate-950" : "bg-gray-100";
  const tabActive = "bg-blue-50 border-blue-300 text-blue-800";
  const tabIdle = isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300";

  // 🔹 NEW: zebra striping for rows
  const rowStripe = isDark
    ? "odd:bg-slate-950/80 even:bg-slate-900/80 hover:bg-slate-800"
    : "odd:bg-white even:bg-slate-100 hover:bg-blue-50/70";

  return (
    <div>
      <div className="mb-2 flex gap-2 text-sm">
        {["ALL", "QB", "RB", "WR", "TE", "DST"].map((t) => (
          <button
            key={t}
            className={`px-2 py-1 rounded border ${
              tab === t ? tabActive : tabIdle
            }`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className={`overflow-auto ${maxHeightClass}`}>
        <table
          className="min-w-full text-[12px] border-separate"
          style={{ borderSpacing: 0 }}
        >
          <thead className={headerBg}>
            <tr>
              <th className={header}>Player</th>
              <th className={header}>Pos</th>
              <th className={header}>Count</th>
              <th className={header}>Expo%</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const active = r.name === selectedPlayer;
              return (
                <tr
                  key={r.name}
                  className={`${rowStripe} ${
                    onSelectPlayer ? "cursor-pointer" : ""
                  } ${
                    active
                      ? isDark
                        ? "ring-1 ring-inset ring-blue-400/70 bg-blue-500/15"
                        : "ring-1 ring-inset ring-blue-300 bg-blue-50"
                      : ""
                  }`}
                  onClick={() => onSelectPlayer?.(r.name)}
                  title={onSelectPlayer ? `Show pairings for ${r.name}` : undefined}
                >
                  <td className={`${cell} ${active ? "font-semibold" : ""}`}>{r.name}</td>
                  <td className={cell}>{r.pos}</td>
                  <td className={`${cell} tabular-nums`}>{fmt0(r.count)}</td>
                  <td className={`${cell} tabular-nums`}>{fmt1(r.pct)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TeamExposureTable({ lineups, rows, isDark }) {
  const rowsByName = useMemo(
    () => new Map(rows.map((r) => [r.name, r])),
    [rows]
  );

  const data = useMemo(() => {
    const counts = new Map();

    for (const L of lineups || []) {
      const teamsInLineup = new Set(
        (L.players || [])
          .map((n) => rowsByName.get(n))
          .filter(Boolean)
          .map((r) => r.team)
          .filter(Boolean)
      );

      for (const t of teamsInLineup) {
        counts.set(t, (counts.get(t) || 0) + 1);
      }
    }

    const total = Math.max(1, lineups?.length || 0);

    return [...counts.entries()]
      .map(([team, count]) => ({
        team,
        count,
        pct: (count / total) * 100,
      }))
      .sort((a, b) => b.pct - a.pct || a.team.localeCompare(b.team));
  }, [lineups, rowsByName]);

  if (!data.length) {
    return (
      <div className="text-sm text-slate-500 italic">
        No lineups generated
      </div>
    );
  }

  const headerText = isDark ? "text-slate-400" : "text-slate-500";
  const border = isDark ? "border-slate-800/50" : "border-slate-200";

  const rowClass = isDark
    ? "bg-transparent hover:bg-slate-900/40 hover:ring-1 hover:ring-slate-700/60 transition"
    : "bg-transparent hover:bg-slate-100 transition";

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className={`grid grid-cols-3 px-3 text-[12px] ${headerText}`}>
        <div className="text-center">Team</div>
        <div className="text-center">Exposure</div>
        <div className="text-center">%</div>
      </div>

      {/* Rows */}
      <div className={`rounded-md border ${border} max-h-[360px] overflow-y-auto overflow-x-hidden pr-1`}>
        {data.map((r) => {
          const color = TEAM_COLORS[r.team] || "#64748b";

          return (
            <div
              key={r.team}
              className={`grid grid-cols-3 items-center px-3 py-1.5
                border-b last:border-b-0
                ${border}
                ${rowClass}`}
            >
              {/* TEAM */}
              <div className="flex justify-center items-center gap-2">
                <TeamPill abbr={r.team} />
                <span className="text-xs opacity-70 tabular-nums">
                  ({fmt0(r.count)})
                </span>
              </div>

              {/* BAR */}
              <div className="flex justify-center">
                <div
                  className={`w-[220px] h-1.5 rounded ${
                    isDark ? "bg-slate-950/60" : "bg-slate-200"
                  } overflow-hidden`}
                >
                  <div
                    className="h-full rounded"
                    style={{
                      width: `${Math.max(0, Math.min(100, r.pct))}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
              </div>

              {/* % */}
              <div className="text-center tabular-nums text-sm">
                {fmt0(Math.round(r.pct))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StackShapesTable({ lineups, rows, allowRB = true, isDark }) {
  if (!lineups?.length) return null;

  const rowsByName = new Map(rows.map((r) => [r.name, r]));
  const isRealSkill = (r) => r && r.pos !== "DST";

  const shapes = [];
  const bringBackCounts = [];
  const teamStackSizes = [];

  for (const L of lineups) {
    const chosen = L.players.map((p) => rowsByName.get(p)).filter(Boolean);
    const qb = chosen.find((r) => r.pos === "QB");
    if (!qb) continue;

    const qbTeam = qb.team;
    const qbOpp = qb.opp || ""; // opponent from the rows data

    const isStackEligible = (r) => {
      if (!r || !isRealSkill(r)) return false;
      if (r.pos === "WR" || r.pos === "TE") return true;
      if (allowRB && r.pos === "RB") return true; // dynamic RB setting
      return false;
    };

    // stack partners: same-team, stack-eligible
    const stackPartners = chosen.filter(
      (r) => r !== qb && r.team === qbTeam && isStackEligible(r)
    );

    // bring-backs: opposing RB/WR/TE, independent of the same-team RB setting
    const bringBack = chosen.filter(
      (r) =>
        r !== qb &&
        qbOpp &&
        r.team === qbOpp &&
        isBringBackEligible(r)
    );
    const bringBackCount = bringBack.length;

    // ----- Primary stack type -----
    const pc = stackPartners.length;
    let shapeLabel = "";

    if (pc >= 4) {
      shapeLabel = "Onslaught (QB+4+)";
    } else if (pc === 3) {
      shapeLabel = "Triple stack (QB+3)";
    } else if (pc === 2) {
      shapeLabel = "Double stack (QB+2)";
    } else if (pc === 1) {
      shapeLabel = "Skinny stack (QB+1)";
    } else if (pc === 0 && bringBackCount > 0) {
      shapeLabel = "Naked QB + Bring-back";
    } else {
      shapeLabel = "Naked QB / Other";
    }

    shapes.push(shapeLabel);
    bringBackCounts.push(bringBackCount);

    // ----- QB team stack size (respects allowRB) -----
    const teamSize = 1 + stackPartners.length;
    let tlabel = "";
    if (teamSize >= 5) tlabel = "5+ man stack";
    else if (teamSize === 4) tlabel = "4-man stack";
    else if (teamSize === 3) tlabel = "3-man (QB+2)";
    else if (teamSize === 2) tlabel = "2-man (QB+1)";
    else tlabel = "Solo QB";

    teamStackSizes.push(tlabel);
  }

  const countPct = (arr) => {
    if (!arr.length) return [];
    const total = arr.length;
    const m = new Map();
    for (const x of arr) m.set(x, (m.get(x) || 0) + 1);
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({
        label: String(label),
        count,
        pct: (count / total) * 100,
      }));
  };

  const data = {
    shapes: countPct(shapes),
    bringBacks: countPct(bringBackCounts),
    teamStacks: countPct(teamStackSizes),
  };

  // -------- Mini Table Component --------
  const MiniTable = ({ title, rows }) => {
    if (!rows?.length) return null;

    const rowStripe = isDark
      ? "odd:bg-slate-900/80 even:bg-slate-800/80 hover:bg-slate-800"
      : "odd:bg-white even:bg-slate-50 hover:bg-slate-100";

    return (
      <div className="space-y-1">
        <div className="text-[12px] font-semibold mb-1 text-center">
          {title}
        </div>

        <table className="min-w-full text-[12px]">
          <thead>
            <tr>
              <th className="px-2 py-1 text-left font-semibold">Shape</th>
              <th className="px-2 py-1 text-right font-semibold">Count</th>
              <th className="px-2 py-1 text-right font-semibold">%</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className={rowStripe}>
                <td className="px-2 py-1 text-left">{r.label}</td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {r.count}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {r.pct.toFixed(1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MiniTable title="Primary stack type" rows={data.shapes} />
        <MiniTable title="Bring-back count" rows={data.bringBacks} />
        <MiniTable title="QB team stack size" rows={data.teamStacks} />
      </div>
    </div>
  );
}

/* ----------------------------- Lineup Cards ----------------------------- */
function LineupCards({ lineups, rows, isDark, site }) {
  const rowsByName = useMemo(
    () => new Map(rows.map((r) => [r.name, r])),
    [rows]
  );

  const logoSrc = (abbr) =>
    `/logos/nfl/${String(abbr || "").toUpperCase()}.png`;

  const cardClass = isDark
    ? "border border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
    : "border border-slate-200 bg-white shadow-sm";

  const headerBg = isDark ? "bg-slate-950" : "bg-gray-100";
  const rowOdd = isDark ? "bg-slate-900/80" : "bg-white";
  const rowEven = isDark ? "bg-slate-950/80" : "bg-slate-100";
  const rowHover = isDark ? "hover:bg-slate-800" : "hover:bg-blue-50/70";
  const subtext = isDark ? "text-slate-400" : "text-gray-600";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
      {lineups.map((L, idx) => {
        const ordered = orderPlayersForSite(L.players || [], rowsByName);

        const totals = ordered.reduce(
          (a, r) => {
            a.proj += Number(r?.proj) || 0;
            a.salary += Number(r?.salary) || 0;
            a.pown += (Number(r?.pown) || 0) * 100;
            a.opt += (Number(r?.opt) || 0) * 100;
            return a;
          },
          { proj: 0, salary: 0, pown: 0, opt: 0 }
        );

        const geoMeanPown = geoMeanPct(
          ordered,
          (r) => (Number(r?.pown) || 0) * 100
        );

        return (
          <div key={idx} className={`rounded-lg p-3 ${cardClass}`}>
          {/* Card header */}
          <div className={`flex items-start justify-between mb-2 ${subtext}`}>
            <div>
              <div className="font-semibold text-base">
                Lineup #{idx + 1}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <div className="text-xs font-medium">
                Geo Mean% {fmt1(geoMeanPown)}
              </div>

              <img
                src={SITES[site]?.logo}
                alt={SITES[site]?.label || site}
                title={SITES[site]?.label || site}
                className="w-[18px] h-[18px] object-contain shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>

            {/* Lineup table */}
            <table className="w-full text-[12px]">
              <thead className={headerBg}>
                <tr>
                  <th className="px-2 py-1 text-left">Slot</th>
                  <th className="px-2 py-1 text-left">Player</th>
                  <th className="px-2 py-1 text-right">Proj</th>
                  <th className="px-2 py-1 text-right">pOWN%</th>
                  <th className="px-2 py-1 text-right">Sal</th>
                </tr>
              </thead>
              <tbody>
                {ordered.map((r, i) => (
                  <tr
                    key={i}
                    className={`transition-colors ${
                      i % 2 === 0 ? rowOdd : rowEven
                    } ${rowHover}`}
                  >
                    <td className="px-2 py-1">{r.pos}</td>
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1">
                        {r.team && (
                          <img
                            src={logoSrc(r.team)}
                            alt={r.team}
                            className="w-4 h-4 rounded-sm"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                        <span>{r.name}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1 text-right">
                      {fmt1(r.proj)}
                    </td>
                    <td className="px-2 py-1 text-right">
                      {fmt1((r.pown || 0) * 100)}
                    </td>
                    <td className="px-2 py-1 text-right">
                      {fmt0(r.salary)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="px-2 py-1" colSpan={2}>
                    Totals
                  </td>
                  <td className="px-2 py-1 text-right">
                    {fmt1(totals.proj)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {fmt1(totals.pown)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {fmt0(totals.salary)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}
    </div>
  );
}
